import type { AppliedFrame } from '../janus-engine/transport/applied-frame';
import { swarmMovementBody } from '../janus-engine/movement/body-geometry.js';
import { deferredPlan as deferredWalkPlan, awaitPlan as awaitWalkPlan, resumePlan as resumeWalkPlan } from '../janus-engine/actions/deferred-plan.js';
import { observeTruthIntent } from '../janus-engine/telemetry/truth-commit-observer';
import { ftCurrentTruthSink, trainingGroundsRefusalMessage } from '../shared/truth-sink';
import { startActionTiming, markActionTiming, bindActionTiming, endActionTiming, withActionTimingEcho } from '../shared/action-timing';
import { hydrateInventoryDefinitions } from '../janus-engine/inventory/definition.js';
import { hydrateMovementCarrierDefinitions } from '../janus-engine/movement/carrier-definitions.js';
import { readInventoryRows } from '../janus-engine/compile/inventory-raw-readers.js';
import { fetchAndCacheObject } from './object-cache.js';
import { physicalCreatureBody } from '../shared/physical-creature-body.js';
import { statusRows, statusItemId } from '../janus-engine/statuses/expiry.js';
import { BURROWED_ITEM_ID } from '../janus-engine/movement/burrow-move.js';
import { resolveWaterWet, projectWaterWet, WATER_WET_ID } from '../janus-engine/movement/water-wet.js';
import { ftClockCalendarFromArr, ftClockColsFromArr, type CampaignClockCols } from '../shared/campaign-clock.js';
import { meshesOf } from '../../../ft3d/engine/mesh-presentation.js';
import { resolvePlainWalkScope, shapelessSnapLanding, crossingWalkRoute, encounterIsShapeless, opportunityLeaveWatchers, type PlainWalkReaders, type ReachReaction } from '../janus-engine/movement/plain-walk-scope.js';
import { prepareWallClimbRoute } from '../janus-engine/movement/wall-climb-route.js';
import { prepareStairWalkRoute } from '../janus-engine/movement/stair-walk.js';
import type { WalkRouteMover, WalkRouteObstacles } from '../janus-engine/movement/crossing-route.js';
import type { TeleportReaders } from '../janus-engine/movement/teleport-move.js';
import { movementStatusInputs20, prepareMovementDispatchSkips, type MovementDispatchContext } from '../janus-engine/movement/dispatch-skips.js';
import type { TriggerConsent } from '../janus-engine/ability/trigger-consent.js';
import { OPPORTUNITY_ATTACK_ID } from '../janus-engine/actions/weapon-attack.js';
import { creatureIsInActiveCombat, opposingTeams } from '../roll-init/client-combat-membership.js';
import { type PlainWalkResult, type PlainWalkCommitInput } from '../janus-engine/movement/plain-walk.js';
import {
  applyMovementTruth,
  MovementTruthRejected,
  MovementTruthTerminal,
  type ActorTruthCommandBase,
  type MovementTruthAcceptedResult,
  type MovementTruthChange,
  type MovementTruthCommand,
  type MovementTruthRejectedResult,
  type MovementTruthRow,
  type MovementTruthTerminalInfo,
} from '../janus-engine/movement/client-truth-commit.js';
import { MovementTruthOutbox, type MovementDelivery } from '../janus-engine/movement/client-truth-outbox.js';
import { appliedFrameOf, exposeAppliedFrame, exposeAppliedComputed } from './applied-result';
import { memberAppliedFrame } from '../janus-engine/transport/member-applied-frame';
import type { AppliedFrameResult } from '../janus-engine/transport/applied-frame';
import { saveAppliedTruth } from '../shared/applied-truth-transport';
import { movementNeedsSavedDelivery, pinMovementEffectIds, solidMovementAppliedFrame, savedMovementFrame } from '../janus-engine/transport/solid-movement-delivery';
import { withAsyncDeadline } from '../shared/async-deadline';
import { encounterCreatureField, encounterField, posCoord, type CasterArr } from '../shared/array-types.js';
import { awaitEntityCompileReady, entityCompileReady } from '../shared/entity-compile-ready.js';
import { casterRowInstalled, handleCasterUpdate } from './caster-update.js';
import { actorMovementPermission, MOVEMENT_PERMISSION_WARNING, movementRefusalMessage } from '../shared/actor-movement-permission.js';
import { _ftCamField } from '../shared/campaign-field.js';
// MV4 (MV2 ASK-1): MV2's exact zone classifier + exit commit (the functions behind window.ftZoneWalkResolve / ftCommitZoneExits).
import { resolveWalkZones, walkZoneRadius, commitZoneExits, reconcileZonesNow } from './zone-truth.js';
import { cutZoneRouteAtStep, legacyStepPositions } from '../janus-engine/zones/walk-zone-crossings.js';
import { classifyStealthConsequences, stealthCompositionReason, stealthWalkFeet, stealthEvidenceAfterHalt, type StealthConsequences } from '../janus-engine/movement/stealth-walk-compose.js';
import { bindStealthRevealLog } from '../janus-engine/movement/stealth-walk-effects.js';
import { resolveWalkTrap, type WalkTrapReaders } from '../janus-engine/movement/walk-trap.js';
import { carriedRoute, runChain, type ChainRevs, type ChainStep } from '../janus-engine/movement/compound-entry-chain.js';
import { moverOfRecord, moverDragFrame, moverSpeedVariable } from '../janus-engine/movement/mover-of-record.js';
import { ordinaryMovementStartRefusal } from '../janus-engine/movement/movement-start.js';
import { pilotedInventories, draftLinkKind } from '../janus-engine/movement/compound-drag.js';
import { inventoryDriveDecision } from '../janus-engine/movement/inventory-drive.js';
import { reloadRefusedEncounter, refusalOf } from './truth-refusal-notice.js';
import type { OverrunContestPlan } from '../janus-engine/movement/overrun-contest.js';
import type { OverrunTrigger } from '../janus-engine/movement/overrun-walk.js';
import { routeWallReader, type RouteWallWorld } from '../janus-engine/movement/route-walls.js';
import { moverMovementInt } from '../janus-engine/movement/difficult-terrain.js';
import { squeezeBodyFt } from '../../../ft3d/engine/ft3d-creature-size.js';
import { pageSceneShape } from '../janus-engine/geometry/perception-context.js';
/** Where `stop` lies on the route: the segment index and its point. A retraced / self-crossing route can hold the stop on several segments —
 *  the occurrence whose arc length is nearest `progress` (the halt foot's distance along the route: feet are resampled at 1 ft) wins, never
 *  simply the first (Codex fold review #4). */
function locateOnRoute(route: ReadonlyArray<readonly number[]>, stop: readonly number[], progress: number): number | null {
  let best: number | null = null, bestGap = Infinity, walked = 0;
  for (let i = 1; i < route.length; i++) {
    const a = route[i - 1]!, b = route[i]!;
    const abx = b[0]! - a[0]!, aby = b[1]! - a[1]!, len2 = abx * abx + aby * aby, len = Math.sqrt(len2);
    const t = len2 > 0 ? ((stop[0]! - a[0]!) * abx + (stop[1]! - a[1]!) * aby) / len2 : 0;
    const px = a[0]! + abx * t, py = a[1]! + aby * t;
    if (t >= -1e-9 && t <= 1 + 1e-9 && Math.hypot(px - stop[0]!, py - stop[1]!) < 1e-6) {
      const gap = Math.abs(walked + Math.max(0, Math.min(1, t)) * len - progress);
      if (gap < bestGap) { bestGap = gap; best = i; }
    }
    walked += len;
  }
  return best;
}
/** The route up to `stop` (on it, `progress` ft along): every vertex before its segment, then the stop. */
function cutRouteAt(route: ReadonlyArray<readonly number[]>, stop: readonly number[], progress: number): number[][] {
  const at = locateOnRoute(route, stop, progress);
  const head = route.slice(0, at ?? route.length).map(p => [p[0]!, p[1]!]);
  return [...head, [stop[0]!, stop[1]!]];
}
/** Zone steps belong to the classifier's 3D route. Re-anchor the rounded stop before the scope's exact endpoint check. */
function zoneHaltCut(walk: PlainWalkResult, haltStep: number, requestedRoute: ReadonlyArray<readonly number[]>) {
  const cut = cutZoneRouteAtStep(walk.truncated ? walk.truncated.route ?? requestedRoute : walk.route, haltStep - 1, walk.origin.z);
  if (!cut) return null;
  const destination = { x: Number(cut.stop[0].toFixed(3)), y: Number(cut.stop[1].toFixed(3)), z: Number(cut.stop[2].toFixed(3)) };
  cut.route[cut.route.length - 1] = cut.route[cut.route.length - 1]!.length > 2
    ? [destination.x, destination.y, destination.z] : [destination.x, destination.y];
  return { ...cut, destination };
}

type Row = Record<string, unknown>;
const asRow = (value: unknown): Row | null => value !== null && typeof value === 'object' && !Array.isArray(value) ? value as Row : null;
const own = (value: unknown, key: string): unknown => asRow(value)?.[key];
const scalar = (value: unknown): unknown => Array.isArray(value) ? value[0] : value;
const MOVEMENT_SCHEMA = 'janus.movement.truth.v1';
/** Session A step 12: a FORCE MOVE's opportunity reaction — the two-point move (no pricing: the bank and AP are unchanged), the leave point
 *  the mover's START (final_creature_move's `$mover_snapshot_for_leave`). */
export interface ForcedOpportunityMove { origin: { x: number; y: number; z: number }; updates: { x_pos: number; y_pos: number; z_pos: number };
  route: [[number, number, number], [number, number, number]] }
/** Reuse the walk's scene and occupancy readers. Actual Overrun contests are stopped before OA admission below. */
export type WalkReactionGeometry = Pick<PlainWalkReaders, 'wallClear' | 'crossingWorld' | 'crossingObstacles' | 'propFootprint' | 'overrunWalk'> & {
  reachReaction?: ReachReaction;
  resume?: (input: PlainWalkCommitInput) => Generator<Promise<unknown>, PlainWalkResult | null, unknown>;
};
type MovementReactionResolver = (input: PlainWalkCommitInput, encounter: Row, mover: MovementTruthRow, watcherId: string,
  forced?: ForcedOpportunityMove, geometry?: WalkReactionGeometry) => PlainWalkResult | null | Promise<PlainWalkResult | null>;
let movementReactionResolver: MovementReactionResolver | null = null;
export function registerMovementReactionResolver(resolve: MovementReactionResolver): void { movementReactionResolver = resolve; }
type MovementReactionPlanner = (...args: Parameters<MovementReactionResolver>) => Generator<Promise<unknown>, PlainWalkResult | null, unknown>;
let movementReactionPlanner: MovementReactionPlanner | null = null;
/** Keep each OA's suspended input checks in the mover's existing plan, including multi-watcher walks. */
export function registerMovementReactionPlanner(plan: MovementReactionPlanner): void { movementReactionPlanner = plan; }
let movementStatusHydrator: ((encounterId: string) => Promise<void> | null) | null = null;
export function registerMovementStatusHydrator(hydrate: NonNullable<typeof movementStatusHydrator>): void { movementStatusHydrator = hydrate; }
import { joinAdoptionWarmup } from '../shared/join-adoption-warmup.js';
import { registerActionInputWarmup } from './action-input-warmup.js';
const movementPreloads = new WeakMap<Row, Promise<void>>();
// Encounter events and OOC rest share this preload; only an actual movement
// warmup needs to scan force/walk/fall carriers, including later grant deltas.
export function hydrateMovementStatuses(encounterId: string, includeCarriers = false): Promise<void> | null {
  const encounter = encounterFor(encounterId);
  const previous = encounter ? movementPreloads.get(encounter) : undefined;
  const carriers = encounter && includeCarriers ? hydrateMovementCarrierDefinitions(encounter, movementScopeReaders(encounter), fetchAndCacheObject) : null;
  if (previous && !carriers) return previous;
  const statuses = previous ?? movementStatusHydrator?.(encounterId) ?? null;
  const props = encounter && !previous ? hydrateInventoryDefinitions(readInventoryRows(encounter, 'encounter_inventories_arr'), encounter.catalog, fetchAndCacheObject) : null;
  const job = Promise.all([statuses, props, carriers]).then(() => undefined);
  if (encounter) {
    movementPreloads.set(encounter, job);
    void job.catch(() => { if (movementPreloads.get(encounter) === job) movementPreloads.delete(encounter); });
  }
  return job;
}
function movementDefinitionsReady(encounter: Row): Promise<boolean> {
  return joinAdoptionWarmup(movementPreloads, encounter,
    () => { void hydrateMovementStatuses(String(encounterField(encounter, 'encounter_id') ?? ''), true); });
}
registerActionInputWarmup('movement', movementDefinitionsReady);
/** Position-only moves need their saved rows and movement definitions, not the
 * encounter's spell, turn and object-destruction adoption jobs. */
async function awaitMovementInputs(input: PlainWalkCommitInput): Promise<boolean> {
  const ids = [input.encounterId, input.actorId];
  if (await window.ftAwaitEntityBase?.(ids) === false || !entityCompileReady(ids)) return false;
  const encounter = encounterFor(input.encounterId);
  return encounter ? movementDefinitionsReady(encounter) : true;
}
/** Suspend before dice, outside the reaction snapshot: input adoption may itself
 * add catalogs or repaint rows. The resumed plan reads current held truth. */
export class MovementInputsNeeded extends Error {}
/** Step 12: resolve + queue a force move together with the watcher's Opportunity Attack (attack-truth.ts). Null = hand back. */
export function forceMoveOpportunity(input: PlainWalkCommitInput, encounter: Row, mover: MovementTruthRow, watcherId: string, forced: ForcedOpportunityMove): PlainWalkResult | null | Promise<PlainWalkResult | null> {
  return movementReactionResolver ? movementReactionResolver(input, encounter, mover, watcherId, forced) : null;
}
/** Force Move S2 (R-099): the FT3D map Force Move (force-move-truth.ts, registered to avoid an import cycle). It returns the applied move or
 *  throws MovementRefused — R-099 REMOVED 2026-09-30: no legacy force branch remains to hand back to. */
let forceMoveResolver: ((input: PlainWalkCommitInput, consequencesReady?: boolean) => Generator<Promise<unknown>, PlainWalkResult, unknown>) | null = null;
export function registerForceMoveResolver(resolve: NonNullable<typeof forceMoveResolver>): void { forceMoveResolver = resolve; }
/** MV3 (R-008 rung `room-clearance`): the walk's WALL world — the scene's shape with its live door states (inventory-floor-move-truth.ts
 *  encounterShape), registered by walk-wall-world.ts to avoid an import cycle. Absent = a walk leaving its start room hands back as before. */
let walkWallWorld: ((encounter: Row, provided: unknown) => RouteWallWorld | null) | null = null;
export function registerWalkWallWorld(resolve: (encounter: Row, provided: unknown) => RouteWallWorld | null): void { walkWallWorld = resolve; }
/** MV6 (R-008 `creature-space`): the walk router's HARD obstacles from the page's rows (walk-route-obstacles.ts — registered there to avoid an
 *  import cycle through inventory-floor-move-truth.ts). */
export type { WalkRouteObstacles };
let walkRouteObstacles: ((encounter: Row, mover: WalkRouteMover) => WalkRouteObstacles) | null = null;
export function registerWalkRouteObstacles(resolve: (encounter: Row, mover: WalkRouteMover) => WalkRouteObstacles): void { walkRouteObstacles = resolve; }
/** fix-bug 2026-10-14: a floor prop's canonical occupancy footprint for the shapeless walk's destination test (walk-route-obstacles.ts, same cycle). */
let walkPropFootprint: ((encounter: Row, row: Row) => ReturnType<NonNullable<PlainWalkReaders['propFootprint']>>) | null = null;
export function registerWalkPropFootprint(resolve: NonNullable<typeof walkPropFootprint>): void { walkPropFootprint = resolve; }
/** MV6 (R-067): a teleport's last landing gate (ft_resolve_move_destination places the body with no nudge) — walk-route-obstacles.ts. */
type TeleportLandingClear = (encounter: Row, mover: Row, world: RouteWallWorld) => (point: { x: number; y: number; z: number }, sizeFt: number) => boolean | 'unknown';
let teleportLandingClear: TeleportLandingClear | null = null;
export function registerTeleportLandingClear(resolve: TeleportLandingClear): void { teleportLandingClear = resolve; }
/** MV11 C4: settle climb failure / budget stops using the page's destination occupancy world. */
type WalkStopPlacement = (encounter: Row, mover: Row, world: RouteWallWorld, origin: { x: number; y: number; z: number }) => NonNullable<PlainWalkReaders['walkStopPlacement']>;
let walkStopPlacement: WalkStopPlacement | null = null;
export function registerWalkStopPlacement(resolve: WalkStopPlacement): void { walkStopPlacement = resolve; }
/** MV4 (2026-10-03, OWNER RULING 2026-10-02 — an optional trigger on a PLAYER-controlled creature is a 5 s prompt to its controller, silence =
 *  decline; NPC / GM-controlled creatures auto-resolve; campaign encounters only): this walk would provoke the universal Opportunity Attack of
 *  player-controlled watchers whose controllers have not answered yet. tryCommitPlainWalk throws it BEFORE any mutation or transport; the
 *  page entry (ftTryCommitPlainWalk) asks the controllers through the registered asker (walk-consent.ts — A10's prompt, A11's
 *  opportunityConsentQuestions) and runs the walk again carrying the answers (`triggerConsent`, A11's seam). */
export class WalkConsentNeeded extends Error {
  constructor(readonly watchers: string[]) { super('walk-consent-needed'); }
}
export interface WalkConsentAsker {
  /** Is this watcher's OA a question for a player (its controller a user id, or '?' — the relay decides), not an auto-resolving NPC? */
  needsAsk(encounterId: string, watcher: Row): boolean;
  create(encounterId: string): TriggerConsent;
  /** Ask every question these watchers' OAs raise at once (one shared 5 s window); the reader's answers are final afterwards. */
  ask(encounterId: string, mover: MovementTruthRow, watchers: string[], consent: TriggerConsent): Promise<void>;
  /** MV5 (coordinator Round 23 P2): the relay ask id this watcher's Opportunity Attack question went out under (undefined: asked on this page's
   *  own card, or never asked) — a decline posted to PHP names it so the server can prove the controller declined (ft_walk_oa_declines.php). */
  askIdFor?(consent: TriggerConsent, watcherId: string): string | undefined;
}
let walkConsentAsker: WalkConsentAsker | null = null;
export function registerWalkConsentAsker(asker: WalkConsentAsker): void { walkConsentAsker = asker; }
/** A walk carrying its controllers' answers (A11's attack-truth.ts WalkReactionConsentInput — the same field). */
type WalkConsentInput = PlainWalkCommitInput & { triggerConsent?: TriggerConsent };
/** Shown while this actor's walk waits on its watchers' answers (another drag of the same actor is refused, not queued behind it). */
export const WALK_CONSENT_PENDING_MESSAGE = 'Waiting for an opportunity attack answer before this move…';
/** R-099: a Force Move this page cannot start (the lane's module did not load, or the rows are not held). */
export const FORCE_MOVE_UNAVAILABLE_MESSAGE = 'Force Move refused: the encounter is still loading on this page. Nothing was moved.';
/** MV2 S2 (built by MV4): a walk's persistent-zone entries — planned BEFORE the walk commits (every hand-back on state), committed after it
 *  is accepted. Implemented by zone-entry-truth.ts, registered here so that module can use the ability lane without an import cycle. */
type ZoneEntryPlan = { kind: 'planned'; token: unknown; haltStep?: number } | { kind: 'handback'; reason: string };
type WalkTrapPlan = { kind: 'fire'; fire: Record<string, unknown> } | { kind: 'handback'; reason: string };
export interface ZoneEntryPlanner {
  /** Refresh saved records only after the route proves dispatching entries, before any dry run or draw. */
  prepare?(encounterId: string, actorId: string, entries?: ReadonlyArray<{ originId: string }>): Promise<unknown> | null;
  /** `strips`: the walk's own end-of-move strips — the planner commits them AFTER the entries, with each walk-through entry's rows. */
  plan(encounterId: string, actorId: string, entries: NonNullable<PlainWalkResult['zone_entries']>, final: { x: number; y: number; z: number },
    strips: NonNullable<PlainWalkResult['zone_exits']>,
    /** Session 3: the walk drags compound followers — `followerZoneRows`: a follower holds a zone-connected row the halt's strips could take. */
    compound?: { followerZoneRows: boolean },
    /** D-051: a compound FOLLOWER carried through the zones (never walking): no halt is planned, and none truncates it on a re-resolution. */
    carried?: boolean,
    /** D-051 chain (Codex review #3 F1): the previous entrant's token — this plan resolves on the rows its verdicts left. */
    after?: unknown,
    /** The walk's members at their landings (applied to the chain's start). */
    moved?: ReadonlyMap<string, { x: number; y: number; z: number }>):
    ZoneEntryPlan | Promise<ZoneEntryPlan>;
  /** D-051 (Codex review #2 F5, #3 F1): plan()'s hand-backs on synthetic dice only, for a CHAIN of entrants (the mover, then every follower,
   *  each on the state the earlier ones left under the same pattern) — run before any plan draws. `haltSteps`: the mover's feet a dry run
   *  halted on (review #3 F3: the cuts whose carried routes are preflighted too). */
  preflight(encounterId: string, entrants: ReadonlyArray<{ actorId: string; entries: NonNullable<PlainWalkResult['zone_entries']>; final: { x: number; y: number; z: number };
    compound?: { followerZoneRows: boolean }; carried?: boolean }>): { reason: string | null; haltSteps: number[] };
  /** The walk halts at `haltStep`: keep the entries up to it (those AT it swept after), and the re-scoped walk's own strips. `resume`
   *  walks the rest of the requested route from the stop — for a planned halt the commit did not land (a dedupe skip, a refusal). */
  truncate(token: unknown, haltStep: number, strips: NonNullable<PlainWalkResult['zone_exits']>, resume: () => PlainWalkResult | null | Promise<PlainWalkResult | null>): void;
  /** The walk is queued: the actor's next walk waits until the entries settle (a status they apply may change its next entry). */
  hold(token: unknown): void;
  /** The walk was accepted at `rev`, the creature at `final`: resolve and commit the entries. `confirmed`: what the compound's earlier commits
   *  confirmed (D-051 F1 — the chain is serialized by the caller); answers what these entries confirmed once they settle. */
  commit(token: unknown, rev: number, final: { x: number; y: number; z: number }, confirmed?: ChainRevs | null): Promise<ChainRevs | null>;
  /** The walk was rejected or failed: nothing to enter. */
  drop(token: unknown): void;
  pending(encounterId: string, actorId: string): boolean;
}
let zoneEntryPlanner: ZoneEntryPlanner | null = null;
/** Janus session 3 step 4: a walk's trap fire — trigger readers and a resumable fire plan (walk-trap-truth.ts, registered to avoid an
 *  import cycle with the ability lane). */
export interface WalkTrapPlanner {
  readers(encounter: Row, mover: Row): WalkTrapReaders;
  preflight?(encounterId: string, victimId: string, hostId: string, abilityId: string,
    seed?: NonNullable<MovementTruthCommand['stealth']>['verdict']['ec']): WalkTrapPlan;
  plan(encounterId: string, victimId: string, hostId: string, abilityId: string,
    seed?: NonNullable<MovementTruthCommand['stealth']>['verdict']['ec'], at?: { x: number; y: number; z: number }):
    WalkTrapPlan | Promise<WalkTrapPlan>;
}
let walkTrapPlanner: WalkTrapPlanner | null = null;
export function registerWalkTrapPlanner(planner: WalkTrapPlanner): void { walkTrapPlanner = planner; }
export interface OverrunWalkPlanner {
  plan(encounterId: string, actorId: string, triggers: readonly OverrunTrigger[], inCombat: boolean): OverrunContestPlan;
  /** Accepted contest: remember pairs and offer deferred Fortunate after saved party values are installed. */
  recorded?(encounterId: string, actorId: string, evidence: NonNullable<MovementTruthCommand['overrun']>, operationId: string): void;
}
let overrunWalkPlanner: OverrunWalkPlanner | null = null;
export interface ClimbWalkPlanner {
  plan(encounterId: string, actorId: string, faces: import('../janus-engine/movement/climb-walk.js').ClimbFace[]):
    import('../janus-engine/movement/climb-walk.js').ClimbChecksPlan;
}
let climbWalkPlanner: ClimbWalkPlanner | null = null;
export function registerClimbWalkPlanner(planner: ClimbWalkPlanner): void { climbWalkPlanner = planner; }
/** MV8 (R-008 stealth walk): websocket/stealth-walk-truth.ts — the awareness hook's page adapter (registered to avoid an import cycle). */
export interface StealthWalkPlanner {
  /** Any non-private Hidden creature in the roster, or a Hidden mover (an unreadable status counts). */
  relevant(encounter: Row, actorId: string, read: PlainWalkReaders): boolean;
  /** Await the round's contests and skill definitions before the synchronous verdict. */
  hydrate?(encounterId: string, actorId: string): Promise<void> | null;
  plan(input: { encounterId: string; actorId: string; feet: ReadonlyArray<readonly [number, number]>; z: number;
    pair: NonNullable<PlainWalkCommitInput['stealthPair']>; read: PlainWalkReaders;
    /** MV10 (2026-10-08): fresh contests compose with dice-free passes or a seeded trap; cached reveals can precede other dice. */
    consequences: StealthConsequences;
    preflightHalt?: (step: number, possibilities: { touched: boolean; freshPossible: boolean; moverPcPossible: boolean }) => string | null;
    preflightTrap?: (seed: NonNullable<MovementTruthCommand['stealth']>['verdict']['ec']) => string | null }):
    { kind: 'handback'; reason: string } | { kind: 'clear' } | { kind: 'resolved'; haltStep: number | null; message: string | null; evidence: NonNullable<MovementTruthCommand['stealth']> };
  recorded?(encounterId: string, evidence: NonNullable<MovementTruthCommand['stealth']>, actorId?: string, operationId?: string): void;
  invalidate?(encounterId: string): void;
}
let stealthWalkPlanner: StealthWalkPlanner | null = null;
/** MV8 (R-008 `move-trigger`): websocket/walk-move-trigger.ts — move-trigger-truth.ts's plan / commit (lane A's, called not edited),
 *  registered to avoid an import cycle. plan() = 'none' (every carrier gated silent), 'handback', or an owed commit run after the walk is accepted. */
export interface WalkMoveTriggerPlanner {
  plan(encounterId: string, moverId: string, landing: { x: number; y: number; z: number }):
    { kind: 'none' } | { kind: 'handback'; reason: string } | { kind: 'planned'; commit: (rev: number) => Promise<unknown> | void };
}
let walkMoveTriggerPlanner: WalkMoveTriggerPlanner | null = null;
export function registerWalkMoveTriggerPlanner(planner: WalkMoveTriggerPlanner): void { walkMoveTriggerPlanner = planner; }
export function registerStealthWalkPlanner(planner: StealthWalkPlanner): void { stealthWalkPlanner = planner; }
export function registerOverrunWalkPlanner(planner: OverrunWalkPlanner): void { overrunWalkPlanner = planner; }
export function registerZoneEntryPlanner(planner: ZoneEntryPlanner): void { zoneEntryPlanner = planner; }
/** A walk-with-entries' strips were committed by the entry planner: hold this actor's next walk until the page no longer shows them (the
 *  ordinary exit path's settle barrier — a stripped speed status must be gone before the next walk is priced). */
export function holdCommittedZoneStrips(encounterId: string, actorId: string, statusIds: readonly string[]): void {
  holdZoneExits(keyOf(encounterId, actorId), statusIds, Date.now() + ZONE_EXIT_SETTLE_MS);
}
/** Own walks whose zone entries commit once the move itself is confirmed (operation id → planner token). */
const pendingZoneEntries = new Map<string, unknown>();
/** D-051: the carried followers' entry plans of a walk, by its operation id (committed with the followers' saved truth). `stationary`: a creature
 *  the walk's carried aura covered — it did not move, so its entries commit at its own position and the revision it was planned on. */
type FollowerEntryPlan = { id: string; token: unknown; stationary?: { rev: number; at: { x: number; y: number; z: number } } };
const pendingFollowerEntries = new Map<string, FollowerEntryPlan[]>();
/** The plans tryCommitPlainWalk made for the walk it is about to commit (handed to commitResolvedPlainWalk). */
let pendingFollowerTokens: FollowerEntryPlan[] | null = null;
type ZoneEntrant = Parameters<ZoneEntryPlanner['preflight']>[1][number];
/** Session 3: a compound follower holding a zone-connected row (a halt's strips at the cut stop could take it for good). */
function compoundFollowerZoneRows(encounterId: string, memberIds: ReadonlySet<string>): boolean {
  return [...memberIds].some(id => {
    const member = rowFor(encounterId, id);
    return !!member && ftCreatureStatuses(member, 'target').some(s => String(s.status_connected_id ?? '') !== '' && !linkStatus(s as Row));
  });
}
/** ONE chain of a walk's zone entrants: the mover's entries, then each carried follower's (D-051) and each creature its carried aura covers
 *  (`at`: it stands where it is). */
function walkEntrants(actorId: string, walk: PlainWalkResult, moverEntries: NonNullable<PlainWalkResult['zone_entries']>,
  moverFinal: { x: number; y: number; z: number }, memberIds: ReadonlySet<string>, followerZoneRows: boolean): { entrants: ZoneEntrant[] } | { reason: string; follower?: string } {
  const entrants: ZoneEntrant[] = [];
  if (moverEntries.length) {
    entrants.push({ actorId, entries: moverEntries, final: moverFinal, ...(memberIds.size ? { compound: { followerZoneRows } } : {}) });
  }
  for (const f of walk.follower_zone_entries ?? []) {
    const member = walk.compound?.members.find(m => m.id.toUpperCase() === f.actorId.toUpperCase());
    const final = f.at ?? member?.after; // a creature the carried aura covers stands where it is
    if (!final) return { reason: 'zone-entry:follower-not-in-compound', follower: f.actorId };
    entrants.push({ actorId: f.actorId, entries: f.entries, final: { x: final.x, y: final.y, z: final.z }, carried: true });
  }
  return { entrants };
}
export const ZONE_ENTRY_PENDING_MESSAGE = 'Entering a zone is still being saved. Try moving again shortly.';
let movementReactionPending: (encounterId: string) => boolean = () => false;
export function registerMovementReactionPending(check: (encounterId: string) => boolean): void { movementReactionPending = check; }
/** ONE outbox per actor, shared by every client-truth schema (movement + A1 actions): the per-actor
 * revision orders them together, so a Dodge queued behind a walk rides the walk's optimistic
 * revision and is invalidated with it. Non-movement schemas register their handlers here. */
export interface TruthSchemaHandlers {
  computedRows?: (command: ActorTruthCommandBase) => readonly Row[];
  apply: (row: MovementTruthRow, command: ActorTruthCommandBase, optimisticRev: number) => void;
  render: (command: ActorTruthCommandBase, row: MovementTruthRow) => void;
  commit: (command: ActorTruthCommandBase) => Promise<unknown>;
  accepted: (command: ActorTruthCommandBase, result: MovementTruthAcceptedResult) => void;
  rejected: (command: ActorTruthCommandBase, result: MovementTruthRejectedResult) => void;
  failed: (command: ActorTruthCommandBase, info: MovementTruthTerminalInfo) => void;
  /** Alert text for a stale rejection of this schema (movement's is the position one). */
  staleMessage: string;
  /** A schema recovering a definite rejection owns its progress/refusal notice. */
  handlesRejection?: (operationId: string) => boolean;
}
const schemaHandlers = new Map<string, TruthSchemaHandlers>();
export function registerTruthSchema(schema: string, handlers: TruthSchemaHandlers): void { schemaHandlers.set(schema, handlers); }
const handlersFor = (command: ActorTruthCommandBase): TruthSchemaHandlers => {
  const handlers = schemaHandlers.get(command.schema);
  if (!handlers) throw new Error('Unregistered client-truth schema: ' + command.schema);
  return handlers;
};
const isMovement = (command: ActorTruthCommandBase): command is MovementTruthCommand => command.schema === MOVEMENT_SCHEMA;
const outboxes = new Map<string, MovementTruthOutbox<ActorTruthCommandBase>>();
const seen = new Set<string>();
const deliveries = new Map<string, MovementDelivery>();
const deliverySchema = new Map<string, string>();
/** Own movement operations that spent the movement bank (M1b) or banked an AP (M1c): a stale rejection
 * restores those scalars too, and the alert says which. */
const bankedOperations = new Map<string, 'bank' | 'ap'>();
/** MV4 (MV2 ASK-1): own walks whose end-of-move zone exits are committed once the move itself is confirmed (operation id → strips). */
const pendingZoneExits = new Map<string, { encounterId: string; actorId: string; strips: NonNullable<PlainWalkResult['zone_exits']> }>();
/** MV4 (Codex S1 review F2): per actor, the zone rows its walks strip that the page may still hold — a zone status can change the
 *  compiled speed a next walk is priced from, so this actor's next walk waits until each strip has settled (status id → deadline;
 *  null while the move or the strip is still in flight). */
const zoneExitHold = new Map<string, Map<string, number | null>>();
const ZONE_EXIT_SETTLE_MS = 20000;
/** MV4 (M6a, Codex S4 F3): own compound walks in flight (operation ids) and the actors they belong to — the next walk of that actor
 *  waits, because it would plan its followers from rows the in-flight commit is about to move. */
const compoundOps = new Map<string, string>();
export const COMPOUND_PENDING_MESSAGE = 'Your companions are still moving. Try again shortly.';
/** Session 3 step 4 (Codex F3): a trapped walk moves SQL two revisions (the walk + the fire's own bump) and changes the victim — the actor's
 *  next walk waits until it settles (it would be planned on rows the fire is about to change and CAS a revision behind). */
const trapOps = new Map<string, string>();
const overrunOps = new Map<string, string>();
const OVERRUN_PENDING_MESSAGE = 'An Overrun contest is still being saved. Try moving again shortly.';
/** MV8: a walk whose stealth contests / reveals are still being saved — the next walk would re-roll a pair the round already met. */
const stealthOps = new Map<string, { key: string; encounterId: string }>();
const STEALTH_PENDING_MESSAGE = 'Stealth checks from the last move are still being saved. Try moving again shortly.';
const climbOps = new Map<string, string>();
const CLIMB_PENDING_MESSAGE = 'Climb checks from the last move are still being saved. Try moving again shortly.';
export const TRAP_PENDING_MESSAGE = 'A trap is still going off. Try moving again shortly.';
/** Session 3 step 7 (Codex rest review): a force move whose end-of-move status tail (Hunkered / Squeezed) runs AFTER its answer — the actor's
 *  next move waits for the answer and then a settle window for the tail's status push (it would be planned on statuses about to change). */
const restOps = new Map<string, string>();
const restHold = new Map<string, number>();
const REST_SETTLE_MS = 4000;
export const REST_PENDING_MESSAGE = 'The last move is still settling. Try moving again shortly.';
export function noteRestOperation(operationId: string, encounterId: string, actorId: string): void {
  restOps.set(operationId, keyOf(encounterId, actorId));
  if (restOps.size > 256) restOps.delete(restOps.keys().next().value!);
}
function restPending(key: string): boolean {
  if ([...restOps.values()].includes(key)) return true;
  const until = restHold.get(key);
  if (until !== undefined && Date.now() < until) return true;
  restHold.delete(key);
  return false;
}
function settleCompound(operationId: string, accepted = false): void {
  compoundOps.delete(operationId); trapOps.delete(operationId); overrunOps.delete(operationId);
  climbOps.delete(operationId);
  const stealthOp = stealthOps.get(operationId);
  if (stealthOp) { stealthOps.delete(operationId); if (!accepted) stealthWalkPlanner?.invalidate?.(stealthOp.encounterId); }
  const rest = restOps.get(operationId);
  if (rest !== undefined) { restOps.delete(operationId); if (accepted) restHold.set(rest, Date.now() + REST_SETTLE_MS); }
}
export const ZONE_EXIT_PENDING_MESSAGE = 'Leaving a zone is still being saved. Try moving again shortly.';
function holdZoneExits(key: string, statusIds: readonly string[], deadline: number | null): void {
  const held = zoneExitHold.get(key) ?? new Map<string, number | null>();
  for (const id of statusIds) held.set(id.toUpperCase(), deadline);
  zoneExitHold.set(key, held);
}
function releaseZoneExits(key: string, statusIds: readonly string[]): void {
  const held = zoneExitHold.get(key);
  if (!held) return;
  for (const id of statusIds) held.delete(id.toUpperCase());
  if (!held.size) zoneExitHold.delete(key);
}
/** Does this actor still wait on a zone strip? A resolved strip settles when the page no longer holds the row or its deadline passes. */
function zoneExitsUnsettled(encounterId: string, actorId: string): boolean {
  const key = keyOf(encounterId, actorId);
  const held = zoneExitHold.get(key);
  if (!held) return false;
  const onPage = new Set<string>();
  for (const creature of (encounterFor(encounterId)?.encounter_creatures_arr as Row[] | undefined) ?? []) {
    for (const status of ftCreatureStatuses(creature, 'target')) onPage.add(String(own(status, 'status_id') ?? '').toUpperCase());
  }
  const now = Date.now();
  for (const [id, deadline] of held) if (deadline !== null && (now > deadline || !onPage.has(id))) held.delete(id);
  if (!held.size) zoneExitHold.delete(key);
  return held.size > 0;
}
/** Where the confirmed snapshot last came from (diagnostics; R1 provenance 2026-09-07). */
export type ConfirmedSource = 'snapshot' | 'commit' | 'frame' | 'legacy-delta' | 'caster-update' | 'encounter-state';
type ConfirmedField = 'x_pos' | 'y_pos' | 'z_pos' | 'movement_pct' | 'aps';
const CONFIRMED_FIELDS: readonly ConfirmedField[] = ['x_pos', 'y_pos', 'z_pos', 'movement_pct', 'aps'];
export type ConfirmedFields = Partial<Record<ConfirmedField, number>>;
/** Last AUTHORITATIVE {rev + every client-truth row scalar} per actor, PER FIELD: each scalar remembers
 * the revision that confirmed it, so saved truth arriving out of order (an A1 committed frame at rev 7
 * before the movement committed frame at rev 6 — the post-flush publishes are not ordered across
 * operations) still lands each field from the newest revision that carried it (Codex M1c review, finding
 * 4). The row's own `movement_rev` runs ahead of `rev` by one per pending optimistic operation; ordering
 * decisions compare against the confirmed value, never the optimistic one. ONE snapshot shared by every
 * schema (movement: position + bank + aps; A1 actions: aps) so a movement restore can never resurrect a
 * pre-Dodge AP (M1c). Every legacy row install (delta, caster_update, encounter snapshot) banks here
 * explicitly, from the INSTALLED payload — never from a merged rendered row (R1/R2 closed 2026-09-07). */
interface Confirmed { rev: number; position: MovementTruthChange; source: ConfirmedSource; fieldRev: Record<ConfirmedField, number> }
const confirmed = new Map<string, Confirmed>();
/** Legacy action/status/resource ordering; walk and End Turn use confirmed truth. */
const provisionalSeen = new Map<string, number>();
let deliveryNotice: HTMLDivElement | null = null;
const keyOf = (encounterId: string, actorId: string): string => String(encounterId).toUpperCase() + ':' + String(actorId).toUpperCase();
const correctionReloads = new Map<string, Set<string>>();
const memberOperations = new Map<string, { operationId: string; rev: number }>();
export const isTruthCorrectionPending = (encounterId: string, memberId?: string): boolean => {
  const refused = correctionReloads.get(encounterId.toUpperCase());
  if (!refused) return false;
  const held = memberId ? memberOperations.get(keyOf(encounterId, memberId)) : undefined;
  return !held || refused.has(held.operationId);
};
window.addEventListener('ft:truth-frame-applied', event => {
  const frame = (event as CustomEvent<AppliedFrameResult>).detail;
  for (const [id, rev] of Object.entries(frame.member_revs)) {
    const key = keyOf(frame.encounter_id, id), held = memberOperations.get(key);
    if (!held || rev >= held.rev) memberOperations.set(key, { operationId: frame.operation_id.toUpperCase(), rev });
  }
  while (memberOperations.size > 2048) memberOperations.delete(memberOperations.keys().next().value!);
});
/** Retire rejected canonical hosts as well as their watermarks. The normal
 * compiler deliberately preserves newer mounted members during ordinary reads. */
export function discardCorrectionEncounter(encounterId: string, members?: ReadonlySet<string>): void {
  const id = encounterId.toUpperCase(), ft = window.FateTide;
  const held = ft?.encounterCache?.get(id);
  if (held) window.ftDiscardInventoryBase?.(held);
  for (const member of ft?.casterCache?.values() ?? []) {
    if (String(own(own(member, 'encounter_creatures'), 'encounter_id') ?? '').toUpperCase() !== id) continue;
    if (members && !members.has(String(ftEncounterCreatureField(member.encounter_creatures, 'encounter_creature_id') ?? '').toUpperCase())) continue;
    window.ftDiscardInventoryBase?.(member);
    // Keep alias keys: the full reload rebinds them and repaints mounted panes.
  }
}
/** A refused save reloads the encounter. Forget the page-pushed watermarks so
 * the last valid saved revision can be installed by that existing recovery. */
export function forgetEncounterTruth(encounterId: string, members?: ReadonlySet<string>): void {
  const prefix = encounterId.toUpperCase() + ':';
  for (const key of confirmed.keys()) if (key.startsWith(prefix) && (!members || members.has(key.slice(prefix.length)))) confirmed.delete(key);
  for (const key of provisionalSeen.keys()) if (key.startsWith(prefix) && (!members || members.has(key.slice(prefix.length)))) provisionalSeen.delete(key);
}
/** Outbox settlement may seed a watermark while the reload is in flight. Drop
 * that seed again immediately before the refused encounter's fresh install. */
export function finishTruthCorrection(encounterId: string): Set<string> | false {
  if (correctionReloads.has(encounterId.toUpperCase())) {
    const members = new Set((window.FateTide?.encounterCache?.get(encounterId)?.encounter_creatures_arr ?? [])
      .map(member => String(ftEncounterCreatureField(member.encounter_creatures, 'encounter_creature_id') ?? '').toUpperCase())
      .filter(id => isTruthCorrectionPending(encounterId, id)));
    discardCorrectionEncounter(encounterId, members);
    window.dispatchEvent(new CustomEvent('ft:truth-correction-install', { detail: { encounterId, memberIds: [...members] } }));
    return members;
  }
  return false;
}
window.addEventListener('ft:truth-correction', event => {
  const detail = (event as CustomEvent<{ encounterId?: string; operationId?: string }>).detail;
  const id = String(detail?.encounterId ?? '').toUpperCase();
  if (id) {
    const operations = correctionReloads.get(id) ?? new Set<string>();
    operations.add(String(detail?.operationId ?? '').toUpperCase());
    correctionReloads.set(id, operations);
  }
});
window.addEventListener('ft:truth-correction-install', event => {
  const detail = (event as CustomEvent<{ encounterId: string; memberIds: string[] }>).detail;
  forgetEncounterTruth(detail.encounterId, new Set(detail.memberIds));
  correctionReloads.delete(detail.encounterId.toUpperCase());
});
window.addEventListener('ft:ws-state-recovery', event => {
  const detail = (event as CustomEvent<{ kind: string; id: string; status: string }>).detail;
  if (detail.kind === 'encounter' && ['failed', 'recovered'].includes(detail.status)) correctionReloads.delete(detail.id.toUpperCase());
});
function encounterFor(id: string): Row | null { return asRow(window.FateTide?.encounterCache?.get(id)); }
function rowFor(encounterId: string, actorId: string): MovementTruthRow | null {
  const roster = encounterFor(encounterId)?.encounter_creatures_arr;
  if (!Array.isArray(roster)) return null;
  const wanted = String(actorId).toUpperCase();
  return roster.find((row: unknown) => String(own(own(row, 'encounter_creatures'), 'encounter_creature_id') ?? '').toUpperCase() === wanted) as MovementTruthRow ?? null;
}
const finiteScalar = (value: unknown): number | undefined => {
  const raw = scalar(value);
  return raw === undefined || raw === null || raw === '' || !Number.isFinite(Number(raw)) ? undefined : Number(raw);
};
/** Every client-truth scalar of a COMPLETE row (a compiled row, a rebuilt `encounter_creatures` bucket): the
 * lean builder elides `movement_pct` at 0, so an absent key on a complete row IS zero — never "unknown"
 * (Codex M1c review, finding 3). Partial truth (a frame, an A1 answer) goes through ConfirmedFields instead. */
const rowFields = (row: MovementTruthRow, includePosition = true): ConfirmedFields => {
  const bank = finiteScalar(row.encounter_creatures.movement_pct), aps = finiteScalar(row.encounter_creatures.aps);
  return {
    ...(includePosition ? {
      x_pos: Number(row.position?.x_pos ?? row.encounter_creatures.x_pos ?? 0),
      y_pos: Number(row.position?.y_pos ?? row.encounter_creatures.y_pos ?? 0),
      z_pos: Number(row.position?.z_pos ?? row.encounter_creatures.z_pos ?? 0),
    } : {}),
    movement_pct: bank ?? 0,
    aps: aps !== undefined && Number.isInteger(aps) ? aps : 0,
  };
};
/** Last AUTHORITATIVE {rev, position (+ bank + aps)} for an actor. Discarding optimistic state restores ALL of
 * it: a revision alone would leave an invalidated destination behind (Codex review 2, findings 1–2), a
 * position alone would leave a spent bank behind (M1b), and a bank alone a spent AP (M1c). */
function confirmedFor(key: string, row: MovementTruthRow): Confirmed {
  const known = confirmed.get(key);
  if (known) return known;
  // First sight: the snapshot's stored revision and scalars are authoritative by construction.
  const initial = Number(row.encounter_creatures.movement_rev ?? 0);
  const rev = Number.isFinite(initial) ? initial : 0;
  const fields = rowFields(row) as Required<ConfirmedFields>;
  const entry: Confirmed = { rev, position: { ...fields }, source: 'snapshot', fieldRev: { x_pos: rev, y_pos: rev, z_pos: rev, movement_pct: rev, aps: rev } };
  confirmed.set(key, entry);
  return entry;
}
const confirmedRev = (key: string, row: MovementTruthRow): number => confirmedFor(key, row).rev;
/** PER-FIELD banking: every field the payload carries lands when `rev` is at least the revision that last
 * confirmed THAT field; absent fields keep their value AND their revision (a position-only confirmation never
 * erases the last authoritative bank or AP — Codex review 2026-09-06, finding 1 — and an A1 aps-only
 * confirmation never re-stamps the position). The snapshot's revision only ever rises. The entry must exist
 * (confirmedFor first). Returns true when any field changed. */
function setConfirmed(key: string, rev: number, fields: ConfirmedFields, source: ConfirmedSource = 'frame'): boolean {
  const known = confirmed.get(key);
  if (!known) throw new Error('confirmed snapshot missing: ' + key);
  const position = { ...known.position }, fieldRev = { ...known.fieldRev };
  let changed = false;
  for (const field of CONFIRMED_FIELDS) {
    const value = fields[field];
    if (value === undefined || !Number.isFinite(Number(value)) || rev < fieldRev[field]) continue;
    if (position[field] !== Number(value)) changed = true;
    position[field] = Number(value); fieldRev[field] = rev;
  }
  confirmed.set(key, { rev: Math.max(rev, known.rev), position, source: rev >= known.rev ? source : known.source, fieldRev });
  return changed;
}
/** Advance the confirmed REVISION (an action frame moves no position) and bank the scalars the action carried —
 * A1 feeds `aps` here so the shared snapshot never lags a Dodge (M1c); per-field, so an action frame that arrives
 * before an older movement frame does not stamp the position it never carried. */
export function bankConfirmedRev(encounterId: string, actorId: string, row: MovementTruthRow, rev: number, scalars: Partial<Pick<MovementTruthChange, 'aps' | 'movement_pct'>> = {}): void {
  const key = keyOf(encounterId, actorId);
  confirmedFor(key, row);
  setConfirmed(key, rev, scalars, 'commit');
}
export function confirmedRevFor(encounterId: string, actorId: string, row: MovementTruthRow): number { return confirmedRev(keyOf(encounterId, actorId), row); }
/** Confirmed snapshot for encounter preservation and diagnostics through window.ftMovementTruthConfirmed. */
export function confirmedSnapshotFor(encounterId: string, actorId: string): Confirmed | null { return confirmed.get(keyOf(encounterId, actorId)) ?? null; }
/** R1 (2026-09-07): a LEGACY install IS saved truth with provenance, banked from the INSTALLED PAYLOAD (a whole
 * rebuilt row, or the rebuilt `encounter_creatures` / `position` sections of a delta) — never from a merged
 * rendered row, whose untouched fields may be this browser's optimism or a peer's provisional (Codex M1c review,
 * finding 1). Strictly NEWER revisions bank (every legacy writer of a client-truth column bumps the revision —
 * owner Q3 2026-09-07 — so an equal revision cannot carry different scalars; arrival order never decides,
 * finding 6). An OLDER install is stale: the confirmed scalars are re-applied onto the row so a stale snapshot
 * cannot lower what is rendered while the watermark stays ahead (finding 2). Pending optimistic operations are
 * left to their CAS answer. Never called for this browser's own render (the `local` caster_update flag). */
export function bankLegacyTruth(encounterId: string, actorId: string, row: MovementTruthRow, rev: number, fields: ConfirmedFields, source: ConfirmedSource): void {
  if (!Number.isInteger(rev) || rev < 0) return;
  const key = keyOf(encounterId, actorId);
  confirmedFor(key, row);
  // Per field (Codex M1c review A2, finding 2): an install older than the actor watermark can still carry the
  // position/bank a newer aps-only confirmation never supplied; setConfirmed lands each field by its own revision.
  setConfirmed(key, rev, fields, source);
  reconcileRow(key, row);
}
/** With NO optimistic operation outstanding, the rendered row must equal the confirmed snapshot (Codex M1c review
 * A2, findings 2, 3, 9): a stale install is lifted, and fields banked out of order are rendered. With work pending
 * the row is the optimistic overlay — the outbox's answers reconcile it, and no revision is ever invented from a
 * request count. Returns true when the row changed. */
function reconcileRow(key: string, row: MovementTruthRow): boolean {
  if ((outboxes.get(key)?.pending() ?? 0) > 0) return false;
  const known = confirmedFor(key, row);
  if (!rowDiffers(row, known.position) && Number(row.encounter_creatures.movement_rev ?? 0) === known.rev) return false;
  applyMovementTruth(row, { ...known.position, rev: known.rev });
  return true;
}
/** Speculative ordering shared by every schema (Codex M1c review A2, finding 5): a provisional frame renders only
 * when newer than saved truth AND newer than the last provisional rendered for the actor. */
export function admitProvisional(encounterId: string, actorId: string, row: MovementTruthRow, rev: number, operationId = ''): boolean {
  const key = keyOf(encounterId, actorId);
  if (rev <= confirmedFor(key, row).rev || rev < (provisionalSeen.get(key) ?? 0)) return false;
  provisionalSeen.set(key, rev);
  // MV3 follow-up (2026-09-28): a schema that can RECOVER from the resync read (it installs `ft:actor-truth-resync`) passes
  // its operation id and arms the actor's watch. A schema that cannot (status / action sections are not in the read)
  // passes none — a watch for it would only resolve on an unrelated revision (Codex follow-up review findings 4-6).
  if (operationId !== '') armActorResync(encounterId, actorId, rev, operationId);
  return true;
}
/** The confirmed AP for an actor (A1 restores the shared scalar from here, never from a rollback capture). */
export function confirmedApsFor(encounterId: string, actorId: string, row: MovementTruthRow): number {
  return Number(confirmedFor(keyOf(encounterId, actorId), row).position.aps ?? 0);
}
/** A row BEHIND saved truth with nothing pending was installed stale (an older row object swapped in after this browser's own commit):
 *  raise it to the confirmed snapshot. Never lowers — a row ahead of confirmed is a peer's provisional render, left to its frame. */
function liftStaleRow(key: string, row: MovementTruthRow): boolean {
  if ((outboxes.get(key)?.pending() ?? 0) > 0) return false;
  const known = confirmedFor(key, row);
  if (Number(row.encounter_creatures.movement_rev ?? 0) >= known.rev) return false;
  applyMovementTruth(row, { ...known.position, rev: known.rev });
  return true;
}
export function liftStaleActorRow(encounterId: string, actorId: string, row: MovementTruthRow): boolean { return liftStaleRow(keyOf(encounterId, actorId), row); }
/** A1 hook: after an action's own effects were reverted, the row's shared scalars come from the snapshot. */
export function reconcileActorRow(encounterId: string, actorId: string, row: MovementTruthRow): boolean {
  return reconcileRow(keyOf(encounterId, actorId), row);
}
/** A whole legacy row install (delta row, caster_update adopt, encounter snapshot member). */
export function bankLegacyRowTruth(encounterId: string, actorId: string, row: MovementTruthRow, source: ConfirmedSource): void {
  const rowRev = Number(row.encounter_creatures.movement_rev);
  if (!Number.isInteger(rowRev)) return;
  bankLegacyTruth(encounterId, actorId, row, rowRev, rowFields(row), source);
}
/** A legacy delta's SECTION install: only the rebuilt sections it carried are truth (`encounter_creatures` for the
 * revision + bank + aps; `position` for the coordinates). A delta without the `encounter_creatures` section
 * carries no revision and banks nothing. */
export function bankLegacyDeltaTruth(encounterId: string, actorId: string, row: MovementTruthRow, sections: { encounter_creatures?: Record<string, unknown> | null; position?: Record<string, unknown> | null }, source: ConfirmedSource): void {
  const ec = asRow(sections.encounter_creatures);
  if (!ec) return;
  const rev = Number(scalar(ec.movement_rev));
  const position = asRow(sections.position);
  const fields = rowFields({ encounter_creatures: ec, position: position ?? undefined }, position !== null);
  bankLegacyTruth(encounterId, actorId, row, rev, fields, source);
}
export function pendingFor(encounterId: string, actorId: string): number { return outboxes.get(keyOf(encounterId, actorId))?.pending() ?? 0; }
export { rowFor as actorRowFor, encounterFor as actorEncounterFor, status as actorHasStatus };
function render(command: MovementTruthCommand, row: MovementTruthRow): void {
  // `local`: this browser's own (possibly optimistic) row — never banked as legacy truth by the caster hook.
  const paths = ['position', ...Object.keys(command.updates).map(column => `encounter_creatures.${column}`)];
  if (command.wet || command.teleport || command.squeeze || command.burrow || command.trap || command.overrun || command.stealth || command.climb)
    paths.push('statuses');
  handleCasterUpdate({ type: 'caster_update', caster_arr: row as unknown as CasterArr, local: true },
    paths);
  // Light and vision derive from current positions. Both the mover and peer receivers
  // must re-project the scene after installing a light-bearing row, for every speed.
  if (['bright_light_dim_light', 'darkness', 'magical_darkness'].some(key => Math.abs(Number(ftLayerValue(row, key))) > 0)) {
    window.dispatchEvent(new CustomEvent('ft:encounter-lighting-changed', {
      detail: { encounterId: command.encounter_id, encounterArr: encounterFor(command.encounter_id) },
    }));
  }
  window.dispatchEvent(new CustomEvent('ft:movement-truth-applied', { detail: { command, get applied_frame() { return appliedFrameOf(command); } } }));
}
function renderFailed(error: unknown, command: MovementTruthCommand): void {
  window.dispatchEvent(new CustomEvent('ft:movement-render-failed', { detail: { error, command } }));
}
const movementStatusId = (key: string): string | undefined => key === 'burrowed' ? BURROWED_ITEM_ID : VISUAL_STATUS_EFFECTS[key];
function storedStatusMatches(value: Row, key: string): boolean {
  const id = movementStatusId(key);
  return !!id && (statusItemId(value) || String(ftItemsCol(value, 'item_id') ?? '').toUpperCase()) === id.toUpperCase();
}
function status(row: Row, key: string): boolean {
  const id = movementStatusId(key);
  return !!id && ftStatusEffectPermanentUuids(row).some(value => value.toUpperCase() === id.toUpperCase())
    || statusRows(row, 'target').some(value => storedStatusMatches(value, key));
}
const LINK_STATUS_KEYS = ['riding', 'ridden', 'piloting', 'piloted', 'grappling', 'grappled'];
/** Compound membership comes from a held row's identity, without requiring unrelated definitions. */
function linkStatus(value: Row): boolean {
  return draftLinkKind(statusItemId(value)) !== undefined || LINK_STATUS_KEYS.some(key => storedStatusMatches(value, key));
}
export function movementScopeReaders(encounter: Row): PlainWalkReaders {
  const read: PlainWalkReaders = {
    name: row => (window as unknown as { getCreatureName?: (row: unknown) => string }).getCreatureName?.(row) ?? '',
    value: (row, key) => ftLayerValue(row, key),
    entries: (row, key) => {
      const compiled = own(own(own(row, 'computed'), 'creature'), 'abilities');
      const slot = own(compiled, key);
      return Array.isArray(slot) ? slot : ftLayerEntries(row, key);
    },
    definition: id => asRow(own(encounter.catalog, id))
      ?? asRow(window.FateTide?.objectCache?.get(id)),
    statuses: row => statusRows(row, 'target').flatMap(value =>
      ftCreatureStatuses({ ...row, statuses: { target: [value] } }, 'target') as Row[]),
    hasStatus: status,
    position: row => [ftPosCoord(row, 'x_pos') ?? 0, ftPosCoord(row, 'y_pos') ?? 0, ftPosCoord(row, 'z_pos') ?? 0],
    inCombat: (enc, row) => creatureIsInActiveCombat(enc, row as never),
    linkStatus,
    opposingTeams: (left, right) => opposingTeams(left as never, right as never),
    geometry: row => {
      const sizeId = String(ftEffectiveSizeId(row) ?? ftLayerValue(row, 'size') ?? '');
      const size = SIZE_TYPES.find(value => value.item_id.toUpperCase() === sizeId.toUpperCase());
      if (!size) return null;
      // Owner ruling 2026-09-03 (PHP twin `ft_ground_occupancy_height_ft`): collision comes from the size tier —
      // the tiers are cubes, so height IS the base dimension — never from token art. This adapter used the
      // standee-sprite height (picture aspect × thumbnail width) until 2026-09-10, which made a Large actor with
      // a portrait capture 15 ft tall, fail the 10 ft-ceiling headroom test, and hand every plain walk in the
      // avatar cell back to legacy (`room-clearance`). Art-driven height stays where it is presentation only.
      const diameter = Number(size.base_size);
      const body = physicalCreatureBody(meshesOf(row as never));
      return swarmMovementBody(row, body ? { ...body, rulesDiameter: diameter } : { diameter, height: diameter });
    },
  };
  return read;
}

/** Shared parent truth for the iframe preview and the commit preflight. No writes or unported telemetry. */
let inventoryDriveAttachment: ((encounter: Row, row: Row) => boolean) | null = null;
export function registerInventoryDriveAttachment(reader: (encounter: Row, row: Row) => boolean): void { inventoryDriveAttachment = reader; }
export function inventoryDrivePlan(encounterId: string, actorId: string, speed?: string) {
  const encounter = encounterFor(encounterId), mover = rowFor(encounterId, actorId);
  if (!encounter || !mover) return null;
  if (!statusRows(mover, 'target').some(row => storedStatusMatches(row, 'piloting') || draftLinkKind(statusItemId(row)) === 'latched')) return null;
  const read = movementScopeReaders(encounter);
  const decision = inventoryDriveDecision(encounter, mover,
    speed ?? moverSpeedVariable(ftEncounterCreatureField(mover.encounter_creatures, 'preferred_speed_type')), read,
    row => inventoryDriveAttachment?.(encounter, row) ?? false);
  return decision?.kind === 'drag' && !inventoryDriveAttachment && !decision.reason
    ? { ...decision, reason: 'The drag attachment state is still loading. Nothing was moved.' } : decision;
}
export function movementDispatchContext(encounterId: string): MovementDispatchContext | null {
  const encounter = encounterFor(encounterId);
  if (!encounter) return null;
  const read = movementScopeReaders(encounter);
  read.crossingWorld = () => {
    const shape = pageSceneShape(encounter);
    return shape ? { shape: shape as never, canvasExterior: Number(encounterField(encounter, 'encounter_inside') ?? 0) !== 1, overlayUnknown: false }
      : walkWallWorld?.(encounter, undefined) ?? null;
  };
  return { encounter, read,
    follower: id => rowFor(encounterId, id)
      ?? (encounter.encounter_inventories_arr as Row[] | undefined)?.find(row =>
        String(ftInventoryField(row, 'inventory_id') ?? '').toUpperCase() === id.toUpperCase()) ?? null,
    name: row => (window as unknown as { getCreatureName: (row: unknown) => string }).getCreatureName(row) };
}
function isPosition(value: unknown): value is MovementTruthRejectedResult['position'] {
  return ['x_pos', 'y_pos', 'z_pos'].every(key => Number.isFinite(own(value, key)));
}
const movementStatusPlans20 = new WeakMap<MovementTruthCommand, Promise<import('../janus-engine/statuses/gm-boundary.js').GmBoundaryVerdict>>();
const movementStatusFrames20 = new WeakMap<MovementTruthCommand, AppliedFrameResult>();
const savedMovementFrames = new WeakMap<MovementTruthCommand, string>();
async function post(command: MovementTruthCommand): Promise<unknown> {
  let statusPlan: import('../janus-engine/statuses/gm-boundary.js').GmBoundaryVerdict | undefined;
  // The planner's inputs: the posted bytes plus each compound follower's end-of-move squeeze decision, which the receiver
  // never sees as a member column — it reaches the follower's rows through the same status_plan as the mover's.
  const statusInputs = movementStatusInputs20(command);
  if (command.burrow?.transition && command.burrow.transition !== 'none' || command.squeeze
      || (command as { rest?: unknown }).rest || command.teleport?.clears?.length || (command.teleport?.dice ?? 0) > 0 || statusInputs.compound?.members.some(member => member.squeeze || member.hunkered)) {
    const plan = (window as unknown as { ftPlanMovementStatuses20?: (encounterId: string,
      command: import('../janus-engine/statuses/application-inputs-20.js').MovementStatusInputs20) => Promise<import('../janus-engine/statuses/gm-boundary.js').GmBoundaryVerdict> }).ftPlanMovementStatuses20;
    if (!plan) throw new MovementTruthTerminal(422, 'Movement status planner is not available.', null);
    // The outbox freezes its request. Keep the resolved bytes beside that immutable snapshot;
    // every transport retry sends the same status ids, connections and recorded outcome.
    let pending = movementStatusPlans20.get(command);
    if (!pending) { pending = plan(command.encounter_id, statusInputs); movementStatusPlans20.set(command, pending); }
    try { statusPlan = await withAsyncDeadline(() => pending!, 6000); }
    catch (error) { throw new MovementTruthTerminal(422, error instanceof Error ? error.message : String(error), null); }
  }
  // Read the SQL-row fingerprint once. Retries keep the original CAS bytes, including after a lost answer.
  for (const member of command.compound?.members ?? []) if (member.inventory_id && !member.expected_row_revision) {
    const query = new URLSearchParams({ encounter_id: command.encounter_id, actor_id: command.actor_id, inventory_id: member.inventory_id });
    const { response, result } = await withAsyncDeadline(async signal => {
      const response = await fetch(`/options/tools/encounter_creatures/ft3d/movement_solid_truth_inc.php?${query}`, { method: 'GET', signal });
      const result = await response.json() as { ok?: boolean; row_revision?: string; error?: string };
      return { response, result };
    }, 6000);
    if (response.status >= 400 && response.status < 500) throw new MovementTruthTerminal(response.status, result.error ?? 'Vehicle unavailable', null);
    if (!response.ok || !result.ok || !/^[0-9a-f]{64}$/.test(result.row_revision ?? '')) throw new Error(result.error ?? 'Vehicle revision unavailable');
    member.expected_row_revision = result.row_revision!;
  }
  // Solid-rule decisions use their shared lazy-save receiver; the envelope, frames and outbox are the walk's.
  const endpoint = command.teleport || command.squeeze || command.burrow || command.compound || command.trap || command.overrun || command.stealth || command.climb || (command as { rest?: unknown }).rest
    ? '/options/tools/encounter_creatures/ft3d/movement_solid_truth_inc.php'
    : '/options/tools/encounter_creatures/ft3d/movement_truth_save_inc.php';
  // `follower_squeezes` is the planner's input, frozen with the command so a retry plans the same settlement; its decision is already
  // inside status_plan, and the receiver's creature member carries exactly id/expected_rev/before/after — it is never posted.
  let response: Response;
  try { response = await saveAppliedTruth(endpoint, command, {
    method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ ...command, follower_squeezes: undefined, follower_hunkers: undefined, ...(statusPlan ? { status_plan: statusPlan } : {}),
      ...(command.wet ? { wet: { after: Object.fromEntries(Object.entries(command.wet.after).filter(([key]) => key !== 'status_arr')) } } : {}) }),
    // A request that never settles must not leave the head `sending` forever.
    signal: AbortSignal.timeout(20000),
  }, !movementNeedsSavedDelivery(command)); }
  catch (error) {
    if (endpoint.endsWith('/movement_solid_truth_inc.php')) throw new MovementTruthTerminal(503, 'movement:save-unconfirmed', null);
    throw error;
  }
  let result: Row | null = null;
  try { result = await withAsyncDeadline(() => response.json() as Promise<Row>, 6000); } catch { result = null; }
  if (!response.ok && result?.error === 'stale' && Number.isInteger(result.rev) && isPosition(result.position)) {
    throw new MovementTruthRejected(result as unknown as MovementTruthRejectedResult);
  }
  if (response.ok && result?.ok === true) {
    const bytes = savedMovementFrames.get(command);
    if (bytes) {
      const frame = savedMovementFrame(JSON.parse(bytes), result);
      await withAsyncDeadline(() => Promise.resolve(window.ftPushAppliedTruth!({ type: 'janus_movement_truth',
        command: { ...command, rev: result!.rev, position: result!.position, committed: true }, applied_frame: frame })), 6000);
      await window.ftSaveAppliedTruth?.(command.operation_id, async () => response);
    }
    return result;
  }
  const message = String(result?.error ?? (response.statusText || 'Movement delivery failed'));
  if (endpoint.endsWith('/movement_solid_truth_inc.php') && (response.status >= 500 || response.ok))
    throw new MovementTruthTerminal(503, 'movement:save-unconfirmed', result);
  // 4xx other than stale is definitive for these bytes, JSON body or not (an HTML 403 from a
  // proxy is still a refusal); 5xx / network / unparseable-2xx stays retryable.
  if (response.status >= 400 && response.status < 500) throw new MovementTruthTerminal(response.status, message, result as Record<string, unknown> | null);
  throw new Error(message);
}
/** The walk is saved before its browser-resolved zone exits. The ability commit CAS-saves the exact removal verdict;
 * a stale page reclassifies through window.ftZoneReconcileNow, and transport retries retain the same operation. */
function commitPendingZoneExits(operationId: string): void {
  const pending = pendingZoneExits.get(operationId);
  if (!pending) return;
  pendingZoneExits.delete(operationId);
  const key = keyOf(pending.encounterId, pending.actorId);
  const ids = pending.strips.map(s => s.statusId);
  // Codex S1 review F3: a strip that does not land is retried — transport failures re-send the same strips, a refusal (the page
  // judged from stale state) re-classifies from what the page holds NOW (ftZoneReconcileNow). The hold is released only once
  // the work is settled or the attempts are spent.
  const attempt = (n: number): void => {
    const work = n === 0 ? commitZoneExits(pending.encounterId, pending.strips) : reconcileZonesNow(pending.encounterId, pending.actorId);
    void work.then(outcome => {
      // `clear` / `handback`: reclassification has no supported exits left for this page to save.
      const done = outcome.kind === 'committed' || outcome.kind === 'clear' || outcome.kind === 'handback';
      if (done || n >= 3) {
        if (outcome.kind === 'committed') holdZoneExits(key, ids, Date.now() + ZONE_EXIT_SETTLE_MS); else releaseZoneExits(key, ids);
        window.dispatchEvent(new CustomEvent('ft:walk-zone-exits', { detail: { operationId, encounterId: pending.encounterId, attempts: n + 1, outcome } }));
        return;
      }
      setTimeout(() => attempt(n + 1), 1500 * (n + 1));
    }, () => { if (n >= 3) releaseZoneExits(key, ids); else setTimeout(() => attempt(n + 1), 1500 * (n + 1)); });
  };
  attempt(0);
}
/** Janus session 3 step 7: a FORCE MOVE's end-of-move strips (force-move-truth.ts) — held exactly like a walk's, committed through MV2's
 *  exit commit once the move is confirmed (accepted → commitPendingZoneExits), dropped with it on a refusal. Call BEFORE enqueueing. */
export function queueZoneExits(operationId: string, encounterId: string, actorId: string, strips: NonNullable<PlainWalkResult['zone_exits']>): void {
  if (!strips.length) return;
  pendingZoneExits.set(operationId, { encounterId, actorId, strips });
  holdZoneExits(keyOf(encounterId, actorId), strips.map(s => s.statusId), null);
  if (pendingZoneExits.size > 256) dropPendingZoneExits(pendingZoneExits.keys().next().value!);
}
/** Janus session 3 step 7: a Force Move re-based onto its MOVER OF RECORD (a dragged rider / piloted row — the legacy endpoint's redirect)
 *  runs the same guards tryCommitPlainWalk ran for the dragged row, for that actor: `refuse` = a local refusal's text, `busy` = hand back. */
export function forceActorGuard(encounterId: string, actorId: string): { refuse?: string; busy?: string } | null {
  const permission = movementPermission(encounterId, actorId);
  if (!permission.allowed) return { refuse: permission.message };
  if (zoneExitsUnsettled(encounterId, actorId)) return { refuse: ZONE_EXIT_PENDING_MESSAGE };
  if (zoneEntryPlanner?.pending(encounterId, actorId)) return { refuse: ZONE_ENTRY_PENDING_MESSAGE };
  if (afterWorkHeld(encounterId, actorId)) return { refuse: CONSEQUENCE_PENDING_MESSAGE };
  const key = keyOf(encounterId, actorId);
  const row = rowFor(encounterId, actorId);
  if (row) liftStaleRow(key, row);
  if ([...compoundOps.values()].includes(key)) return { refuse: COMPOUND_PENDING_MESSAGE };
  if ([...trapOps.values()].includes(key)) return { refuse: TRAP_PENDING_MESSAGE };
  if ([...overrunOps.values()].includes(key)) return { refuse: OVERRUN_PENDING_MESSAGE };
  if ([...climbOps.values()].includes(key)) return { refuse: CLIMB_PENDING_MESSAGE };
  if (restPending(key)) return { refuse: REST_PENDING_MESSAGE };
  if (legacyMoveBusy(encounterId, actorId)) return { busy: 'legacy-move-in-flight' };
  return null;
}
/** A Force Move carrying compound followers: the actor's next move waits until it settles (as a compound walk does). */
export function noteCompoundOperation(operationId: string, encounterId: string, actorId: string): void {
  compoundOps.set(operationId, keyOf(encounterId, actorId));
  if (compoundOps.size > 256) compoundOps.delete(compoundOps.keys().next().value!);
}
/** Release strips queued for an operation that never entered the outbox (a synchronous enqueue refusal). */
export function dropQueuedZoneExits(operationId: string): void { dropPendingZoneExits(operationId); }
/** Session A step 10 (2026-09-30): work a move owes AFTER it is accepted — a Force Move's REAL FALL (fall-truth.ts), resolved before the
 *  move was sent, commits once the landing is saved (`accepted`, with the mover's saved revision) or is dropped with the move (`dropped`).
 *  Call BEFORE enqueueing. */
type AfterAcceptedWork = { encounterId: string; actorId: string; accepted: (rev: number) => Promise<unknown> | void; dropped: () => void };
const afterAccepted = new Map<string, AfterAcceptedWork>();
/** Actors owing after-accept work (queued, or its commit still in flight): their next move waits (Codex 9–10 review P1 — a second move
 *  before the fall saved advanced the revision the fall was derived from, and the owed fall was lost as `stale`). */
const afterWorkPending = new Map<string, number>();
export const CONSEQUENCE_PENDING_MESSAGE = 'The consequences of the last move are still being saved. Try moving again shortly.';
const afterWorkHeld = (encounterId: string, actorId: string): boolean => (afterWorkPending.get(keyOf(encounterId, actorId)) ?? 0) > 0;
function releaseAfterWork(work: AfterAcceptedWork): void {
  const key = keyOf(work.encounterId, work.actorId), n = (afterWorkPending.get(key) ?? 0) - 1;
  if (n > 0) afterWorkPending.set(key, n); else afterWorkPending.delete(key);
}
export function queueAfterAccepted(operationId: string, work: AfterAcceptedWork): void {
  afterAccepted.set(operationId, work);
  const key = keyOf(work.encounterId, work.actorId);
  afterWorkPending.set(key, (afterWorkPending.get(key) ?? 0) + 1);
  if (afterAccepted.size > 256) { const first = afterAccepted.keys().next().value!; settleAfterAccepted(first, null); }
}
/** A command whose enqueue threw: drop its after-accept work and release the actor hold (Codex Round 32 P3). */
export function dropAfterAccepted(operationId: string): void { settleAfterAccepted(operationId, null); }
function settleAfterAccepted(operationId: string, rev: number | null): void {
  const work = afterAccepted.get(operationId);
  if (!work) return;
  afterAccepted.delete(operationId);
  try {
    if (rev === null) { work.dropped(); releaseAfterWork(work); return; }
    void Promise.resolve(work.accepted(rev)).finally(() => releaseAfterWork(work));
  } catch (error) { releaseAfterWork(work); console.warn('[MOVEMENT-TRUTH] after-accept work failed', error); }
}
/** Does the row render something other than the authoritative scalars? */
function rowDiffers(row: MovementTruthRow, authoritative: MovementTruthChange): boolean {
  return ['x_pos', 'y_pos', 'z_pos'].some(key => Number(own(authoritative, key)) !== Number(row.position?.[key] ?? row.encounter_creatures[key] ?? 0))
    || (authoritative.movement_pct !== undefined && Number(authoritative.movement_pct) !== Number(scalar(row.encounter_creatures.movement_pct) ?? 0))
    || (authoritative.aps !== undefined && Number(authoritative.aps) !== Number(scalar(row.encounter_creatures.aps) ?? 0));
}
/** The walk's owed zone entries, committed once the move itself is saved — shared by the plain walk (accepted) and the opportunity-walk
 *  envelope (attack-truth.ts, settleWalkZoneWork). `result`: the move's saved revision / position (and its compound members' saved truth). */
function settleZoneEntryChain(operationId: string, encounterId: string, actorId: string, result: Row): void {
  // The walk's entries, then each carried follower's (D-051), ONE AFTER ANOTHER (Codex review #2 F1): each commits with the revisions the walk
  // and every earlier entry confirmed (a zone writing its caster saw three entrants on one caster revision — the third went stale, its effect
  // lost). The seed is the walk's own saved truth: the mover's revision and every follower's.
  const point = (p: unknown): { x: number; y: number; z: number } => ({ x: Number(own(p, 'x_pos') ?? 0), y: Number(own(p, 'y_pos') ?? 0), z: Number(own(p, 'z_pos') ?? 0) });
  const savedMembers = asRow(result.members);
  const reactionMembers = asRow(asRow(result.frame)?.reaction_members);
  const seed: ChainRevs = { revs: new Map(), touched: new Set() };
  if (Number.isInteger(result.rev)) seed.revs.set(actorId.toUpperCase(), Number(result.rev));
  for (const [memberId, saved] of Object.entries({ ...savedMembers, ...reactionMembers })) { const s = asRow(saved); if (s && Number.isInteger(s.rev)) seed.revs.set(memberId.toUpperCase(), Number(s.rev)); }
  const steps: ChainStep[] = [];
  const entryToken = pendingZoneEntries.get(operationId);
  if (entryToken !== undefined) {
    pendingZoneEntries.delete(operationId);
    if (isPosition(result.position) && zoneEntryPlanner) { const planner = zoneEntryPlanner, at = point(result.position); steps.push(c => planner.commit(entryToken, Number(result.rev), at, c)); }
    else zoneEntryPlanner?.drop(entryToken);
  }
  // D-051: each carried follower's entries, at the follower's saved landing and revision
  const followerEntries = pendingFollowerEntries.get(operationId);
  if (followerEntries) {
    pendingFollowerEntries.delete(operationId);
    for (const f of followerEntries) {
      if (f.stationary && zoneEntryPlanner) { const planner = zoneEntryPlanner, { rev, at } = f.stationary; steps.push(c => planner.commit(f.token, Math.max(rev, c.revs.get(f.id) ?? rev), at, c)); continue; }
      const s = asRow(savedMembers?.[f.id] ?? savedMembers?.[f.id.toLowerCase()]);
      if (s && Number.isInteger(s.rev) && isPosition(s.position) && zoneEntryPlanner) { const planner = zoneEntryPlanner, rev = Number(s.rev), at = point(s.position); steps.push(c => planner.commit(f.token, rev, at, c)); }
      else zoneEntryPlanner?.drop(f.token);
    }
  }
  // Codex review #2 F2: while the followers' entries are owed, the CARRIER stays held too (it could otherwise drag them again and move the
  // arrival revisions under the owed commits) — released once every entry of the chain settled.
  const chainHold = followerEntries?.length ? operationId + ':follower-entries' : null;
  if (chainHold) compoundOps.set(chainHold, keyOf(encounterId, actorId));
  if (steps.length) void runChain(seed, steps).finally(() => { if (chainHold) compoundOps.delete(chainHold); });
  else if (chainHold) compoundOps.delete(chainHold);
}
function accepted(command: MovementTruthCommand, result: MovementTruthAcceptedResult): void {
  commitPendingZoneExits(command.operation_id);
  settleAfterAccepted(command.operation_id, Number.isInteger(result.rev) ? Number(result.rev) : null);
  settleZoneEntryChain(command.operation_id, command.encounter_id, command.actor_id, result as unknown as Row);
  settleCompound(command.operation_id, true);
  // Session 3 step 4: a trapped walk's fire landed with it — the victim's saved columns (vitality, statuses, luck …) on every roster copy.
  const trapVictim = (result as unknown as Row).trap_victim;
  applyOverrunParties(command.encounter_id, (result as unknown as Row).overrun_parties);
  if (command.overrun) try { overrunWalkPlanner?.recorded?.(command.encounter_id, command.actor_id, command.overrun, command.operation_id); } catch (error) { console.warn('[MOVEMENT-TRUTH] overrun acceptance failed', error); }
  applyAcceptedStealth(command, result);
  if (trapVictim) {
    try { (window as unknown as { ftTrapApplyVictim?: (e: string, s: unknown) => void }).ftTrapApplyVictim?.(command.encounter_id, trapVictim); }
    catch (error) { console.warn('[MOVEMENT-TRUTH] trap victim apply failed', error); }
    window.dispatchEvent(new CustomEvent('ft:walk-trap-fired', { detail: { operationId: command.operation_id, encounterId: command.encounter_id, actorId: command.actor_id, victim: trapVictim } }));
  }
  // MV4 (M6a): the followers' saved truth came back with the mover's — install it as their committed frames (the published ones,
  // carrying a derived operation id, arrive later at an equal revision and change nothing).
  const members = asRow((result as unknown as Row).members);
  for (const [memberId, saved] of Object.entries(members ?? {})) {
    const s = asRow(saved);
    if (s?.inventory_id && isPosition(s.position)) {
      const p = s.position as MovementTruthChange;
      (window as unknown as { ftApplyTransportInventoryTruth?: (frame: unknown) => void }).ftApplyTransportInventoryTruth?.({
        schema: 'janus.inventory.floor_move.v1', operation_id: s.operation_id ?? command.operation_id + ':' + memberId,
        encounter_id: command.encounter_id, inventory_id: memberId, committed: true, position: { x: p.x_pos, y: p.y_pos, z: p.z_pos } });
      continue;
    }
    if (!s || !Number.isInteger(s.rev) || !isPosition(s.position)) continue;
    handleMovementTruth({ schema: MOVEMENT_SCHEMA, operation_id: command.operation_id + ':' + memberId, encounter_id: command.encounter_id, actor_id: memberId,
      expected_rev: Number(s.rev) - 1, before: s.position as MovementTruthChange, updates: s.position as MovementTruthChange, route: [], rev: Number(s.rev),
      position: s.position as MovementTruthChange, committed: true } as MovementTruthFrame);
  }
  const key = keyOf(command.encounter_id, command.actor_id);
  const row = rowFor(command.encounter_id, command.actor_id);
  if (!row || !isPosition(result.position)) return;
  // Per-field: at an equal revision the server's saved scalars beat a provisional another author published for
  // the same number (Codex review 3, finding 4); fields a newer revision already confirmed are kept.
  confirmedFor(key, row);
  setConfirmed(key, result.rev, result.position, 'commit');
  // A delayed response must never lower truth a newer frame already raised (Codex review
  // 2026-09-06, finding 1). The row's optimistic revision equals result.rev when this is the
  // newest pending move; it is higher when later moves are still queued behind it.
  const optimistic = Number(row.encounter_creatures.movement_rev ?? 0);
  if (result.rev < optimistic) return;
  // Apply the MERGED confirmed scalars, never the raw payload (R3, 2026-09-07): a position-only answer must
  // not leave a stale rendered bank or AP behind. The merged state is stamped with the CONFIRMED revision, which
  // may exceed this answer's when a newer frame already banked (Codex M1c review, finding 2).
  const known = confirmedFor(key, row);
  if (rowDiffers(row, known.position)) {
    applyMovementTruth(row, { ...known.position, rev: known.rev });
    render({ ...command, position: known.position, rev: known.rev }, row);
  } else {
    row.encounter_creatures.movement_rev = known.rev;
  }
}
function dropPendingZoneExits(operationId: string): void {
  settleAfterAccepted(operationId, null);
  settleCompound(operationId);
  const entryToken = pendingZoneEntries.get(operationId);
  if (entryToken !== undefined) { pendingZoneEntries.delete(operationId); zoneEntryPlanner?.drop(entryToken); }
  const followerEntries = pendingFollowerEntries.get(operationId);
  if (followerEntries) { pendingFollowerEntries.delete(operationId); for (const f of followerEntries) zoneEntryPlanner?.drop(f.token); }
  const pending = pendingZoneExits.get(operationId);
  if (!pending) return;
  pendingZoneExits.delete(operationId);
  releaseZoneExits(keyOf(pending.encounterId, pending.actorId), pending.strips.map(s => s.statusId));
}
function rejected(command: MovementTruthCommand, result: MovementTruthRejectedResult): void {
  dropPendingZoneExits(command.operation_id);
  reloadRefusedEncounter(command.encounter_id, command.operation_id);
  refusalOf({ status: 409, error: result.error, terminal: true }, 'move');
  window.dispatchEvent(new CustomEvent('ft:movement-truth-reconciled', { detail: { command, authoritative: result } }));
}
function failed(command: MovementTruthCommand, info: MovementTruthTerminalInfo): void {
  dropPendingZoneExits(command.operation_id);
  reloadRefusedEncounter(command.encounter_id, command.operation_id);
  window.dispatchEvent(new CustomEvent('ft:movement-truth-refused', { detail: { command, info, refusal: refusalOf(info, 'move') } }));
}
/** A stale / refused outcome reloads the encounter and is never retried, so its notice is
 * informational: it expires. Before 2026-09-14 terminal entries never left `deliveries`, so the notice had no exit
 * and stayed on screen for the rest of the session. Only a retryable failure keeps a persistent notice (it needs Retry). */
const TERMINAL_NOTICE_MS = 6000;
const isTerminalDelivery = (value: MovementDelivery): boolean => value.commit === 'failed'
  && (own(value.errors.commit, 'error') === 'stale' || own(value.errors.commit, 'terminal') === true);
function report(delivery: MovementDelivery): void {
  const handled = isTerminalDelivery(delivery)
    && schemaHandlers.get(deliverySchema.get(delivery.operationId) ?? MOVEMENT_SCHEMA)?.handlesRejection?.(delivery.operationId);
  if (delivery.commit === 'delivered' || handled) deliveries.delete(delivery.operationId);
  else deliveries.set(delivery.operationId, delivery);
  window.dispatchEvent(new CustomEvent('ft:movement-delivery-status', { detail: delivery }));
  if (isTerminalDelivery(delivery)) {
    window.setTimeout(() => {
      // A newer report for the same operation owns the entry.
      if (deliveries.get(delivery.operationId) !== delivery) return;
      deliveries.delete(delivery.operationId);
      renderDeliveryNotice();
    }, TERMINAL_NOTICE_MS);
  }
  renderDeliveryNotice();
}
function dismissTerminalDeliveries(): void {
  for (const [operationId, value] of deliveries) if (isTerminalDelivery(value)) deliveries.delete(operationId);
  renderDeliveryNotice();
}
function renderDeliveryNotice(): void {
  const failures = [...deliveries.values()].filter(value => value.commit === 'failed');
  if (!failures.length) { deliveryNotice?.remove(); deliveryNotice = null; return; }
  const stale = failures.filter(value => own(value.errors.commit, 'error') === 'stale');
  const refused = failures.filter(value => own(value.errors.commit, 'terminal') === true);
  const retryable = failures.filter(value => !stale.includes(value) && !refused.includes(value));
  deliveryNotice ??= document.createElement('div');
  deliveryNotice.setAttribute('role', 'alert');
  deliveryNotice.className = 'alert alert-warning position-fixed bottom-0 start-50 translate-middle-x';
  deliveryNotice.style.zIndex = '20000';
  deliveryNotice.dataset.ftMovementDeliveryNotice = '1';
  deliveryNotice.replaceChildren();
  const text = document.createElement('span');
  const messages: string[] = [];
  const staleMessages = new Set<string>();
  for (const value of stale) {
    const schema = deliverySchema.get(value.operationId) ?? MOVEMENT_SCHEMA;
    const banked = bankedOperations.get(value.operationId);
    staleMessages.add(schema === MOVEMENT_SCHEMA
      ? (banked === 'ap'
        ? 'Your move was rejected: this creature\'s state changed elsewhere. Reloading the encounter.'
        : banked === 'bank'
          ? 'Your move was rejected: this creature\'s state changed elsewhere. Reloading the encounter.'
          : 'Your move was rejected: this creature moved elsewhere. Reloading the encounter.')
      : (schemaHandlers.get(schema)?.staleMessage ?? 'Your action was rejected. Reloading the encounter.'));
  }
  messages.push(...staleMessages);
  // The refusal text is per schema: an End Turn or an action refused here is not a cancelled move.
  const refusedMoves = refused.filter(value => (deliverySchema.get(value.operationId) ?? MOVEMENT_SCHEMA) === MOVEMENT_SCHEMA);
  const refusedOther = refused.filter(value => !refusedMoves.includes(value));
  const refusalReasons = (values: MovementDelivery[]): string[] => values.map(value => String(own(value.errors.commit, 'error') ?? ''));
  if (refusedMoves.length) messages.push(movementRefusalMessage(refusalReasons(refusedMoves)));
  if (refusedOther.length) messages.push(['Your action was refused.', ...new Set(refusalReasons(refusedOther).map(reason => reason.trim()).filter(Boolean))].join(' ') + ' Reloading the encounter.');
  const retryableMoves = retryable.some(value => (deliverySchema.get(value.operationId) ?? MOVEMENT_SCHEMA) === MOVEMENT_SCHEMA);
  if (retryable.length) messages.push((retryableMoves ? 'Movement delivery is incomplete. ' : 'Your action has not been saved yet. ') + retryable.map(value => {
    const error = value.errors.commit;
    return error instanceof Error ? error.message : String(error);
  }).join(' '));
  text.textContent = ftCurrentTruthSink() ? failures.map(value => trainingGroundsRefusalMessage(
    String(own(value.errors.commit, 'error') ?? 'Action delivery failed'),
    (deliverySchema.get(value.operationId) ?? MOVEMENT_SCHEMA) === MOVEMENT_SCHEMA ? 'Movement' : 'This action')).filter((value, index, all) => all.indexOf(value) === index).join(' ') : messages.join(' ');
  deliveryNotice.append(text);
  if (retryable.length) {
    const retry = document.createElement('button');
    retry.type = 'button'; retry.className = 'btn btn-sm btn-warning ms-2'; retry.textContent = 'Retry delivery';
    retry.onclick = () => { for (const outbox of outboxes.values()) outbox.retry('commit'); };
    deliveryNotice.append(retry);
  } else {
    const close = document.createElement('button');
    close.type = 'button'; close.className = 'btn-close ms-2 align-middle'; close.setAttribute('aria-label', 'Dismiss');
    close.onclick = dismissTerminalDeliveries;
    deliveryNotice.append(close);
  }
  if (!deliveryNotice.isConnected) document.body.append(deliveryNotice);
}
/** A gameplay refusal the front end decided itself (not your turn). The iframe shows the message and snaps
 * the token back; no request is made (owner 2026-09-06: PHP is never consulted for a gameplay gate). */
export class MovementRefused extends Error {
  constructor(message: string, readonly reasonCode: string | null = null) { super(message); this.name = 'MovementRefused'; }
}
/** Read-only, before geometry, animation, AP changes, or an outbox enqueue. */
export function movementPermission(encounterId: string, actorId: string): { allowed: boolean; message: string } {
  const encounter = encounterFor(encounterId), row = rowFor(encounterId, actorId);
  if (!encounter || !row) return { allowed: false, message: 'Movement permissions are still loading. Try again in a moment.' };
  const campaignId = String(encounterField(encounter, 'encounter_master_parent_id') ?? '').toUpperCase();
  const ft = window.FateTide as unknown as { campaignCache?: Map<string, unknown>; currentCampaignArr?: unknown } | undefined;
  const candidates = [asRow(encounter.campaign_arr), asRow(ft?.campaignCache?.get(campaignId)), asRow(ft?.currentCampaignArr)];
  const campaign = candidates.find(value => campaignId !== '' && String(_ftCamField(value, 'campaign_id') ?? '').toUpperCase() === campaignId);
  const allowed = actorMovementPermission(window.FateTide?.user?.id,
    encounterCreatureField(row.encounter_creatures, 'encounter_creature_created_by_id'),
    _ftCamField(campaign, 'campaign_gm'), window.FateTide?.user?.primaryRole,
    encounterField(encounter, 'encounter_created_by_id'), own(encounter.permissions, 'can_edit'));
  return { allowed, message: allowed ? '' : MOVEMENT_PERMISSION_WARNING };
}
/** Scene admission only: force moves retain actor authority without consulting the moved body's rules. */
export function movementStartPermission(encounterId: string, actorId: string, speed?: string, force = false): { allowed: boolean; message: string } {
  const permission = movementPermission(encounterId, actorId);
  if (!permission.allowed || force) return permission;
  const encounter = encounterFor(encounterId), row = rowFor(encounterId, actorId);
  if (!encounter || !row) return { allowed: false, message: 'Movement permissions are still loading. Try again in a moment.' };
  const read = movementScopeReaders(encounter), mover = moverOfRecord(encounter, row, read);
  const requestedSpeed = mover.id === actorId.toUpperCase() ? speed ?? movementSpeedVariable(encounterId, actorId)
    : movementSpeedVariable(encounterId, mover.id);
  const drive = inventoryDrivePlan(encounterId, mover.id, requestedSpeed);
  // Match commit preflight: a pilot's adopted vehicle speed can be zero while its draft team walks.
  const readers = drive ? { ...read, value: (subject: Row, key: string) => subject === mover.row && key === drive.speed
    ? drive.speedFt : read.value(subject, key) } : read;
  const message = ordinaryMovementStartRefusal(mover.row, readers, {
    encounter, actorId: mover.id, speed: drive?.speed ?? requestedSpeed,
  }) ?? drive?.reason ?? null;
  return { allowed: message === null, message: message ?? '' };
}
/** LEGACY MOVE LANE — one actor, one movement authority in flight, in order (owner 2026-09-10: "when moving, you
 * teleport to previous positions constantly"). A walk the plain-walk scope hands back (another creature's reach,
 * `room-clearance`, force movement …) is POSTed to ft3d_move_encounter_creature.php, which walks from the SERVER's
 * copy of the mover and writes without a revision check, and whose cache patch + WS echo run post-flush (1.3–2.1 s in
 * a large scene). Fired in parallel — every drag made while one is in flight is legacy too, because its route starts
 * at the pending drop, not at the row — the requests walked from stale origins, landed in completion order (the DB
 * could end at an OLDER drop, with the higher revision), and each answer reconciled the standee to its own final or to
 * the stale origin it refused from. So each request waits for the previous one's WRITE TO REACH THIS BROWSER (the row
 * revision rises past the one it was sent from) and for this actor's client-truth outbox to drain, then sends. The
 * caller's promise resolves on the ANSWER with `superseded` = a newer request for the same actor was issued before it
 * arrived — the newest move owns the standee, so a superseded answer must not reconcile it. */
interface LegacyMoveLane { tail: Promise<void>; busy: number; issued: number }
const legacyLanes = new Map<string, LegacyMoveLane>();
/** How long a sent legacy move holds the lane for its echo (the write reaching this browser's row): above the
 * endpoint's documented post-flush fan-out (1.3–2.1 s in a large scene), so the next request walks from fresh truth. */
const LEGACY_ECHO_WAIT_MS = 5000;
/** A legacy move's answer never settles when the scene iframe that owns the request reloads mid-flight, or PHP hangs. */
const LEGACY_SEND_TIMEOUT_MS = 30000;
/** How long a legacy move waits for this actor's client-truth outbox to drain (a commit POST times out at 20 s). */
const LEGACY_IDLE_WAIT_MS = 25000;
export const LEGACY_MOVE_BLOCKED_MESSAGE = 'An earlier move of this creature has not been saved yet. Retry its delivery, then move again.';
export const LEGACY_MOVE_UNCONFIRMED_MESSAGE = 'An earlier move of this creature has not been confirmed yet. Try again in a moment.';
/** A legacy move whose ANSWER timed out may still land: the lane moves on, but a dependent action (a keyhole peek, a
 * push) must not read the saved position until that write shows up (the row revision rises past the one it was sent
 * from) — else the late write lands under it (Codex review 2026-09-28). Kept outside the lane, which is deleted when
 * idle; an entry that never confirms expires, since no PHP request outlives it. */
const unconfirmedLegacy = new Map<string, { revBefore: number | null; since: number }>();
const LEGACY_UNCONFIRMED_EXPIRY_MS = 120000;
function waitFor(test: () => boolean | 'fail', capMs: number): Promise<boolean> {
  return new Promise(resolve => {
    const startedAt = Date.now();
    const tick = (): void => {
      const state = test();
      if (state === true) { resolve(true); return; }
      if (state === 'fail' || Date.now() - startedAt >= capMs) { resolve(false); return; }
      window.setTimeout(tick, 50);
    };
    tick();
  });
}
const rowRevOf = (encounterId: string, actorId: string): number | null => {
  const rev = Number(scalar(rowFor(encounterId, actorId)?.encounter_creatures.movement_rev));
  return Number.isFinite(rev) ? rev : null;
};
/** A legacy move for this actor is queued, in flight, or still waiting for its echo — including one whose ANSWER timed out and
 * whose write has not shown up yet (the lane is gone but PHP may still be writing): a client-truth commit admitted then would
 * race that write (Codex Force Move S2 second pass). Cleared like waitForActorMovement clears it: the row revision rising past
 * the one it was sent from, or expiry. */
export function legacyMoveBusy(encounterId: string, actorId: string): boolean {
  return (legacyLanes.get(keyOf(encounterId, actorId))?.busy ?? 0) > 0 || legacyWriteUnconfirmed(encounterId, actorId);
}
/** A timed-out legacy move of this actor whose write has not shown up yet. Clears (and forgets the marker) once the row revision
 * rises past the one it was sent from, or at expiry. */
function legacyWriteUnconfirmed(encounterId: string, actorId: string): boolean {
  const key = keyOf(encounterId, actorId);
  const unconfirmed = unconfirmedLegacy.get(key);
  if (!unconfirmed) return false;
  if (Date.now() - unconfirmed.since >= LEGACY_UNCONFIRMED_EXPIRY_MS
    || (unconfirmed.revBefore !== null && (rowRevOf(encounterId, actorId) ?? -1) > unconfirmed.revBefore)) {
    unconfirmedLegacy.delete(key);
    return false;
  }
  return true;
}
/** Run ONE legacy move request for an actor through its lane. `send` performs the request; `wrote` says whether the
 * answer means the server may have written (success, or a mid-path refusal away from the origin) — a pre-walk refusal
 * wrote nothing and releases the lane at once. Rejects with MovementRefused (before sending) when an earlier
 * client-truth delivery of this actor is stuck on a network failure. */
export function runActorLegacyMove<T>(encounterId: string, actorId: string, send: () => Promise<T>, wrote: (result: T) => boolean): Promise<{ result: T; superseded: boolean }> {
  const key = keyOf(encounterId, actorId);
  let lane = legacyLanes.get(key);
  if (!lane) { lane = { tail: Promise.resolve(), busy: 0, issued: 0 }; legacyLanes.set(key, lane); }
  const own = lane;
  const sequence = ++own.issued;
  own.busy += 1;
  const answered = own.tail.then(async () => {
    const idle = await waitFor(() => {
      const outbox = outboxes.get(key);
      if (!outbox || outbox.pending() === 0) return true;
      return outbox.blocked() ? 'fail' : false;
    }, LEGACY_IDLE_WAIT_MS);
    if (!idle) throw new MovementRefused(LEGACY_MOVE_BLOCKED_MESSAGE);
    // An EARLIER legacy move whose answer timed out may still be writing: this request would walk from the server's pre-write row
    // and race it (BUG LEDGER 09-29 residual). Wait for that write to show up (or the marker to expire); refuse, unsent, if it doesn't.
    const confirmed = await waitFor(() => !legacyWriteUnconfirmed(encounterId, actorId), LEGACY_IDLE_WAIT_MS);
    if (!confirmed) throw new MovementRefused(LEGACY_MOVE_UNCONFIRMED_MESSAGE);
    const revBefore = rowRevOf(encounterId, actorId);
    // The request is the CALLER's (a participant's is the scene iframe's own fetch): an iframe that reloads mid-request
    // never settles it, and a hung PHP request never answers. A parent-side timer bounds the lane either way; the
    // caller sees an unknown verdict and takes its transport-failure path.
    let timer = 0;
    // No answer (timeout) or no readable answer (transport failure): the server may still write. Only a later row
    // revision past revBefore — seen in waitForActorMovement — or expiry clears the marker; another move's answer
    // proves nothing about this one (Codex review 2026-09-28).
    const markUnconfirmed = (): void => {
      const existing = unconfirmedLegacy.get(key);
      unconfirmedLegacy.set(key, { revBefore: existing ? existing.revBefore : revBefore, since: Date.now() });
    };
    const result = await Promise.race([
      send(),
      new Promise<never>((_, reject) => { timer = window.setTimeout(() => {
        reject(new Error('The move could not be confirmed (no answer).'));
      }, LEGACY_SEND_TIMEOUT_MS); }),
    ]).catch((error: unknown) => {
      if (!(error instanceof MovementRefused)) markUnconfirmed();
      throw error;
    }).finally(() => window.clearTimeout(timer));
    return { result, revBefore };
  });
  own.tail = answered.then(async ({ result, revBefore }) => {
    let written = false;
    try { written = wrote(result); } catch { written = true; }
    if (written && revBefore !== null) await waitFor(() => (rowRevOf(encounterId, actorId) ?? -1) > revBefore, LEGACY_ECHO_WAIT_MS);
  }).catch(() => undefined).finally(() => {
    own.busy -= 1;
    if (own.busy === 0 && legacyLanes.get(key) === own) legacyLanes.delete(key);
  });
  return answered.then(({ result }) => ({ result, superseded: sequence < own.issued }));
}

/** Null names an unsupported slice before mutation. MovementRefused supplies a locally decided refusal's text. */
export function tryCommitPlainWalk(input: PlainWalkCommitInput): PlainWalkResult | null | Promise<PlainWalkResult | null> {
  observeTruthIntent('movement', { schema: 'janus.movement.intent.v1', input });
  const moverInput = plainWalkMoverInput(input);
  const refused = (error: unknown): never => {
    healDraggedWalkOrigin(input, moverInput, error);
    throw error;
  };
  try {
    const result = tryCommitMoverWalk(moverInput);
    return deferredWalkPlan(result) ? result.then(walk => draggedWalkResult(input, moverInput, walk), refused)
      : draggedWalkResult(input, moverInput, result);
  } catch (error) { return refused(error); }
}

/** The canonical mover's refusal already heals its token. Heal the passenger
 * the scene is dragging too, before its catch preserves that reconciliation. */
function healDraggedWalkOrigin(input: PlainWalkCommitInput, moverInput: PlainWalkCommitInput, error: unknown): void {
  if (!(error instanceof MovementRefused) || error.reasonCode !== 'movement:route-or-movement-mode:route-origin'
    || input.actorId.toUpperCase() === moverInput.actorId.toUpperCase()) return;
  const dragged = rowFor(input.encounterId, input.actorId);
  if (dragged) dispatchMovementReconcile(input.encounterId, input.actorId, {
    x_pos: Number(posCoord(dragged, 'x_pos') ?? 0), y_pos: Number(posCoord(dragged, 'y_pos') ?? 0),
    z_pos: Number(posCoord(dragged, 'z_pos') ?? 0),
  });
}

/** Resolve authority from the parent's held rows, before permission, turn, economy or consequences. */
function plainWalkMoverInput(input: PlainWalkCommitInput): PlainWalkCommitInput {
  if (input.force || input.heldMove) return input;
  const encounter = encounterFor(input.encounterId), dragged = rowFor(input.encounterId, input.actorId);
  if (!encounter || !dragged) return input;
  liftStaleRow(keyOf(input.encounterId, input.actorId), dragged);
  const drive = inventoryDrivePlan(input.encounterId, input.actorId, input.speed);
  if (drive?.reason) throw new MovementRefused(drive.reason, drive.reasonCode);
  if (drive) input = { ...input, speed: drive.speed, inventoryDrive: { speed: drive.speed, speedFt: drive.speedFt } };
  const links = ftCreatureStatuses(dragged, 'target').map(value => String(ftItemsCol(value, 'item_id') ?? value.status_item_id ?? '').toUpperCase());
  if (!input.pilotDriveFramed && (input.pilotDriveFrame || input.authoredGroundDrop && links.includes(VISUAL_STATUS_EFFECTS.piloting ?? ''))) {
    const read = movementScopeReaders(encounter);
    const vehicle = pilotedInventories(encounter, dragged, read)[0];
    if (!vehicle && input.pilotDriveFrame) throw new MovementRefused('Movement refused: compound:mover-changed. Try driving again. Nothing was moved.');
    if (vehicle) {
      if (input.pilotDriveFrame && input.pilotDriveFrame.inventoryId.toUpperCase() !== String(ftInventoryField(vehicle, 'inventory_id') ?? '').toUpperCase())
        throw new MovementRefused('Movement refused: compound:mover-changed. Try driving again. Nothing was moved.');
      // Undo the scene's advisory operator frame, then apply this page's current held offset.
      const offset = input.pilotDriveFrame?.offset;
      const groundInput = offset ? { ...input, ...moverDragFrame(input,
        { position: { x_pos: offset.x, y_pos: offset.y, z_pos: offset.z } }, { position: {} }, read) } : input;
      const [x, y, z] = read.position(vehicle);
      const drive = { ...groundInput, route: groundInput.route.map((point, i) => i === 0 ? [x, y, z] : point) };
      return { ...input, ...moverDragFrame(drive, vehicle, dragged, read), pilotDriveFramed: true };
    }
  }
  // Most walks have no passenger link; do not construct scope readers or scan the roster for them.
  if (!links.some(id => id === VISUAL_STATUS_EFFECTS.riding || id === VISUAL_STATUS_EFFECTS.piloted)) return input;
  const readers = movementScopeReaders(encounter);
  const mover = moverOfRecord(encounter, dragged as unknown as Row, readers);
  if (mover.id === input.actorId.toUpperCase()) return input;
  if (input.speed && !['speed_walking', 'speed_flying', 'speed_jumping'].includes(input.speed))
    throw new MovementRefused('Movement refused: passenger-movement-mode. Choose an ordinary walk.');
  if (pendingFor(input.encounterId, input.actorId)) throw new MovementRefused(COMPOUND_PENDING_MESSAGE);
  const row = rowFor(input.encounterId, mover.id);
  if (!row) throw new MovementRefused('Movement refused: compound:mover-not-held. Nothing was moved.');
  liftStaleRow(keyOf(input.encounterId, mover.id), row);
  return { ...input, ...moverDragFrame(input, dragged as unknown as Row, row as unknown as Row, readers), actorId: mover.id,
    speed: movementSpeedVariable(input.encounterId, mover.id) };
}

function movementSpeedVariable(encounterId: string, actorId: string): string {
  return moverSpeedVariable(ftEncounterCreatureField(rowFor(encounterId, actorId)?.encounter_creatures, 'preferred_speed_type'));
}

/** The outbox owns the mover's result; the scene needs the dragged follower's landing and path. */
function draggedWalkResult(input: PlainWalkCommitInput, moverInput: PlainWalkCommitInput, walk: PlainWalkResult | null): PlainWalkResult | null {
  if (!walk || input.actorId.toUpperCase() === moverInput.actorId.toUpperCase()) return walk;
  const member = walk.compound?.members.find(m => m.id.toUpperCase() === input.actorId.toUpperCase());
  if (!member) throw new MovementRefused('Movement refused: compound:passenger-not-planned.');
  const { before: _moverBefore, ...passengerWalk } = walk;
  return { ...passengerWalk, actor_id: input.actorId, origin: member.before, destination: member.after,
    route: carriedRoute(walk.route, walk.origin, member).map(p => [p[0]!, p[1]!, member.after.z]),
    updates: { x_pos: member.after.x, y_pos: member.after.y, z_pos: member.after.z },
    ...(walk.truncated ? { truncated: { ...walk.truncated, requested: input.destination } } : {}) };
}

const waitingWalkPlans = new Set<string>();
/** Saved state is fenced by the page's wire/base revisions. Also retain the small
 * movement cells that optimistic local actions can change before their save returns. */
function walkPlanStamp(encounterId: string): string {
  const encounter = encounterFor(encounterId);
  const roster = Array.isArray(encounter?.encounter_creatures_arr) ? encounter.encounter_creatures_arr as Row[] : [];
  return JSON.stringify([
    window.ftEncounterMutationGeneration?.(encounterId) ?? 0, window.ftBaseHeardRevision?.(encounterId) ?? 0,
    ['in_combat', 'active_combat_id', 'current_encounter_creature_id_turn'].map(key => encounterField(encounter, key) ?? ''),
    roster.map(member => [encounterCreatureField(member.encounter_creatures, 'encounter_creature_id'),
      ['movement_rev', 'v_current', 'v_temp', 'aps', 'movement_pct', 'encounter_creature_dead']
        .map(key => Number(encounterCreatureField(member.encounter_creatures, key) ?? 0)),
      (['x_pos', 'y_pos', 'z_pos'] as const).map(key => posCoord(member, key) ?? 0)]),
  ]);
}
/** Equipment without an AP cost and floor drags can paint before any saved revision
 * changes. Fence their existing local mutation events for this wait alone. */
function watchWalkPlan(encounterId: string): { current: () => boolean; release: () => void } {
  const before = walkPlanStamp(encounterId), id = encounterId.toUpperCase();
  let localEdit = false;
  const equip = (event: Event): void => {
    const detail = (event as CustomEvent<{ kind: string; frame?: { encounter_id?: string } }>).detail;
    if (detail?.kind === 'provisional' && detail.frame?.encounter_id?.toUpperCase() === id) localEdit = true;
  };
  const floor = (event: Event): void => {
    const detail = (event as CustomEvent<{ envelope?: { encounter_id?: string } }>).detail;
    if (detail?.envelope?.encounter_id?.toUpperCase() === id) localEdit = true;
  };
  window.addEventListener('ft:inventory-equip-truth', equip);
  window.addEventListener('ft:inventory-floor-move-truth-applied', floor);
  return { current: () => !localEdit && walkPlanStamp(encounterId) === before, release: () => {
    window.removeEventListener('ft:inventory-equip-truth', equip);
    window.removeEventListener('ft:inventory-floor-move-truth-applied', floor);
  } };
}
const walkPrefixSaves = new WeakSet<Promise<unknown>>();
/** A committed prefix changes its own encounter while saving. The planner resumes
 * from the accepted saved rows; ordinary input/consent waits keep their change fence. */
export function waitForWalkPrefixSave<T>(save: Promise<T>): Promise<T> { walkPrefixSaves.add(save); return save; }
const walkCommitPlans = new WeakSet<Promise<unknown>>();
/** A commit plan's own wait: the outbox checks the actor's before-state after it, then its continuation installs its own diff. */
export function waitForWalkCommitPlan<T>(plan: Promise<T>): Promise<T> { walkCommitPlans.add(plan); return plan; }
function tryCommitMoverWalk(input: PlainWalkCommitInput, consequencesReady = true): PlainWalkResult | null | Promise<PlainWalkResult | null> {
  const key = input.encounterId.toUpperCase();
  if (waitingWalkPlans.has(key)) throw new MovementRefused('Waiting for a movement reaction answer…');
  const steps = planMoverWalk(input, consequencesReady);
  const advance = (value?: unknown, failed = false): PlainWalkResult | null | Promise<PlainWalkResult | null> => {
    const previousKey = handbackWalkKey;
    let step: IteratorResult<Promise<unknown>, PlainWalkResult | null>;
    try {
      handbackWalkKey = keyOf(input.encounterId, input.actorId);
      step = failed ? steps.throw(value) : steps.next(value);
    } finally { handbackWalkKey = previousKey; }
    if (step.done) return step.value;
    const ownsApply = walkPrefixSaves.delete(step.value) || walkCommitPlans.delete(step.value);
    const watch = ownsApply ? undefined : watchWalkPlan(input.encounterId);
    waitingWalkPlans.add(key);
    try { input.onPending?.('Resolving movement reactions…'); } catch { /* Presentation cannot abandon the suspended action. */ }
    return step.value.then(result => {
      if (watch && !watch.current())
        return advance(new MovementRefused('The encounter changed while the reaction was answered. Nothing was moved.'), true);
      return advance(result);
    }, error => advance(error, true)).finally(() => { watch?.release(); waitingWalkPlans.delete(key); });
  };
  return advance();
}
/** fbroutemode: how far a walk route's first vertex may sit off the row's origin and still be the same start. The scene seats a best-fit
 *  landing's standee on its half-foot grid (≤ 0.354 ft away) while the commit stores the exact landing, so the next drag starts there. */
const ROUTE_ORIGIN_SKEW_FT = 0.5;
/** fbroutemode: which input check a bare route-or-movement-mode hand-back tripped, so the refusal names it. */
function routeModeWhy(route: ReadonlyArray<readonly number[]>, origin: { x: number; y: number; z?: number }, destination?: { x: number; y: number; z: number }): string | undefined {
  if (route.length < 2 || route.length > 512) return `route-length:${route.length}`;
  const skew = route.length ? Math.hypot(route[0]![0]! - origin.x, route[0]![1]! - origin.y) : NaN;
  if (skew > 1e-9) return `route-origin:${skew.toFixed(3)}`;
  if (destination && Math.abs(destination.x - origin.x) < 0.01 && Math.abs(destination.y - origin.y) < 0.01
    && Math.abs(destination.z - (origin.z ?? destination.z)) < 0.01) return 'route-no-displacement';
  return undefined;
}
function* planMoverWalk(input: PlainWalkCommitInput, consequencesReady = false): Generator<Promise<unknown>, PlainWalkResult | null, unknown> {
  pendingFollowerTokens = null; // discard a stopped walk's uncommitted follower plan
  const encounter = encounterFor(input.encounterId), row = rowFor(input.encounterId, input.actorId);
  // R-099 (2026-09-30): a Force Move has no legacy lane — a page that does not hold the rows refuses it
  if (!encounter || !row) {
    if (input.force) throw new MovementRefused(FORCE_MOVE_UNAVAILABLE_MESSAGE);
    return null;
  }
  // 2026-10-07 (held-target-move.ts): a held-creature move is the CASTER's — its permission stands in for the moved row's (a force move only)
  if (input.heldMove && !input.force) throw new MovementRefused('A held creature can only be moved by its holder.');
  const permission = movementPermission(input.encounterId, input.heldMove ? input.heldMove.casterId : input.actorId);
  if (!permission.allowed) throw new MovementRefused(permission.message);
  // A provisional spent reaction is not proof that another mover can safely leave
  // its watcher. Do not resolve a dependent walk until that two-actor write settles.
  if (movementReactionPending(input.encounterId)) throw new MovementRefused('An opportunity reaction is still being saved. Try moving again shortly.');
  // MV4 (Codex S1 review F2): a zone strip this actor's last walk owes can change the speed this walk is priced from.
  if (zoneExitsUnsettled(input.encounterId, input.actorId)) throw new MovementRefused(ZONE_EXIT_PENDING_MESSAGE);
  // MV2 S2: an entry this actor's last walk made is still being resolved / saved.
  if (zoneEntryPlanner?.pending(input.encounterId, input.actorId)) throw new MovementRefused(ZONE_ENTRY_PENDING_MESSAGE);
  // Session A (Codex 9–10 P1): a fall / move-trigger consequence of this actor's last move is still being saved.
  if (afterWorkHeld(input.encounterId, input.actorId)) throw new MovementRefused(CONSEQUENCE_PENDING_MESSAGE);
  // MV4 (M6a, Codex S4 F3): a compound walk of this actor is still in flight.
  const actorKey = keyOf(input.encounterId, input.actorId);
  // An installer that swapped in an OLDER row object (a page-load snapshot landing after this browser's own commit) left the row behind
  // saved truth; with nothing pending the confirmed snapshot IS the row — lift it before the walk reads its revision and origin (MV4:
  // a walk right after a zone entry was resolved on the stale revision and answered 409).
  liftStaleRow(actorKey, row);
  if ([...compoundOps.values()].includes(actorKey)) throw new MovementRefused(COMPOUND_PENDING_MESSAGE);
  if ([...trapOps.values()].includes(actorKey)) throw new MovementRefused(TRAP_PENDING_MESSAGE);
  if ([...overrunOps.values()].includes(actorKey)) throw new MovementRefused(OVERRUN_PENDING_MESSAGE);
  if ([...stealthOps.values()].some(op => op.encounterId === input.encounterId.toUpperCase())) throw new MovementRefused(STEALTH_PENDING_MESSAGE);
  if ([...climbOps.values()].includes(actorKey)) throw new MovementRefused(CLIMB_PENDING_MESSAGE);
  if (restPending(actorKey)) throw new MovementRefused(REST_PENDING_MESSAGE);
  // A legacy move of this actor is queued or in flight: the row still holds the pre-move origin and revision, so a
  // walk resolved from it now would race that write. Hand it to the legacy lane, which runs it after the one ahead.
  if (legacyMoveBusy(input.encounterId, input.actorId)) {
    // R-099: a Force Move cannot queue behind it on a legacy lane (none remains for it) — refused until the earlier write is confirmed
    if (input.force) {
      window.dispatchEvent(new CustomEvent('ft:force-move-refused', { detail: { reason: 'legacy-move-in-flight', message: LEGACY_MOVE_UNCONFIRMED_MESSAGE } }));
      throw new MovementRefused(LEGACY_MOVE_UNCONFIRMED_MESSAGE);
    }
    window.dispatchEvent(new CustomEvent('ft:movement-slice-unavailable', { detail: { supported: false, reason: 'legacy-move-in-flight', origin: input.origin, destination: input.destination } }));
    return null;
  }
  // Force Move S2 (R-099): a Force Move is not a walk — no route, no pricing, collisions ignored. Its own resolver (the drop is
  // committed as posted, landed by the fall twin); never the walk scope. R-099 REMOVED 2026-09-30: it applies or it refuses.
  if (input.force) {
    if (!forceMoveResolver) throw new MovementRefused(FORCE_MOVE_UNAVAILABLE_MESSAGE);
    return yield* forceMoveResolver(input, consequencesReady);
  }
  // The drag frame is presentation state. In particular, its Z can include
  // scenery relief even at the origin. Read the calculation's starting truth
  // from encounterArr; the route anchor checks still reject stale X/Y paths.
  const origin = { x: ftPosCoord(row, 'x_pos') ?? 0, y: ftPosCoord(row, 'y_pos') ?? 0, z: ftPosCoord(row, 'z_pos') ?? 0 };
  if (input.stair && !input.stairPlan) {
    const planned = prepareStairWalkRoute(input, encounter, row as unknown as Row, movementScopeReaders(encounter));
    if (!planned.ok) throw new MovementRefused(planned.refusal);
    input = { ...input, origin, destination: planned.destination, route: planned.route,
      shape: planned.shape, speed: 'speed_walking', stairPlan: planned.plan };
  }
  if (input.wallClimb) {
    const planned = prepareWallClimbRoute(input, encounter, row as unknown as Row, movementScopeReaders(encounter));
    if (!planned.ok) throw new MovementRefused(planned.refusal);
    const end = planned.plan.path[planned.plan.path.length - 1]!;
    input = { ...input, origin, destination: { x: end[0], y: end[1], z: end[2] },
      route: planned.plan.path, speed: 'speed_walking', wallClimbPlan: planned.plan };
  }
  // Persisted coordinates are DECIMAL(…,3). Commit the same precision to local
  // truth and the wire so the next move's before-state agrees with SQL readback.
  const destination = { x: Number(input.destination.x.toFixed(3)), y: Number(input.destination.y.toFixed(3)), z: Number(input.destination.z.toFixed(3)) };
  const route = input.route.map(point => [...point]);
  if (route.length) route[route.length - 1] = input.stairPlan ? [destination.x, destination.y, destination.z] : [destination.x, destination.y];
  // fbroutemode (owner 2026-10-21): the route starts at the scene's standee, which a best-fit drop leaves on its half-foot snap while the row
  // holds the exact landing; the next drag was handed back (route-or-movement-mode). Within that skew the walk starts where the row says the
  // creature stands. A materially stale route keeps the anchor refusal (named by routeModeWhy).
  const originSkew = route.length >= 2 ? Math.hypot(route[0]![0]! - origin.x, route[0]![1]! - origin.y) : 0;
  if (input.speed === 'speed_walking' && originSkew > 0 && originSkew <= ROUTE_ORIGIN_SKEW_FT) route[0] = [origin.x, origin.y, ...route[0]!.slice(2)];
  input = { ...input, origin, destination, route };
  let scope: ReturnType<typeof resolvePlainWalkScope>;
  const walkInput = input;
  const trapReaders = walkTrapPlanner ? walkTrapPlanner.readers(encounter, row as unknown as Row) : null;
  const rawReaders = movementScopeReaders(encounter);
  const baseReaders: PlainWalkReaders = input.inventoryDrive ? { ...rawReaders,
    value: (subject, key) => subject === row && key === input.inventoryDrive!.speed
      ? input.inventoryDrive!.speedFt : rawReaders.value(subject, key) } : rawReaders;
  // MV3 (R-008 rung `room-clearance`): the per-foot wall proof for a walk that leaves its start room — the scene's shape (input.shape, live
  // door states) for the mover's own collision body, from its stored feet.
  const moverGeometry = baseReaders.geometry(row as unknown as Row);
  let wallClear: PlainWalkReaders['wallClear'];
  try {
    wallClear = moverGeometry && walkWallWorld ? routeWallReader(walkWallWorld(encounter, (input as { shape?: unknown }).shape),
      { ...moverGeometry, baseFt: moverGeometry.rulesDiameter ?? moverGeometry.diameter,
        squeezeSizeFt: squeezeBodyFt(moverGeometry.diameter, moverMovementInt(row as unknown as Row, baseReaders, 'squeeze_size_category_offset')),
        mesh: moverGeometry.rulesDiameter !== undefined }, origin) ?? undefined : undefined;
  } catch { wallClear = undefined; }
  // MV5 (R-008 rung `creature-space`): the world the legacy router re-plans a crossing walk in — the same scene shape (live door states) the wall
  // proof reads; an unshaped encounter routes on PHP's bounded open plane.
  const crossingWorld: PlainWalkReaders['crossingWorld'] = () => {
    try { return walkWallWorld?.(encounter, (walkInput as { shape?: unknown }).shape) ?? (encounterIsShapeless(encounter) ? { shape: null, canvasExterior: true, overlayUnknown: false } : null); }
    catch { return null; }
  };
  // MV6: the router's hard set (solid props + corpse support heights) for that world
  const crossingObstacles: PlainWalkReaders['crossingObstacles'] = walkRouteObstacles ? mover => walkRouteObstacles!(encounter, mover) : undefined;
  // MV6 (R-067): a teleport's last landing gate, read on the same wall world (teleport-move.ts — the receiver no longer re-derives the landing)
  const teleportWorld = (() => { try { return walkWallWorld?.(encounter, (walkInput as { shape?: unknown }).shape) ?? null; } catch { return null; } })();
  const landing: Partial<TeleportReaders> = teleportLandingClear && teleportWorld ? { landingClear: teleportLandingClear(encounter, row as unknown as Row, teleportWorld) } : {};
  const stopPlacement = walkStopPlacement && teleportWorld ? { walkStopPlacement: walkStopPlacement(encounter, row as unknown as Row, teleportWorld, origin) } : {};
  const propFootprint: PlainWalkReaders['propFootprint'] = walkPropFootprint ? prop => walkPropFootprint!(encounter, prop) : undefined;
  const readers: PlainWalkReaders = { ...baseReaders, ...landing, ...stopPlacement, ...(wallClear ? { wallClear } : {}), crossingWorld, ...(crossingObstacles ? { crossingObstacles } : {}), ...(propFootprint ? { propFootprint } : {}),
    ...walkZoneReaders(walkInput.encounterId, walkInput.actorId),
    compoundWalk: true,
    ...(climbWalkPlanner ? { climbWalk: true } : {}),
    ...(overrunWalkPlanner ? { overrunWalk: true } : {}), ...(walkMoveTriggerPlanner ? { moveTriggerWalk: true } : {}), ...(stealthWalkPlanner ? { stealthWalk: true } : {}),    ...(trapReaders ? { trapWalk: (route: ReadonlyArray<readonly number[]>) => resolveWalkTrap(Array.isArray(encounter.encounter_inventories_arr) ? encounter.encounter_inventories_arr as Row[] : [],
      legacyStepPositions(route), trapReaders) } : {}) };
  // Placement precedes consent: every watcher judges the snapped route. Player-controlled Opportunity Attacks are
  // prompt-then-resolve (owner 2026-10-02); a decline or 5 s silence counts as resolved for the scope.
  const snapped = shapelessSnapLanding(input, encounter, row as unknown as Row, readers);
  if (snapped?.supported) {
    const { supported: _supported, ...landing } = snapped;
    Object.assign(destination, snapped.destination);
    input = { ...input, ...landing, destination };
  }
  const consent = (input as WalkConsentInput).triggerConsent;
  // Consent, the walk and any OA re-check share the crossing router's route and its prefixes.
  try {
    const replanned = crossingWalkRoute(input, encounter, row as unknown as Row, readers);
    if (replanned) input = { ...input, route: replanned, crossingReplanned: true };
  } catch { /* The scope below names an unavailable crossing plan. */ }
  const leaving = walkConsentAsker ? opportunityLeaveWatchers(input.actorId, origin, input.route, encounter, row as unknown as Row, baseReaders) : [];
  const unanswered = leaving.filter(id => {
    const watcher = rowFor(input.encounterId, id);
    return !!watcher && walkConsentAsker!.needsAsk(input.encounterId, watcher as unknown as Row) && consent?.answer(id, OPPORTUNITY_ATTACK_ID) === undefined;
  });
  const declined = new Set([...input.reachReactionsHandled ?? [], ...leaving.filter(id => consent?.answer(id, OPPORTUNITY_ATTACK_ID) === false)]);
  // MV5 (coordinator Round 23 P2): `WATCHER@ASK` — the relay ask the decline answered (PHP binds it to the controller's recorded answer)
  if (declined.size) lastOaDeclined.set(keyOf(input.encounterId, input.actorId), { ids: [...declined].map(id => {
    const ask = consent ? walkConsentAsker?.askIdFor?.(consent, id) : undefined;
    return ask ? `${id}@${ask}` : id;
  }), at: Date.now() });
  try {
    scope = resolvePlainWalkScope(input, encounter, row as unknown as Row, readers, declined);
    if (!input.wallClimbPlan && !scope.supported && !scope.refused && scope.reason === 'reach'
      && scope.detail?.leave === 'opportunity-attack' && (movementReactionResolver || movementReactionPlanner)) {
      // Finish occupancy and budget before consent or dice. An initial overlap can be
      // walked out of without a contest; the OA frame cannot execute a real Overrun.
      const admission = resolvePlainWalkScope(input, encounter, row as unknown as Row, readers,
        new Set([...declined, ...leaving, String(scope.detail.watcher ?? '').toUpperCase()]));
      if (!admission.supported) scope = admission;
      else if (admission.walk.overrun) scope = { supported: false, reason: 'creature-space', detail: { why: 'overrun-with-opportunity-attack' } };
    }
  }
  catch (error) { window.dispatchEvent(new CustomEvent('ft:movement-slice-unavailable', { detail: { reason: String(error) } })); return null; }
  // An unknown scope may be a cold listener definition. Preserve its full preload
  // before deciding that the rule is unsupported, once, and before any real draw.
  if (!consequencesReady && !scope.supported && !scope.refused) {
    throw new MovementInputsNeeded();
  }
  if (!scope.supported) {
    if (scope.refused) {
      window.dispatchEvent(new CustomEvent('ft:movement-refused-locally', { detail: { message: scope.message, actorId: input.actorId, origin: input.origin, destination: input.destination } }));
      throw new MovementRefused(scope.message);
    }
    if (scope.reachReaction && movementReactionPlanner) {
      const { wallClear, crossingWorld, crossingObstacles, propFootprint, overrunWalk } = readers;
      return yield* movementReactionPlanner(input, encounter, row, scope.reachReaction.watcher, undefined,
        { wallClear, crossingWorld, crossingObstacles, propFootprint, overrunWalk, reachReaction: scope.reachReaction,
          resume: next => planMoverWalk(next, true) });
    }
    if (scope.reason === 'route-or-movement-mode') {
      const why = routeModeWhy(input.route, origin, input.destination) ?? `movement-mode:${String(input.speed ?? 'unspecified')}`;
      const cause = why.startsWith('route-origin:') ? 'route-origin' : why.startsWith('route-length:') ? 'route-length' : why;
      const reasonCode = `movement:route-or-movement-mode:${cause}`;
      const message = `Movement refused: ${why}. ${why === 'movement-mode:speed_swimming' ? 'Choose Walking; swimming is applied automatically in water. ' : ''}Nothing was moved.`;
      window.dispatchEvent(new CustomEvent('ft:movement-refused-locally', { detail: { reason: why, message,
        actorId: input.actorId, origin, destination: input.destination } }));
      if (why.startsWith('route-origin:')) dispatchMovementReconcile(input.encounterId, input.actorId,
        { x_pos: origin.x, y_pos: origin.y, z_pos: origin.z });
      throw new MovementRefused(message, reasonCode);
    }
    if (!input.wallClimbPlan && scope.reason === 'reach' && scope.detail?.leave === 'opportunity-attack' && (movementReactionResolver || movementReactionPlanner)) {
      if (unanswered.length) throw new WalkConsentNeeded(unanswered);
      const { wallClear, crossingWorld, crossingObstacles, propFootprint, overrunWalk } = readers;
      const args: Parameters<MovementReactionResolver> = [input, encounter, row, String(scope.detail.watcher ?? ''), undefined,
        { wallClear, crossingWorld, crossingObstacles, propFootprint, overrunWalk }];
      const reacted = movementReactionPlanner ? yield* movementReactionPlanner(...args)
        : yield* awaitWalkPlan(movementReactionResolver!(...args));
      if (reacted) return reacted;
    }
    window.dispatchEvent(new CustomEvent('ft:movement-slice-unavailable', { detail: { ...scope, origin: input.origin, destination: input.destination } }));
    return null;
  }
  const stealthRelevant = (() => {
    if (stealthWalkPlanner) { try { return stealthWalkPlanner.relevant(encounter, input.actorId, readers); } catch { return true; } }
    return (Array.isArray(encounter.encounter_creatures_arr) ? encounter.encounter_creatures_arr : []).some(r => { try { return status(r as Row, 'hidden'); } catch { return true; } });
  })();
  if (!consequencesReady && (stealthRelevant || scope.walk.climb || scope.walk.wallClimb || scope.walk.overrun
    || scope.walk.trap || scope.walk.move_trigger || scope.walk.zone_entries?.length || scope.walk.zone_exits?.length
    || scope.walk.follower_zone_entries?.length || scope.walk.squeeze || scope.walk.burrow)) {
    throw new MovementInputsNeeded();
  }
  // Ordered halt planners currently cut planar walks. A radial climb must not replay its original full face after such a cut.
  if (scope.walk.wallClimb && (stealthRelevant || scope.walk.zone_entries?.length || scope.walk.zone_exits?.length
    || scope.walk.follower_zone_entries?.length || scope.walk.trap || scope.walk.overrun || scope.walk.move_trigger)) {
    window.dispatchEvent(new CustomEvent('ft:movement-slice-unavailable', { detail: { supported: false,
      reason: 'wall-climb:ordered-consequence', origin: input.origin, destination: input.destination } }));
    return null;
  }
  // MV11: derived climb checks are isolated from ordered consequences BEFORE any planner draws.
  if (scope.walk.climb) {
    const w = scope.walk;
    const back = (reason: string): null => {
      window.dispatchEvent(new CustomEvent('ft:movement-slice-unavailable', { detail: { supported: false, reason: 'climb',
        detail: { climb: reason }, origin: input.origin, destination: input.destination } }));
      return null;
    };
    if (stealthRelevant || w.zone_entries?.length || w.zone_exits?.length || w.follower_zone_entries?.length
      || w.trap || w.overrun || w.move_trigger || w.compound || w.squeeze || w.teleport || w.burrow)
      return back('climb:with-ordered-consequence');
    if (!climbWalkPlanner) return back('climb:planner-not-registered');
    const faces = w.climb!.faces;
    const planned = climbWalkPlanner.plan(input.encounterId, input.actorId, faces.map(({ key, step, dc }) => ({ key, step, dc })));
    if (planned.kind === 'handback') {
      if (planned.reason.startsWith('climb:real-draw-')) throw new MovementRefused('Movement refused after the climb check. Nothing was moved.');
      return back(planned.reason);
    }
    try {
      const settled = planned.failed === null ? w : faces[planned.failed]!.failure;
      const { climb: _climb, ...walk } = settled;
      input = { ...input, destination: { ...walk.destination }, route: walk.route };
      if (planned.failed !== null && walk.truncated)
        window.dispatchEvent(new CustomEvent('ft:movement-warning', { detail: { message: walk.truncated.message } }));
      return yield* awaitWalkPlan(commitResolvedPlainWalk(input, row, walk, undefined, undefined, undefined, undefined, undefined, planned.evidence));
    } catch { throw new MovementRefused('Movement refused after the climb check. Nothing was moved.'); }
  }
  // MV4 (R-008 rung `shapeless`): an unshaped encounter's walk settles on the canvas plane (z = 0, as PHP lands it) — every later step (zone
  // entries' final feet, a halt's re-scope) reads the walk's own landing height, never the scene's posted presentation z.
  if (encounterIsShapeless(encounter) && !scope.walk.traversal) destination.z = scope.walk.destination.z;  // D-051 (Codex review #2 F5, #3 F1 / F3): every hand-back the mover's and each carried follower's entries can make is decided on synthetic
  // dice BEFORE any plan draws a real die — as ONE chain (each follower on the rows the mover's and the earlier followers' entries leave under
  // the same pattern), and again for every cut a dry-run HALT can make (the walk then ends a foot short and a follower's carried route
  // changes: a rider offset from its mount crosses other ground). What remains is the real-draw residual every plan already accepts.
  if (zoneEntryPlanner && (scope.walk.zone_entries?.length || scope.walk.follower_zone_entries?.length)) {
    const planner = zoneEntryPlanner;
    const memberIds = new Set((scope.walk.compound?.members ?? []).map(m => m.id.toUpperCase()));
    const followerZoneRows = compoundFollowerZoneRows(input.encounterId, memberIds);
    const chainOf = (walk: PlainWalkResult, moverEntries: NonNullable<PlainWalkResult['zone_entries']>, moverFinal: { x: number; y: number; z: number }) =>
      walkEntrants(input.actorId, walk, moverEntries, moverFinal, memberIds, followerZoneRows);
    const back = (reason: string, follower?: string): null => {
      window.dispatchEvent(new CustomEvent('ft:movement-slice-unavailable', { detail: { supported: false, reason: 'persistent-area',
        detail: { zones: reason, ...(follower ? { follower } : {}) }, origin: input.origin, destination: input.destination } }));
      return null;
    };
    const full = chainOf(scope.walk, scope.walk.zone_entries ?? [], destination);
    if ('reason' in full) return back(full.reason, full.follower);
    const records = planner.prepare?.(input.encounterId, input.actorId, full.entrants.flatMap(entrant => entrant.entries));
    if (records) throw new ZoneRecordsNeeded(records);
    const proven = planner.preflight(input.encounterId, full.entrants);
    if (proven.reason) return back(proven.reason);
    // (a carried aura's covered creatures too: a cut walk covers fewer of them)
    if ((memberIds.size || scope.walk.follower_zone_entries?.some(f => f.at)) && scope.walk.zone_entries?.length) for (const step of proven.haltSteps) {
      // a halt on the FIRST foot leaves no walk to carry anyone (the plan would hand it back after its draw) — decided here instead
      if (step < 2) return back('zone-entry:halt-on-first-foot');
      const cut = zoneHaltCut(scope.walk, step, input.route);
      if (!cut) return back('zone-entry:halt-rescope');
      const shortened = { ...input, destination: cut.destination, route: cut.route };
      let rescoped;
      try { rescoped = resolvePlainWalkScope(shortened, encounter, row as unknown as Row, readers, declined); } catch { rescoped = null; }
      if (!rescoped?.supported) return back('zone-entry:halt-rescope');
      const cutChain = chainOf(rescoped.walk, scope.walk.zone_entries.filter(e => e.step <= step), shortened.destination);
      if ('reason' in cutChain) return back(cutChain.reason, cutChain.follower);
      const cutProof = planner.preflight(input.encounterId, cutChain.entrants);
      if (cutProof.reason) return back(cutProof.reason);
    }
  }
  // MV10 (2026-10-08), Round 34 P2: preflight every possible stealth halt cut before live dice; reuse its scope after a spot.
  // Fresh contests compose with dice-free passes or a seeded trap; every residual post-draw failure is a local refusal.
  let stealthEvidence: MovementTruthCommand['stealth'] | undefined;
  let stealthMessage: string | null = null;
  const refuseAfterStealthDraw = (): void => {
    if (stealthEvidence?.contests.length) throw new MovementRefused('Movement refused after the stealth check. Nothing was moved.');
  };
  try {
  if (stealthRelevant) {
    const back = (reason: string): null => {
      refuseAfterStealthDraw();
      window.dispatchEvent(new CustomEvent('ft:movement-slice-unavailable', { detail: { supported: false, reason: 'stealth', detail: { stealth: reason }, origin: input.origin, destination: input.destination } }));
      return null;
    };
    const pair = (input as PlainWalkCommitInput).stealthPair;
    if (!stealthWalkPlanner || !pair) return back(!stealthWalkPlanner ? 'stealth:planner-not-registered' : 'stealth:no-scene-facts');
    const w = scope.walk;
    const feet = stealthWalkFeet(w, input.route);
    const halts = new Map<number, { input: typeof input; scope: Extract<ReturnType<typeof resolvePlainWalkScope>, { supported: true }> }>();
    const preflightHalt: NonNullable<Parameters<StealthWalkPlanner['plan']>[0]['preflightHalt']> = (step, possible) => {
      if (step < 2) return null; // stay put: no cut scope or walked consequences
      if (!halts.has(step)) {
        const stop = feet[step - 1];
        if (!stop) return 'stealth:halt-stop';
        const shortened = { ...input, destination: { x: Number(stop[0].toFixed(3)), y: Number(stop[1].toFixed(3)), z: w.destination.z },
          route: cutRouteAt(input.route, stop, step - 1), crossingReplanned: true };
        let rescoped;
        try { rescoped = resolvePlainWalkScope(shortened, encounter, row as unknown as Row, readers, declined); } catch { rescoped = null; }
        if (!rescoped?.supported) return 'stealth:halt-rescope';
        halts.set(step, { input: shortened, scope: rescoped });
      }
      const cut = halts.get(step)!.scope.walk;
      const reason = stealthCompositionReason(classifyStealthConsequences(cut), possible.touched, possible.freshPossible, possible.moverPcPossible);
      if (reason) return reason;
      // A halt precedes the original trap foot; any trap on this cut has not had that cut's readiness preflight.
      return cut.trap ? 'walk-trap:rescope' : null;
    };
    const preflightTrap = w.trap ? (seed: NonNullable<MovementTruthCommand['stealth']>['verdict']['ec']): string | null => {
      const trap = w.trap!;
      if (!walkTrapPlanner) return 'walk-trap:planner-not-registered';
      const stop = feet[trap.step - 1];
      if (!stop) return 'walk-trap:stop';
      const shortened = { ...input, destination: { x: Number(stop[0].toFixed(3)), y: Number(stop[1].toFixed(3)), z: w.destination.z },
        route: cutRouteAt(input.route, stop, trap.step - 1) };
      let rescoped;
      try { rescoped = resolvePlainWalkScope(shortened, encounter, row as unknown as Row, readers, declined); } catch { rescoped = null; }
      if (!rescoped?.supported || rescoped.walk.trap || rescoped.walk.zone_entries?.length) return 'walk-trap:rescope';
      const proof = walkTrapPlanner.preflight?.(input.encounterId, input.actorId, trap.hostId, trap.abilityId, seed);
      if (!proof) return 'walk-trap:preflight-not-registered';
      return proof.kind === 'handback' ? proof.reason : null;
    } : undefined;
    const planned = stealthWalkPlanner.plan({ encounterId: input.encounterId, actorId: input.actorId, feet,
      z: w.origin.z, pair, read: readers, consequences: classifyStealthConsequences(w), preflightTrap, preflightHalt });
    if (planned.kind === 'handback') return back(planned.reason);
    if (planned.kind === 'resolved') {
      stealthEvidence = planned.evidence;
      stealthMessage = planned.message;
      if (planned.haltStep !== null) {
        if (planned.haltStep < 2) {
          // the first foot is never taken: the mover stays put, the walk carries only the hook's writes (bank and AP unchanged)
          const at = [w.origin.x, w.origin.y, w.origin.z] as [number, number, number];
          const { move_trigger: _unowed, trap: _trap, overrun: _overrun, zone_entries: _entries, zone_exits: _exits,
            follower_zone_entries: _followers, compound: _compound, squeeze: _squeeze, truncated: _truncated, snapped: _snapped, ...still } = w;
          scope = { supported: true, walk: { ...still, destination: { ...w.origin }, route: [at, [...at] as [number, number, number]], speed_cost: 0,
            updates: { ...w.updates, x_pos: w.origin.x, y_pos: w.origin.y, z_pos: w.origin.z, ...(w.before ? { movement_pct: w.before.movement_pct, aps: w.before.aps } : {}) } } as PlainWalkResult };
          input = { ...input, destination: { ...w.origin }, route: [[w.origin.x, w.origin.y], [w.origin.x, w.origin.y]] };
        } else {
          const halted = halts.get(planned.haltStep);
          if (!halted) return back('stealth:halt-not-preflighted');
          input = halted.input;
          scope = halted.scope;
        }
      }
      destination.x = scope.walk.destination.x; destination.y = scope.walk.destination.y; destination.z = scope.walk.destination.z;
    }
  }
  // MV8 (R-008 `move-trigger`): the mover's triggers_on_move tail (final_creature_move L661–683: ONCE, at the landing, when the planar
  // displacement exceeds 0.01 ft) — planned NOW on the final landing (every earlier cut is applied), committed once the walk is accepted
  // (queueAfterAccepted holds the mover until it settles). A walk that also carries another ordered consequence hands back.
  let moveTriggerCommit: ((rev: number) => Promise<unknown> | void) | undefined;
  if (scope.walk.move_trigger && Math.hypot(scope.walk.destination.x - scope.walk.origin.x, scope.walk.destination.y - scope.walk.origin.y) > 0.01) {
    const w = scope.walk;
    const back = (reason: string): null => {
      refuseAfterStealthDraw();
      window.dispatchEvent(new CustomEvent('ft:movement-slice-unavailable', { detail: { supported: false, reason: 'move-trigger', detail: { trigger: reason }, origin: input.origin, destination: input.destination } }));
      return null;
    };
    if (!walkMoveTriggerPlanner) return back('move-trigger:planner-not-registered');
    if (w.zone_entries?.length || w.zone_exits?.length || w.follower_zone_entries?.length || w.trap || w.overrun?.triggers.length || w.compound || w.squeeze || w.traversal || w.teleport || stealthEvidence)
      return back('move-trigger:with-ordered-consequence');
    const planned = walkMoveTriggerPlanner.plan(input.encounterId, input.actorId, { x: w.destination.x, y: w.destination.y, z: w.destination.z });
    if (planned.kind === 'handback') return back(planned.reason);
    if (planned.kind === 'planned') moveTriggerCommit = planned.commit;
  }
  // A trap / an overrun cuts the walk AFTER the entry plans: a follower or carried-aura entrant planned on the full route would then be wrong —
  // refused by name here, before any plan draws a real die (the later guards stay as backstops).
  if (scope.walk.follower_zone_entries?.length && (scope.walk.trap || scope.walk.overrun?.triggers.length)) {
    refuseAfterStealthDraw();
    window.dispatchEvent(new CustomEvent('ft:movement-slice-unavailable', { detail: { supported: false, reason: 'persistent-area',
      detail: { zones: 'zone-entry:follower-entries-with-trap-or-overrun' }, origin: input.origin, destination: input.destination } }));
    return null;
  }
  let entryToken: unknown;
  if (scope.walk.zone_entries?.length) {
    // MV2 S2: every entry hand-back is decided HERE, before the walk commits.
    // The mover resolves first; the shared chain adopts every follower's saved landing revision before persisting these entries.
    const followerIds = new Set((scope.walk.compound?.members ?? []).map(m => m.id.toUpperCase()));
    // Codex step-1 review F2: a follower holding a zone-connected row can lose it to a halt's strips at the cut stop — the planner refuses such a
    // halt (a skipped halt resumes the walk, and followers never re-enter).
    const followerZoneRows = [...followerIds].some(id => {
      const member = rowFor(input.encounterId, id);
      return !!member && ftCreatureStatuses(member, 'target').some(s => String(s.status_connected_id ?? '') !== '' && !linkStatus(s as Row));
    });
    const planned = zoneEntryPlanner ? yield* awaitWalkPlan(zoneEntryPlanner.plan(input.encounterId, input.actorId, scope.walk.zone_entries, { x: destination.x, y: destination.y, z: destination.z },
        scope.walk.zone_exits ?? [], followerIds.size ? { followerZoneRows } : undefined))
      : { kind: 'handback' as const, reason: 'zone-entry:planner-not-registered' };
    if (planned.kind === 'handback') {
      refuseAfterStealthDraw();
      window.dispatchEvent(new CustomEvent('ft:movement-slice-unavailable', { detail: { supported: false, reason: 'persistent-area', detail: { zones: planned.reason },
        origin: input.origin, destination: input.destination } }));
      return null;
    }
    entryToken = planned.token;
    if (planned.haltStep !== undefined) {
      // A HALTING entry: the legacy loop restores the step — the walk ends on the foot BEFORE the entry's (mv4_zone_halt_oracle H1 / H3).
      // Re-scope the walk cut there (its price, its own zone crossings and end-of-move strips), keep the entries up to the halting foot.
      if (planned.haltStep < 2) {
        refuseAfterStealthDraw();
        window.dispatchEvent(new CustomEvent('ft:movement-slice-unavailable', { detail: { supported: false, reason: 'persistent-area', detail: { zones: 'zone-entry:halt-on-first-foot' } } }));
        return null;
      }
      const cut = zoneHaltCut(scope.walk, planned.haltStep, input.route);
      if (!cut) {
        refuseAfterStealthDraw();
        window.dispatchEvent(new CustomEvent('ft:movement-slice-unavailable', { detail: { supported: false, reason: 'persistent-area', detail: { zones: 'zone-entry:halt-rescope' } } }));
        return null;
      }
      const resumeStop = cut.destination;
      const shortened = { ...input, destination: { ...resumeStop }, route: cut.route };
      let rescoped;
      try { rescoped = resolvePlainWalkScope(shortened, encounter, row as unknown as Row, readers, declined); } catch { rescoped = null; }
      if (!rescoped?.supported) {
        refuseAfterStealthDraw();
        window.dispatchEvent(new CustomEvent('ft:movement-slice-unavailable', { detail: { supported: false, reason: 'persistent-area', detail: { zones: 'zone-entry:halt-rescope' } } }));
        return null;
      }
      // If the planned halt never lands, continue from the saved stop; shared records suppress already-resolved entries.
      const full = input;
      const resume = (): PlainWalkResult | null | Promise<PlainWalkResult | null> => {
        const rest = cut.rest.map(p => [...p]);
        rest[0] = rest[0]!.length > 2 ? [resumeStop.x, resumeStop.y, resumeStop.z] : [resumeStop.x, resumeStop.y];
        return tryCommitPlainWalkRecorded({ ...full, origin: { ...resumeStop }, route: rest });
      };
      zoneEntryPlanner!.truncate(entryToken, planned.haltStep, rescoped.walk.zone_exits ?? [], resume);
      input = shortened;
      scope = rescoped;
      stealthEvidence = stealthEvidenceAfterHalt(stealthEvidence, planned.haltStep);
    }
    // The strips ride the entry plan (committed after the entries), never the walk's own post-move exit commit.
    scope = { ...scope, walk: { ...scope.walk, zone_exits: undefined } };
  }
  // D-051 (owner 2026-09-30): the carried followers' own zone entries — planned (every hand-back decided before the walk commits), committed
  // after the walk is accepted, each at the follower's saved landing and revision. A follower is carried, never walking: an entry's halt does
  // not cut anything (the follower keeps its landing; the halting status stands).
  const followerTokens: FollowerEntryPlan[] = [];
  // Codex review #3 F1: each follower resolves on the rows the mover's (and the earlier followers') REAL verdicts left, every walk member at
  // its landing
  const landings = new Map<string, { x: number; y: number; z: number }>([[input.actorId.toUpperCase(), { x: input.destination.x, y: input.destination.y, z: input.destination.z }],
    ...(scope.walk.compound?.members ?? []).map(m => [m.id.toUpperCase(), { x: m.after.x, y: m.after.y, z: m.after.z }] as [string, { x: number; y: number; z: number }])]);
  let chainPrior: unknown = entryToken;
  for (const f of scope.walk.follower_zone_entries ?? []) {
    const member = scope.walk.compound?.members.find(m => m.id.toUpperCase() === f.actorId.toUpperCase());
    const final = f.at ?? member?.after; // a creature the carried aura covers: entered where it stands, at its own revision
    const coveredRow = f.at ? rowFor(input.encounterId, f.actorId) : null;
    const planned = !final || (f.at && !coveredRow) ? { kind: 'handback' as const, reason: f.at ? 'zone-entry:covered-not-held' : 'zone-entry:follower-not-in-compound' }
      : zoneEntryPlanner ? yield* awaitWalkPlan(zoneEntryPlanner.plan(input.encounterId, f.actorId, f.entries, { x: final.x, y: final.y, z: final.z }, [], undefined, true, chainPrior, landings))
      : { kind: 'handback' as const, reason: 'zone-entry:planner-not-registered' };
    if (planned.kind === 'handback') {
      if (entryToken !== undefined) zoneEntryPlanner?.drop(entryToken);
      for (const t of followerTokens) zoneEntryPlanner?.drop(t.token);
      refuseAfterStealthDraw();
      window.dispatchEvent(new CustomEvent('ft:movement-slice-unavailable', { detail: { supported: false, reason: 'persistent-area', detail: { zones: planned.reason, follower: f.actorId },
        origin: input.origin, destination: input.destination } }));
      return null;
    }
    followerTokens.push({ id: f.actorId.toUpperCase(), token: planned.token,
      ...(f.at ? { stationary: { rev: Number(scalar(coveredRow!.encounter_creatures.movement_rev) ?? 0), at: { ...f.at } } } : {}) });
    chainPrior = planned.token;
  }
  // Session 3 step 4: an armed trap fires on foot `step` — the legacy loop does not take that step: the walk is cut on the foot before it
  // (re-scoped: its own price), the fire is resolved on TRAPS' resolver BEFORE anything commits, and rides the walk commit.
  let trapEvidence: MovementTruthCommand['trap'] | undefined;
  let overrunEvidence: MovementTruthCommand['overrun'] | undefined;
  if (scope.walk.overrun) {
    const triggers = scope.walk.overrun.triggers;
    const back = (reason: string): null => {
      refuseAfterStealthDraw();
      window.dispatchEvent(new CustomEvent('ft:movement-slice-unavailable', { detail: { supported: false, reason: 'creature-space', detail: { why: 'overrun', overrun: reason }, origin: input.origin, destination: input.destination } }));
      return null;
    };
    if (scope.walk.trap || entryToken !== undefined || followerTokens.length || !overrunWalkPlanner) return back('overrun:with-trap-or-entry-or-no-planner');
    // Admission of EVERY possible stop is pre-draw too. A losing roll must never fall back and get rolled again by legacy PHP.
    const stops = new Map<number, { input: typeof input; scope: typeof scope }>();
    for (const trigger of triggers) {
      const stop = legacyStepPositions(scope.walk.route)[trigger.step - 1];
      if (!stop) return back('overrun:stop');
      const shortened = { ...input, destination: { x: Number(stop[0].toFixed(3)), y: Number(stop[1].toFixed(3)), z: scope.walk.destination.z },
        route: cutRouteAt(scope.walk.route, stop, trigger.step - 1), crossingReplanned: true };
      let rescoped;
      try { rescoped = resolvePlainWalkScope(shortened, encounter, row as unknown as Row, readers, declined); } catch { rescoped = null; }
      if (!rescoped?.supported || rescoped.walk.trap || rescoped.walk.zone_entries?.length || rescoped.walk.zone_exits?.length || rescoped.walk.compound || rescoped.walk.squeeze) return back('overrun:rescope');
      const prefix = rescoped.walk.overrun?.triggers ?? [];
      if (prefix.some((t, i) => t.step !== triggers[i]?.step || JSON.stringify(t.ids) !== JSON.stringify(triggers[i]?.ids))) return back('overrun:rescope-not-prefix');
      stops.set(trigger.step, { input: shortened, scope: rescoped });
    }
    const planned = overrunWalkPlanner.plan(input.encounterId, input.actorId, triggers, readers.inCombat(encounter, row as unknown as Row));
    if (planned.kind === 'handback') return back(planned.reason);
    if (planned.blockedStep !== null) {
      const halted = stops.get(planned.blockedStep);
      if (!halted) return back('overrun:stop-not-planned');
      input = halted.input;
      scope = halted.scope;
      stealthEvidence = stealthEvidenceAfterHalt(stealthEvidence, planned.blockedStep);
    }
    overrunEvidence = planned.evidence;
    if (planned.warning) window.dispatchEvent(new CustomEvent('ft:movement-warning', { detail: { message: planned.warning } }));
  }
  if (scope.walk.trap) {
    const trap = scope.walk.trap;
    const back = (reason: string): null => {
      refuseAfterStealthDraw();
      window.dispatchEvent(new CustomEvent('ft:movement-slice-unavailable', { detail: { supported: false, reason: 'trap-or-hidden-inventory', detail: { trap: reason }, origin: input.origin, destination: input.destination } }));
      return null;
    };
    if (entryToken !== undefined || followerTokens.length || !walkTrapPlanner) return back(walkTrapPlanner ? 'walk-trap:with-zone-entry' : 'walk-trap:planner-not-registered');
    const feet = legacyStepPositions(input.route);
    const stop = feet[trap.step - 1];
    if (!stop) return back('walk-trap:stop');
    const cut = cutRouteAt(input.route, stop, trap.step - 1);
    const shortened = { ...input, destination: { x: Number(stop[0].toFixed(3)), y: Number(stop[1].toFixed(3)), z: destination.z }, route: cut };
    let rescoped;
    try { rescoped = resolvePlainWalkScope(shortened, encounter, row as unknown as Row, readers, declined); } catch { rescoped = null; }
    if (!rescoped?.supported || rescoped.walk.trap || rescoped.walk.zone_entries?.length) return back('walk-trap:rescope');
    const planned = yield* awaitWalkPlan(walkTrapPlanner.plan(input.encounterId, input.actorId, trap.hostId, trap.abilityId, stealthEvidence?.verdict.ec, shortened.destination));
    // MV10 r1: the trap's own live dice were drawn — a residual after them refuses locally (legacy would fire the trap with new dice)
    if (planned.kind === 'handback' && planned.reason.startsWith('walk-trap:real-draw-')) throw new MovementRefused('Movement refused after the trap check. Nothing was moved.');
    if (planned.kind === 'handback') return back(planned.reason);
    trapEvidence = { host_inventory_id: trap.hostId, verb: trap.verb, foot: { x: trap.foot[0], y: trap.foot[1] }, fire: planned.fire };
    input = shortened;
    scope = rescoped;
  }
  if (stealthEvidence && stealthMessage) window.dispatchEvent(new CustomEvent('ft:movement-warning', { detail: { message: stealthMessage } }));
  pendingFollowerTokens = followerTokens.length ? followerTokens : null; // handed over only here: a walk refused above leaves none behind
  return yield* awaitWalkPlan(commitResolvedPlainWalk(input, row, scope.walk, entryToken, trapEvidence, overrunEvidence, stealthEvidence, moveTriggerCommit));
  } catch (error) {
    refuseAfterStealthDraw();
    throw error;
  }
}

// ── The OPPORTUNITY-ATTACK walk lane (attack-truth.ts resolveOpportunityWalk — fix-bug v2-a9 2026-10-15, owner bug "Paladin Guy"): that lane
// resolves its own walk scope and commits the move either plainly or atomically with the swing (its own envelope). These give it the walk's
// zone work exactly as this lane does it: the classifier readers, every entry hand-back on synthetic dice before any draw, the real plans just
// before the commit, and — for the envelope — the exits and entries owed once the move is saved.
/** The zone readers an ordinary walk binds (tryCommitMoverWalk): the classifier, and the follower / carried-aura entrants' classifier. */
export function walkZoneReaders(encounterId: string, actorId: string): Pick<PlainWalkReaders, 'zoneWalk' | 'followerZoneWalk' | 'zoneRadius'> {
  return { zoneRadius: (holder, status) => walkZoneRadius(encounterId, holder, status),
    zoneWalk: (route, moved, options) => resolveWalkZones(encounterId, actorId, route, { entries: zoneEntryPlanner !== null, moved, ...options }),
    ...(zoneEntryPlanner ? { followerZoneWalk: (id: string, route: ReadonlyArray<readonly number[]>, moved?: ReadonlyMap<string, readonly [number, number]>,
      options?: Parameters<NonNullable<PlainWalkReaders['followerZoneWalk']>>[3]) =>
      resolveWalkZones(encounterId, id, route, { entries: true, moved, ...options }) } : {}) };
}
/** A zone-records hydration this lane awaits and retries the walk after (an error a reaction resolver must let through). */
export const isWalkRetryAfterLoad = (error: unknown): boolean => error instanceof ZoneRecordsNeeded;
/** Ordinary entry plans prove their complete chain on synthetic dice. A combined reaction
 * frame owns reached contacts and the actual stop instead; both lanes hydrate records first. */
export function preflightWalkZoneEntries(encounterId: string, actorId: string, walk: PlainWalkResult, reactionFrame = false): string | null {
  if (!walk.zone_entries?.length && !walk.follower_zone_entries?.length) return null;
  if (!zoneEntryPlanner) return 'zone-entry:planner-not-registered';
  const records = zoneEntryPlanner.prepare?.(encounterId, actorId,
    [...walk.zone_entries ?? [], ...(walk.follower_zone_entries ?? []).flatMap(follower => follower.entries)]);
  if (records) throw new ZoneRecordsNeeded(records);
  if (reactionFrame) return null;
  const memberIds = new Set((walk.compound?.members ?? []).map(m => m.id.toUpperCase()));
  const chain = walkEntrants(actorId, walk, walk.zone_entries ?? [], walk.destination, memberIds, compoundFollowerZoneRows(encounterId, memberIds));
  if ('reason' in chain) return chain.reason;
  const proven = zoneEntryPlanner.preflight(encounterId, chain.entrants);
  if (proven.reason) return proven.reason;
  return proven.haltSteps.length && walk.zone_entries?.length ? 'zone-entry:halt-with-reaction' : null;
}
/** A walk's planned zone work: the walk to commit (the mover's strips ride its entry plan), the mover's plan, the entrants' plans. */
export interface WalkZonePlan { walk: PlainWalkResult; entryToken?: unknown; followers: FollowerEntryPlan[] }
/** The REAL plans of the walk's zone entries (after preflightWalkZoneEntries): the mover's, then each entrant's on the rows the earlier left. */
type PlannedWalkZones = { kind: 'planned'; plan: WalkZonePlan } | { kind: 'handback'; reason: string };
export function planWalkZoneEntries(encounterId: string, actorId: string, walk: PlainWalkResult): PlannedWalkZones | Promise<PlannedWalkZones> {
  return resumeWalkPlan(planWalkZones(encounterId, actorId, walk));
}
function* planWalkZones(encounterId: string, actorId: string, walk: PlainWalkResult): Generator<Promise<unknown>, PlannedWalkZones, unknown> {
  const plan: WalkZonePlan = { walk, followers: [] };
  if (!walk.zone_entries?.length && !walk.follower_zone_entries?.length) return { kind: 'planned', plan };
  const planner = zoneEntryPlanner;
  if (!planner) return { kind: 'handback', reason: 'zone-entry:planner-not-registered' };
  const memberIds = new Set((walk.compound?.members ?? []).map(m => m.id.toUpperCase()));
  const final = { x: walk.destination.x, y: walk.destination.y, z: walk.destination.z };
  if (walk.zone_entries?.length) {
    const planned = yield* awaitWalkPlan(planner.plan(encounterId, actorId, walk.zone_entries, final, walk.zone_exits ?? [],
      memberIds.size ? { followerZoneRows: compoundFollowerZoneRows(encounterId, memberIds) } : undefined));
    if (planned.kind === 'handback') return planned;
    if (planned.haltStep !== undefined) { planner.drop(planned.token); return { kind: 'handback', reason: 'zone-entry:halt-with-reaction' }; }
    plan.entryToken = planned.token;
    plan.walk = { ...walk, zone_exits: undefined }; // the strips ride the entry plan (committed after the entries)
  }
  const landings = new Map<string, { x: number; y: number; z: number }>([[actorId.toUpperCase(), final],
    ...(walk.compound?.members ?? []).map(m => [m.id.toUpperCase(), { x: m.after.x, y: m.after.y, z: m.after.z }] as [string, { x: number; y: number; z: number }])]);
  let chainPrior: unknown = plan.entryToken;
  for (const f of walk.follower_zone_entries ?? []) {
    const member = walk.compound?.members.find(m => m.id.toUpperCase() === f.actorId.toUpperCase());
    const at = f.at ?? member?.after;
    const coveredRow = f.at ? rowFor(encounterId, f.actorId) : null;
    let planned: Awaited<ReturnType<ZoneEntryPlanner['plan']>>;
    try {
      planned = !at || (f.at && !coveredRow) ? { kind: 'handback', reason: f.at ? 'zone-entry:covered-not-held' : 'zone-entry:follower-not-in-compound' }
        : yield* awaitWalkPlan(planner.plan(encounterId, f.actorId, f.entries, { x: at.x, y: at.y, z: at.z }, [], undefined, true, chainPrior, landings));
    } catch (error) { dropWalkZonePlan(plan); throw error; }
    if (planned.kind === 'handback') { dropWalkZonePlan(plan); return planned; }
    plan.followers.push({ id: f.actorId.toUpperCase(), token: planned.token,
      ...(f.at ? { stationary: { rev: Number(scalar(coveredRow!.encounter_creatures.movement_rev) ?? 0), at: { ...f.at } } } : {}) });
    chainPrior = planned.token;
  }
  return { kind: 'planned', plan };
}
/** A plan that will not be committed (the reaction stepped aside, the envelope could not be queued). */
export function dropWalkZonePlan(plan: WalkZonePlan): void {
  if (plan.entryToken !== undefined) zoneEntryPlanner?.drop(plan.entryToken);
  for (const f of plan.followers) zoneEntryPlanner?.drop(f.token);
}
/** The reaction lane's PLAIN commit of a planned walk (the watcher declined, or the route never leaves its reach). */
export function commitPlannedPlainWalk(input: PlainWalkCommitInput, row: MovementTruthRow, plan: WalkZonePlan): PlainWalkResult | Promise<PlainWalkResult> {
  pendingFollowerTokens = plan.followers.length ? plan.followers : null;
  return commitResolvedPlainWalk(input, row, plan.walk, plan.entryToken);
}
/** The opportunity-walk ENVELOPE saves the move itself: its zone work rides the envelope's operation id — queued BEFORE the envelope is
 *  enqueued, settled once it is accepted (settleWalkZoneWork, the envelope's saved revision / position), dropped on any other answer
 *  (dropQueuedZoneExits). */
export function queueWalkZoneWork(operationId: string, encounterId: string, actorId: string, plan: WalkZonePlan): void {
  if (plan.walk.zone_exits?.length) queueZoneExits(operationId, encounterId, actorId, plan.walk.zone_exits);
  if (plan.entryToken !== undefined) { pendingZoneEntries.set(operationId, plan.entryToken); zoneEntryPlanner?.hold(plan.entryToken); }
  if (plan.followers.length) {
    pendingFollowerEntries.set(operationId, plan.followers);
    for (const t of plan.followers) zoneEntryPlanner?.hold(t.token);
    if (pendingFollowerEntries.size > 256) pendingFollowerEntries.delete(pendingFollowerEntries.keys().next().value!);
  }
}
export function settleWalkZoneWork(operationId: string, encounterId: string, actorId: string, result: Row): void {
  commitPendingZoneExits(operationId);
  settleZoneEntryChain(operationId, encounterId, actorId, result);
}

/** Used only after the ordinary walk proof, including any owned reaction, has completed. */
export function commitResolvedPlainWalk(input: PlainWalkCommitInput, row: MovementTruthRow, walk: PlainWalkResult, zoneEntryToken?: unknown,
  trapEvidence?: MovementTruthCommand['trap'], overrunEvidence?: MovementTruthCommand['overrun'], stealthEvidence?: MovementTruthCommand['stealth'],
  moveTriggerCommit?: (rev: number) => Promise<unknown> | void, climbEvidence?: MovementTruthCommand['climb']): PlainWalkResult | Promise<PlainWalkResult> {
  const result = resumeWalkPlan(planResolvedPlainWalk(input, row, walk, zoneEntryToken, trapEvidence, overrunEvidence, stealthEvidence, moveTriggerCommit, climbEvidence));
  // The outbox checks the actor's before-state after planning, then this continuation installs its own diff.
  if (deferredWalkPlan(result)) walkCommitPlans.add(result);
  return result;
}
function* planResolvedPlainWalk(input: PlainWalkCommitInput, row: MovementTruthRow, walk: PlainWalkResult, zoneEntryToken?: unknown,
  trapEvidence?: MovementTruthCommand['trap'], overrunEvidence?: MovementTruthCommand['overrun'], stealthEvidence?: MovementTruthCommand['stealth'],
  moveTriggerCommit?: (rev: number) => Promise<unknown> | void, climbEvidence?: MovementTruthCommand['climb']): Generator<Promise<unknown>, PlainWalkResult, unknown> {
  if (walk.climb && !climbEvidence) throw new Error('A derived climb walk needs its face check plan');
  const { climb: _climb, ...settledWalk } = walk;
  walk = settledWalk;
  const scope = { walk };
  // Only tryCommitPlainWalk plans entries; a walk carrying them without a plan would lose them.
  if (walk.zone_entries?.length && zoneEntryToken === undefined) throw new Error('A walk entering a zone needs its entry plan');
  if (walk.overrun?.triggers.length && !overrunEvidence) throw new Error('An overrun walk needs its contest plan');
  // The snapshot revision is banked on first sight (confirmedFor) and every legacy row install since has
  // banked itself (bankLegacyRowTruth), so the row's revision here is confirmed + pending — nothing to infer.
  confirmedFor(keyOf(input.encounterId, input.actorId), row);
  const outbox = actorOutbox(input.encounterId, input.actorId);
  const expectedRev = Number(row.encounter_creatures.movement_rev ?? 0);
  // In combat both scalars ride, always (owner Q1 2026-09-07): the bank the walk was priced from and the AP held.
  const banked = scope.walk.before !== undefined && scope.walk.updates.movement_pct !== undefined && scope.walk.updates.aps !== undefined;
  // The shared request reader relays uppercase UUIDs; own-operation dedup must use that spelling too.
  const command: MovementTruthCommand = { schema: 'janus.movement.truth.v1', operation_id: crypto.randomUUID().toUpperCase(),
    encounter_id: input.encounterId, actor_id: input.actorId, expected_rev: expectedRev,
    before: { x_pos: input.origin.x, y_pos: input.origin.y, z_pos: input.origin.z, ...(banked ? { movement_pct: scope.walk.before!.movement_pct, aps: scope.walk.before!.aps } : {}) },
    updates: scope.walk.updates, route: scope.walk.route,
    ...(scope.walk.teleport ? { teleport: { landing: { ...scope.walk.teleport.landing }, displaced_ft: scope.walk.teleport.displaced_ft, dice: scope.walk.teleport.dice,
      // MV5 (R-067): the statuses the teleport ends, written by the receiver after the commit (the creature's own rows only)
      ...(scope.walk.teleport.clears?.length ? { clears: [...scope.walk.teleport.clears] } : {}) } } : {}),
    ...(scope.walk.squeeze ? { squeeze: { squeezed: scope.walk.squeeze.squeezed, very: scope.walk.squeeze.very } } : {}),
    ...(scope.walk.burrow ? { burrow: { transition: scope.walk.burrow.transition } } : {}),
    ...(scope.walk.compound ? { compound: { members: scope.walk.compound.members.map(m => {
      const member = rowFor(input.encounterId, m.id);
      return { ...(m.inventoryId ? { inventory_id: m.inventoryId } : { id: m.id, expected_rev: Number(ftEncounterCreatureField(member?.encounter_creatures, 'movement_rev') ?? 0) }),
        before: { x_pos: m.before.x, y_pos: m.before.y, z_pos: m.before.z }, after: { x_pos: Number(m.after.x.toFixed(3)), y_pos: Number(m.after.y.toFixed(3)), z_pos: Number(m.after.z.toFixed(3)) } };
    }) } } : {}),
    ...(trapEvidence ? { trap: trapEvidence } : {}), ...(overrunEvidence ? { overrun: overrunEvidence } : {}), ...(stealthEvidence ? { stealth: stealthEvidence } : {}),
    ...(climbEvidence ? { climb: climbEvidence } : {}) };
  if (command.stealth) command.stealth = bindStealthRevealLog(command.stealth, command.operation_id);
  const followerTokens = pendingFollowerTokens;
  pendingFollowerTokens = null;
  prepareMovementDispatchSkips(command, movementDispatchContext(command.encounter_id) ?? undefined);
  const statusInputs = movementStatusInputs20(command);
  if (command.burrow?.transition && command.burrow.transition !== 'none' || command.squeeze || command.rest || command.teleport?.clears?.length || (command.teleport?.dice ?? 0) > 0
    || statusInputs.compound?.members.some(member => member.squeeze || member.hunkered)) {
    try {
      const bridge = window as unknown as { ftPlanMovementStatuses20?: (id: string, input: typeof statusInputs) => Promise<NonNullable<MovementTruthCommand['status_plan']>>;
        ftMovementStatusAppliedFrame20?: (command: MovementTruthCommand) => AppliedFrameResult };
      if (!bridge.ftPlanMovementStatuses20) throw new MovementRefused('Movement status planner is not available.');
      if (!bridge.ftMovementStatusAppliedFrame20) throw new MovementRefused('Movement status projection is not available.');
      command.status_plan = yield* awaitWalkPlan(withAsyncDeadline(() => bridge.ftPlanMovementStatuses20!(command.encounter_id, statusInputs), 20_000));
      if (scope.walk.teleport && scope.walk.teleport.dice > 0) scope.walk.teleport.forceDamage = command.status_plan.rows
        .filter(row => row.roll_item_id === '7A5F5C86-B457-462B-964F-33C5CFAF9BF7' && row.dice?.die === 10)
        .reduce((sum, row) => sum + row.roll_result, 0);
    } catch (error) {
      dropWalkZonePlan({ walk, entryToken: zoneEntryToken, followers: followerTokens ?? [] });
      throw error;
    }
  }
  if (banked) {
    bankedOperations.set(command.operation_id, scope.walk.updates.aps! < scope.walk.before!.aps ? 'ap' : 'bank');
    if (bankedOperations.size > 512) bankedOperations.delete(bankedOperations.keys().next().value!);
  }
  if (trapEvidence) {
    trapOps.set(command.operation_id, keyOf(input.encounterId, input.actorId));
    if (trapOps.size > 256) trapOps.delete(trapOps.keys().next().value!);
  }
  if (stealthEvidence) {
    stealthOps.set(command.operation_id, { key: keyOf(input.encounterId, input.actorId), encounterId: input.encounterId.toUpperCase() });
    if (stealthOps.size > 256) stealthOps.delete(stealthOps.keys().next().value!);
  }
  if (overrunEvidence) {
    overrunOps.set(command.operation_id, keyOf(input.encounterId, input.actorId));
    if (overrunOps.size > 256) overrunOps.delete(overrunOps.keys().next().value!);
  }
  if (climbEvidence) climbOps.set(command.operation_id, keyOf(input.encounterId, input.actorId));
  if (scope.walk.compound?.members.length) {
    compoundOps.set(command.operation_id, keyOf(input.encounterId, input.actorId));
    if (compoundOps.size > 256) compoundOps.delete(compoundOps.keys().next().value!);
  }
  if (scope.walk.zone_exits?.length) {
    pendingZoneExits.set(command.operation_id, { encounterId: input.encounterId, actorId: input.actorId, strips: scope.walk.zone_exits });
    holdZoneExits(keyOf(input.encounterId, input.actorId), scope.walk.zone_exits.map(s => s.statusId), null);
    if (pendingZoneExits.size > 256) dropPendingZoneExits(pendingZoneExits.keys().next().value!);
  }
  if (zoneEntryToken !== undefined) {
    pendingZoneEntries.set(command.operation_id, zoneEntryToken);
    zoneEntryPlanner?.hold(zoneEntryToken);
  }
  if (followerTokens?.length) {
    pendingFollowerEntries.set(command.operation_id, followerTokens);
    for (const t of followerTokens) zoneEntryPlanner?.hold(t.token);
    if (pendingFollowerEntries.size > 256) pendingFollowerEntries.delete(pendingFollowerEntries.keys().next().value!);
  }
  // MV8: the walk's owed triggers_on_move tail — committed once the landing is saved (call BEFORE enqueueing)
  if (moveTriggerCommit) { const commit = moveTriggerCommit; queueAfterAccepted(command.operation_id, { encounterId: input.encounterId, actorId: input.actorId, accepted: rev => commit(rev), dropped: () => undefined }); }
  // Codex Round 32 P3: an outbox that refuses synchronously (capacity) never settles the queued tail — release it, or the mover stays held
  bindActionTiming(walkTimingKey(input), command.operation_id);
  try { outbox.enqueue(command); } catch (error) { climbOps.delete(command.operation_id); dropAfterAccepted(command.operation_id); throw error; }
  seen.add(command.operation_id);
  if (scope.walk.snapped) window.dispatchEvent(new CustomEvent('ft:movement-warning', { detail: scope.walk.snapped }));
  return scope.walk;
}
/** Resolve once, before optimistic position changes; retries retain the same Wet bytes. */
function prepareMovementWet(command: MovementTruthCommand, row: MovementTruthRow): void {
  if (command.wet) return;
  const encounter = encounterFor(command.encounter_id);
  if (!encounter) return;
  const campaignId = String(asRow(encounter.encounters)?.encounter_master_parent_id ?? encounter.encounter_master_parent_id ?? '').toUpperCase();
  const current = asRow((window.FateTide as unknown as { currentCampaignArr?: unknown })?.currentCampaignArr);
  const campaign = asRow(window.FateTide?.campaignCache?.get(campaignId))
    ?? (String(asRow(current?.campaigns)?.campaign_id ?? '').toUpperCase() === campaignId ? current : null);
  if (!campaign) return; // editor-only scenes have no campaign clock
  const readers = movementScopeReaders(encounter), geometry = readers.geometry(row);
  if (!geometry) return;
  const immune = readers.entries(row, 'immune_status_effect').some(entry => String(typeof entry === 'string' ? entry : ftItemsCol(entry, 'item_id') ?? '').toUpperCase() === WATER_WET_ID);
  const projected = own(own(own(row, 'computed'), 'creature'), 'defenses');
  const immuneRows = own(projected, 'immune_status');
  if (immune || (Array.isArray(immuneRows) && immuneRows.some(entry => String(own(entry, 'item_layer_effect') ?? '').toUpperCase() === WATER_WET_ID))) return;
  const derived = asRow(asRow((window as unknown as { ftCampaignClock?: { getDerived?: () => unknown } }).ftCampaignClock?.getDerived?.())?.cols);
  const now = derived ? derived as unknown as CampaignClockCols : ftClockColsFromArr(campaign);
  const change = resolveWaterWet({ encounter, actor: row, actorId: command.actor_id,
    movementReaders: readers,
    route: command.route, height: geometry.height, now, calendar: ftClockCalendarFromArr(campaign),
    teleport: !!command.teleport, newId: () => crypto.randomUUID().toUpperCase() });
  if (change) {
    const definition = readers.definition(WATER_WET_ID);
    if (definition) change.after.status_arr = definition;
    command.wet = change;
  }
}

/** The actor's shared outbox; every callback dispatches on the command's schema. */
export function actorOutbox(encounterId: string, actorId: string): MovementTruthOutbox<ActorTruthCommandBase> {
  const key = keyOf(encounterId, actorId);
  let outbox = outboxes.get(key);
  if (!outbox) {
    outbox = new MovementTruthOutbox<ActorTruthCommandBase>({ encounterId, actorId,
      row: () => { const current = rowFor(encounterId, actorId); if (!current) throw new Error('Movement actor disappeared'); return current; },
      prepare: (command, row) => {
        if (isMovement(command)) {
          prepareMovementDispatchSkips(command, movementDispatchContext(command.encounter_id) ?? undefined);
          prepareMovementWet(command, row);
          pinMovementEffectIds(command);
          if (movementNeedsSavedDelivery(command) && !window.ftPushAppliedTruth)
            throw new MovementRefused('movement:truth-transport-unavailable');
        }
      },
      computedUnchanged: command => isMovement(command) && !command.wet,
      computedMembers: (command, row) => (isMovement(command) ? [row]
        : handlersFor(command).computedRows?.(command) ?? [row]).map(member =>
          [String(ftEncounterCreatureField(member.encounter_creatures, 'encounter_creature_id') ?? command.actor_id), member] as const),
      apply: (row, command, rev) => {
        markActionTiming(command.operation_id, 'apply_ms');
        if (isMovement(command)) {
          if (command.wet) projectWaterWet(row, command.wet);
          applyMovementTruth(row, { ...command.updates, rev });
          if (movementNeedsSavedDelivery(command)) {
            if (command.status_plan) movementStatusPlans20.set(command, Promise.resolve(command.status_plan));
            const encounter = encounterFor(command.encounter_id)!;
            const campaignId = String(encounterField(encounter, 'encounter_master_parent_id') ?? '').toUpperCase();
            const campaign = asRow(window.FateTide?.campaignCache?.get(campaignId));
            const frame = solidMovementAppliedFrame(command, encounter, campaign, id => movementScopeReaders(encounter).definition(id));
            movementStatusFrames20.set(command, frame);
            return;
          }
          if (command.status_plan) {
            const apply = (window as unknown as { ftMovementStatusAppliedFrame20?: (command: MovementTruthCommand) => AppliedFrameResult }).ftMovementStatusAppliedFrame20;
            if (!apply) throw new MovementRefused('Movement status projection is not available.');
            movementStatusPlans20.set(command, Promise.resolve(command.status_plan));
            movementStatusFrames20.set(command, apply(command));
          }
        } else handlersFor(command).apply(row, command, rev);
      },
      render: (command, row) => {
        markActionTiming(command.operation_id, 'paint_ms');
        exposeAppliedComputed(command, outbox?.appliedResult(command.operation_id)?.computed);
        if (isMovement(command)) {
          const statuses = movementStatusFrames20.get(command);
          if (statuses && !('cache_ref' in statuses)) {
            // The endpoint projector owns computed/mirror paths. The outer movement
            // observer declares only Wet's final status lists; merge those fields
            // without replacing the actor's skill/speed delta or its before-values.
            const observed = outbox?.appliedResult(command.operation_id)?.computed;
            const computed = { ...observed, ...statuses.computed_delta };
            if (command.wet) for (const [id, wet] of Object.entries(observed ?? {})) {
              computed[id] = { ...computed[id]!,
                ...('statuses' in wet ? { statuses: wet.statuses } : {}),
                ...('status_projection' in wet ? { status_projection: wet.status_projection } : {}) };
            }
            exposeAppliedComputed(command, computed);
            if (movementNeedsSavedDelivery(command))
              savedMovementFrames.set(command, JSON.stringify({ ...statuses, computed_delta: computed }));
          }
          if (!movementNeedsSavedDelivery(command))
            exposeAppliedFrame(command, () => statuses ?? memberAppliedFrame({ operation_id: command.operation_id, encounter_id: command.encounter_id,
              actor_id: command.actor_id, rev: command.expected_rev + 1, member_revs: { [command.actor_id]: command.expected_rev + 1 } }, [row]));
          render(command, row);
        } else handlersFor(command).render(command, row);
      },
      onRenderError: (error, command) => { if (isMovement(command)) renderFailed(error, command); },
      commit: command => isMovement(command) ? post(command) : handlersFor(command).commit(command),
      accepted: (command, result) => withActionTimingEcho(command,
        () => { if (isMovement(command)) accepted(command, result); else handlersFor(command).accepted(command, result); }),
      rejected: (command, result) => withActionTimingEcho(command,
        () => { if (isMovement(command)) rejected(command, result); else handlersFor(command).rejected(command, result); }),
      failed: (command, info) => withActionTimingEcho(command,
        () => { if (isMovement(command)) failed(command, info); else handlersFor(command).failed(command, info); }),
      changed: report,
      // The queue drained: whatever saved truth merged into the snapshot while our work was pending is rendered now.
      settled: () => {
        const current = rowFor(encounterId, actorId);
        if (!current || !reconcileRow(key, current)) return;
        const merged = confirmedFor(key, current);
        render({ schema: MOVEMENT_SCHEMA, operation_id: 'settled', encounter_id: encounterId, actor_id: actorId, expected_rev: merged.rev, before: merged.position, updates: merged.position, route: [], rev: merged.rev, position: merged.position }, current);
      } });
    outboxes.set(key, outbox);
  }
  return outbox;
}
/** Bank the actor's supplied scalar values without replaying any rule projector. */
export function bankAppliedFrame(frame: AppliedFrame): void {
  for (const [id, rev] of Object.entries(frame.member_revs)) {
    const row = rowFor(frame.encounter_id, id);
    const incoming = frame.rows.find(entry => entry.table === 'encounter_creatures' && entry.row_id.toUpperCase() === id.toUpperCase());
    if (!row || !incoming) continue;
    const fields: ConfirmedFields = {};
    for (const key of ['aps', 'movement_pct'] as const) {
      const value = encounterCreatureField(incoming.row, key);
      if (!incoming.partial || value !== undefined) fields[key] = Number(value ?? 0);
    }
    if (incoming.position) for (const key of ['x_pos', 'y_pos', 'z_pos'] as const) fields[key] = Number(posCoord({ position: incoming.position }, key) ?? 0);
    bankLegacyTruth(frame.encounter_id, id, row, rev, fields, 'frame');
    handleCasterUpdate({ type: 'caster_update', caster_arr: row as unknown as CasterArr, local: true });
  }
}
declare global { interface Window { ftBankAppliedFrame?: typeof bankAppliedFrame } }
window.ftBankAppliedFrame = bankAppliedFrame;
export function noteOwnOperation(operationId: string, schema: string): void { seen.add(operationId); deliverySchema.set(operationId, schema); }
/** An operation this browser owns, or one whose saved outcome (committed / correction) already arrived: a delayed
 * provisional for it must never render again (Codex M1c review A3: a provisional after its correction resurrected the
 * operation). Shared by every schema's subscriber. */
export function operationSettled(operationId: string): boolean {
  return seen.has(operationId) || seen.has(operationId + '#committed') || seen.has(operationId + '#correction');
}
/** Shared dedup for the A1 subscriber (same `seen` set, same kinds). Returns false when the frame was already handled. */
export function noteFrameSeen(operationId: string, kind: 'provisional' | 'committed' | 'correction'): boolean {
  const dedupKey = operationId + '#' + kind;
  if (seen.has(dedupKey) || (kind === 'provisional' && operationSettled(operationId))) return false;
  seen.add(dedupKey);
  if (seen.size > 512) seen.delete(seen.values().next().value!);
  return true;
}
type MovementTruthFrame = MovementTruthCommand & {
  provisional?: boolean; committed?: boolean; correction?: boolean;
  applied_frame?: AppliedFrameResult;
  /** Command markers whose presence excludes ordinary walk playback.
   * held_move, teleport and climb retain their optional MovementTruthCommand types. */
  intent?: unknown;
};
const MAX_PEER_WALK_PLAYBACKS = 256;
/** A terminal can arrive before the preceding scene walk finishes playing. */
const peerWalkPlaybackLiveUntil = new Map<string, number>();
const PEER_WALK_PLAYBACK_LIFETIME_MS = 30_000;
/** Embedded sockets still project row truth, but the movement-commit owner delivers scene playback/verdicts. */
function ownsMovementCommits(): boolean {
  return window.parent === window
    || typeof (window.parent as Window & { ftTryCommitPlainWalk?: unknown })?.ftTryCommitPlainWalk !== 'function';
}

export function dispatchMovementReconcile(encounterId: string, actorId: string, position: MovementTruthChange): void {
  if (!ownsMovementCommits()) return;
  const payload = {
    schema: 'ft.ft3d.move-committed.v1', encounterId, entityId: actorId,
    positionFt: { x: position.x_pos, y: position.y_pos, z: position.z_pos },
  };
  // The campaign shell forwards this event to its scene, independently of debounced row projection.
  window.dispatchEvent(new CustomEvent('ft:ft3d-move-committed', { detail: payload }));
  // Only a standalone scene self-posts; the top-level campaign shell owns its iframe forwarder.
  if (!(window as unknown as { ft3dCampaignMoveCommittedForwarder?: boolean }).ft3dCampaignMoveCommittedForwarder
    && window.parent === window && document.querySelector('[data-ft-encounter-creation-engine]')) {
    window.postMessage({ type: 'ft3d-campaign-move-committed-v1', payload }, window.location.origin);
  }
}
/** Adapt a peer's solo walk to the scene's existing authoritative-commit playback wire (encounter feet). */
export function movementTruthMovePreview(command: MovementTruthFrame, speedVariable: string) {
  const route = command.route;
  if (!Array.isArray(route) || route.length < 2
    || ['intent', 'held_move', 'teleport', 'climb'].some(key => Object.prototype.hasOwnProperty.call(command, key))
    // Creature followers keep peer snaps; inventory publishes already animate vehicle/draft groups.
    || (Array.isArray(command.compound?.members)
      && command.compound.members.some(member => String(member?.id ?? '').toUpperCase() !== String(command.actor_id).toUpperCase()
        || Object.prototype.hasOwnProperty.call(member, 'inventory_id')))) return null;
  const first = route[0]!, last = route[route.length - 1]!;
  return {
    type: 'ft3d_move_preview',
    schema: 'ft.ft3d.authoritative-move-commit.v1',
    move_id: command.operation_id.toLowerCase(),
    encounter_id: command.encounter_id,
    from_user_id: 'movement-truth',
    kind: 'creature',
    draggable_id: command.actor_id,
    origin: { x: first[0], z: first[1], elevation: first[2] },
    preview: { x: last[0], z: last[1], elevation: last[2] },
    accepted_path: { schema: 'ft.ft3d.accepted-move-path.v1', points: route.map(([x, y, z]) => ({ x, y, z })) },
    committed: true,
    speed_variable: speedVariable,
  };
}
function applyOverrunParties(encounterId: string, saved: unknown): void {
  if (!Array.isArray(saved)) return;
  for (const party of saved) (window as unknown as { ftTrapApplyVictim?: (e: string, s: unknown) => void }).ftTrapApplyVictim?.(encounterId, party);
}
/** A luck offer sees the saved luck/reaction even when the post-flush roster refresh arrives later. */
function applyAcceptedStealth(command: MovementTruthCommand, result: MovementTruthAcceptedResult): void {
  if (!command.stealth) return;
  try {
    applyOverrunParties(command.encounter_id, (result as unknown as Row).stealth_parties);
    stealthWalkPlanner?.recorded?.(command.encounter_id, command.stealth, command.actor_id, command.operation_id);
  } catch (error) { console.warn('[MOVEMENT-TRUTH] stealth confirmation failed', error); }
}
/** Browser-applied walk truth; the lazy save publishes only a refusal correction. */
export function handleMovementTruth(command: MovementTruthFrame): void {
  if (command?.schema !== 'janus.movement.truth.v1' || !Number.isInteger(command.rev) || command.rev! < 0) return;
  if (command.correction) { reloadRefusedEncounter(command.encounter_id, command.operation_id); return; }
  if (!(command.committed || command.provisional) || !noteFrameSeen(command.operation_id, 'committed')) return;
  disarmResync(keyOf(command.encounter_id, command.actor_id), command.operation_id);
  const row = rowFor(command.encounter_id, command.actor_id), position = command.position ?? command.updates;
  if (!row || !isPosition(position)) return;
  const key = keyOf(command.encounter_id, command.actor_id), known = confirmedFor(key, row).rev;
  const changed = setConfirmed(key, command.rev!, position, 'frame');
  // Installation already banked this revision. Presentation is deduped by
  // noteFrameSeen above, independently of the installed row's watermark.
  const installed = command.applied_frame && !('cache_ref' in command.applied_frame);
  if (command.rev! < known || (command.rev! === known && !installed) || seen.has(command.operation_id)) {
    if (changed && !seen.has(command.operation_id) && reconcileRow(key, row)) {
      const merged = confirmedFor(key, row); render({ ...command, position: merged.position, rev: merged.rev }, row);
    }
    return;
  }
  applyOverrunParties(command.encounter_id, (command as unknown as Row).overrun_parties);
  if (command.wet) projectWaterWet(row, command.wet);
  if (command.rev! < Number(row.encounter_creatures.movement_rev ?? 0)) return;
  const merged = confirmedFor(key, row);
  applyMovementTruth(row, { ...merged.position, rev: merged.rev });
  render({ ...command, position: merged.position, rev: merged.rev }, row);
  if (ownsMovementCommits()) {
    const preview = movementTruthMovePreview(command, movementSpeedVariable(command.encounter_id, command.actor_id));
    const now = performance.now();
    if (preview) {
      for (const [actorKey, until] of peerWalkPlaybackLiveUntil) if (until <= now) peerWalkPlaybackLiveUntil.delete(actorKey);
      peerWalkPlaybackLiveUntil.delete(key);
      peerWalkPlaybackLiveUntil.set(key, now + PEER_WALK_PLAYBACK_LIFETIME_MS);
      if (peerWalkPlaybackLiveUntil.size > MAX_PEER_WALK_PLAYBACKS) peerWalkPlaybackLiveUntil.delete(peerWalkPlaybackLiveUntil.keys().next().value!);
      window.dispatchEvent(new CustomEvent('ft:ft3d-move-preview', { detail: preview }));
    } else if ((peerWalkPlaybackLiveUntil.get(key) ?? 0) > now) {
      // Ordinary peer projection already seats and schedules perception. Only
      // a still-playing walk needs an explicit terminal to retire its playback.
      peerWalkPlaybackLiveUntil.delete(key);
      dispatchMovementReconcile(command.encounter_id, command.actor_id, merged.position);
    }
  }
}

/** The remaining legacy resource lane keeps its bounded saved-row recovery watch.
 * Browser-applied walk and End Turn truth do not arm provisional watches. */
const RESYNC_AFTER_MS = 6000;
/** Reads per watch. A read whose install a schema DEFERRED (its own work pending) does not count toward RESYNC_ATTEMPTS, but
 * every read counts toward RESYNC_MAX_READS, so a stuck outbox cannot poll forever. */
const RESYNC_ATTEMPTS = 3;
const RESYNC_MAX_READS = 8;
/** One watch per actor holding EVERY unresolved operation (Codex MV3 closing review, P2): a newer provisional ADDS to the
 * set instead of replacing the watch, so an older operation whose correction was lost is never dropped by a later,
 * unrelated operation settling. An operation resolves only by its own committed / correction frame (`seen`), or when a
 * read at or past its revision was installed by every schema without deferral. */
interface ResyncWatch { encounterId: string; actorId: string; ops: Map<string, number>; attempts: number; reads: number; timer: number; armed: boolean }
/** Unresolved operations kept per actor: settled ones are pruned on every add; past this the OLDEST are dropped. */
const RESYNC_MAX_OPS = 32;
const resyncWatch = new Map<string, ResyncWatch>();
const resyncInFlight = new Set<string>();
const saved = (operationId: string): boolean => seen.has(operationId + '#committed') || seen.has(operationId + '#correction');
/** Drop settled operations; true when none remain. */
function pruneResync(watch: ResyncWatch): boolean {
  for (const operationId of [...watch.ops.keys()]) if (saved(operationId)) watch.ops.delete(operationId);
  return watch.ops.size === 0;
}
function disarmResync(key: string, operationId: string): void {
  const watch = resyncWatch.get(key);
  if (!watch || !watch.ops.delete(operationId) || watch.ops.size > 0) return;
  window.clearTimeout(watch.timer);
  resyncWatch.delete(key);
}
function scheduleResync(key: string, watch: ResyncWatch): void {
  window.clearTimeout(watch.timer);
  watch.armed = true;
  watch.timer = window.setTimeout(() => { watch.armed = false; void resyncActor(key, watch); }, RESYNC_AFTER_MS);
  resyncWatch.set(key, watch);
}
function armActorResync(encounterId: string, actorId: string, rev: number, operationId: string): void {
  if (saved(operationId)) return;
  const key = keyOf(encounterId, actorId);
  const watch = resyncWatch.get(key) ?? { encounterId, actorId, ops: new Map<string, number>(), attempts: 0, reads: 0, timer: 0, armed: false };
  if (watch.ops.has(operationId)) return;
  pruneResync(watch); // settled operations leave promptly, not at the next read (Codex MV3 verification)
  watch.ops.set(operationId, rev);
  while (watch.ops.size > RESYNC_MAX_OPS) watch.ops.delete(watch.ops.keys().next().value!);
  watch.attempts = 0; // a new unresolved operation earns a fresh set of reads (RESYNC_MAX_READS still caps the watch)
  // KEEP a running deadline: restarting it on every new provisional let a steady stream of peer actions postpone the
  // read forever (Codex MV3 verification, P2). Only an idle watch is (re)scheduled.
  if (watch.armed) resyncWatch.set(key, watch); else scheduleResync(key, watch);
  if (resyncWatch.size > 256) { const [oldest, stale] = resyncWatch.entries().next().value!; window.clearTimeout(stale.timer); resyncWatch.delete(oldest); }
}
/** Saved truth from a resync read: banked per field, rendered only when nothing of ours is pending on the actor. */
function installResyncTruth(encounterId: string, actorId: string, rev: number, position: MovementTruthChange): void {
  const row = rowFor(encounterId, actorId);
  if (!row) return;
  const key = keyOf(encounterId, actorId);
  confirmedFor(key, row);
  setConfirmed(key, rev, position, 'snapshot');
  if (!reconcileRow(key, row)) return;
  const merged = confirmedFor(key, row);
  render({ schema: MOVEMENT_SCHEMA, operation_id: 'resync', encounter_id: encounterId, actor_id: actorId, expected_rev: merged.rev,
    before: merged.position, updates: merged.position, route: [], rev: merged.rev, position: merged.position }, row);
}
async function resyncActor(key: string, watch: ResyncWatch): Promise<void> {
  if (resyncWatch.get(key) !== watch) return;
  if (pruneResync(watch)) { resyncWatch.delete(key); return; }      // every saved-truth frame arrived after all
  if (resyncInFlight.has(key)) { scheduleResync(key, watch); return; }
  resyncInFlight.add(key);
  watch.reads += 1;
  let readRev = -1, deferred = false;
  try {
    const response = await fetch('/options/tools/encounter_creatures/ft3d/movement_truth_resync_inc.php', {
      method: 'POST', headers: { 'Content-Type': 'application/json' }, signal: AbortSignal.timeout(15000),
      body: JSON.stringify({ schema: 'janus.movement.resync.v1', encounter_id: watch.encounterId, actor_id: watch.actorId }),
    });
    const result = response.ok ? await response.json() as Row : null;
    if (result?.ok === true && Number.isInteger(result.rev) && isPosition(result.position)) {
      const position = result.position as MovementTruthChange;
      readRev = result.rev as number;
      // Saved truth is safe to bank whatever else happened meanwhile (per-field revisions, pending-aware render).
      installResyncTruth(watch.encounterId, watch.actorId, readRev, position);
      // Every OTHER schema's saved columns ride the same read: each adapter installs its own (resource-truth.ts does) and
      // sets `deferred` when it had to skip (its own work pending) — the watch then reads again instead of resolving.
      const actorEvent = { encounter_id: watch.encounterId, actor_id: watch.actorId, rev: readRev, values: asRow(result.values) ?? {}, deferred: false };
      window.dispatchEvent(new CustomEvent('ft:actor-truth-resync', { detail: actorEvent }));
      deferred = actorEvent.deferred;
      window.dispatchEvent(new CustomEvent('ft:movement-truth-resync', { detail: { operations: [...watch.ops.keys()], encounter_id: watch.encounterId,
        actor_id: watch.actorId, rev: readRev, position, deferred } }));
    }
  } catch { /* retried below; the periodic snapshot remains the backstop */ } finally { resyncInFlight.delete(key); }
  if (resyncWatch.get(key) !== watch) return;
  // A clean read at or past an operation's revision settles it; a deferred read settles nothing.
  if (readRev >= 0 && !deferred) for (const [operationId, rev] of [...watch.ops]) if (readRev >= rev) watch.ops.delete(operationId);
  if (!deferred) watch.attempts += 1;
  if (pruneResync(watch) || watch.attempts >= RESYNC_ATTEMPTS || watch.reads >= RESYNC_MAX_READS) { resyncWatch.delete(key); return; }
  scheduleResync(key, watch); // a write may still be landing, or an install was deferred: read again
}

/** A dependent action waits for the existing movement lane, never a second cache-poll bridge. */
export async function waitForActorMovement(encounterId: string, actorId: string): Promise<void> {
  const key = keyOf(encounterId, actorId);
  await legacyLanes.get(key)?.tail;
  const settled = await waitFor(() => {
    const outbox = outboxes.get(key);
    return outbox?.blocked() ? 'fail' : !outbox || outbox.pending() === 0 ? true : false;
  }, LEGACY_IDLE_WAIT_MS);
  if (!settled) throw new MovementRefused(LEGACY_MOVE_BLOCKED_MESSAGE);
  const unconfirmed = unconfirmedLegacy.get(key);
  if (!unconfirmed) return;
  if (Date.now() - unconfirmed.since >= LEGACY_UNCONFIRMED_EXPIRY_MS) { unconfirmedLegacy.delete(key); return; }
  const confirmed = await waitFor(() => unconfirmed.revBefore !== null
    && (rowRevOf(encounterId, actorId) ?? -1) > unconfirmed.revBefore, LEGACY_ECHO_WAIT_MS);
  if (!confirmed) throw new MovementRefused(LEGACY_MOVE_UNCONFIRMED_MESSAGE);
  if (unconfirmedLegacy.get(key) === unconfirmed) unconfirmedLegacy.delete(key);
}

/** Last unsupported walk reason, keyed by mover. Read once within 10 seconds for the scene's named refusal. */
let handbackWalkKey: string | null = null;
let lastHandback: { key: string; reason: string; at: number } | null = null;
function handbackSummary(detail: unknown): string {
  const d = asRow(detail) ?? {};
  const inner = asRow(d.detail) ?? {};
  if (d.reason === 'stealth' && typeof inner.stealth === 'string') return inner.stealth.slice(0, 240);
  const parts = [String(d.reason ?? 'unknown')];
  for (const key of ['watcher', 'proof', 'leave', 'zones', 'trap', 'why', 'overrun', 'compound', 'squeeze', 'resolver', 'stealth', 'climb', 'trigger', 'inventory', 'replan', 'uncertain']) {
    const value = inner[key];
    if (value !== undefined && value !== null && value !== '') parts.push(`${key}=${typeof value === 'object' ? JSON.stringify(value) : String(value)}`);
  }
  return parts.join(' ').slice(0, 240);
}
window.addEventListener('ft:movement-slice-unavailable', event => {
  if (handbackWalkKey) lastHandback = { key: handbackWalkKey, reason: handbackSummary((event as CustomEvent).detail), at: Date.now() };
});
function tryCommitPlainWalkOnce(input: PlainWalkCommitInput, consequencesReady: boolean): PlainWalkResult | null | Promise<PlainWalkResult | null> {
  handbackWalkKey = keyOf(input.encounterId, input.actorId);
  lastHandback = null;
  lastOaDeclined.delete(keyOf(input.encounterId, input.actorId));
  try { return tryCommitMoverWalk(input, consequencesReady); } finally { handbackWalkKey = null; }
}
/** The page entry awaits movement inputs; due consequences join their own pre-draw inputs. */
export class ZoneRecordsNeeded extends Error {
  constructor(readonly loading: Promise<unknown>) { super(ZONE_ENTRY_PENDING_MESSAGE); }
}
function tryCommitPlainWalkRecorded(input: PlainWalkCommitInput): PlainWalkResult | null | Promise<PlainWalkResult | null> {
  observeTruthIntent('movement', { schema: 'janus.movement.intent.v1', input });
  return awaitMovementInputs(input).then(ready => {
    if (!ready) throw new MovementRefused('Movement refused: action inputs unavailable. Nothing was moved.');
    markActionTiming(walkTimingKey(input), 'decide_ms');
    return tryCommitPlainWalkHydrated(input);
  });
}
function tryCommitPlainWalkHydrated(input: PlainWalkCommitInput): PlainWalkResult | null | Promise<PlainWalkResult | null> {
  const mover = plainWalkMoverInput(input), encounter = encounterFor(mover.encounterId);
  if (encounter && stealthWalkPlanner?.relevant(encounter, mover.actorId, movementScopeReaders(encounter)) && input.stealthSceneReady?.() === false)
    throw new MovementRefused('Movement refused: scene still loading. Try again when the scene is ready.');
  const loading = encounter && stealthWalkPlanner?.relevant(encounter, mover.actorId, movementScopeReaders(encounter))
    ? stealthWalkPlanner.hydrate?.(mover.encounterId, mover.actorId) : null;
  if (loading) {
    input.onPending?.('Preparing movement…');
    return loading.then(() => tryCommitPlainWalkPrepared(input));
  }
  return tryCommitPlainWalkPrepared(input);
}
function tryCommitPlainWalkPrepared(input: PlainWalkCommitInput, consequencesReady = false): PlainWalkResult | null | Promise<PlainWalkResult | null> {
  const draggedInput = input;
  input = plainWalkMoverInput(input);
  const key = keyOf(input.encounterId, input.actorId);
  if (consentAsking.has(key)) throw new MovementRefused(WALK_CONSENT_PENDING_MESSAGE);
  if (zoneRecordsLoading.has(key)) throw new MovementRefused(ZONE_ENTRY_PENDING_MESSAGE);
  try {
    const result = tryCommitPlainWalkOnce(input, consequencesReady);
    return deferredWalkPlan(result) ? result.then(walk => draggedWalkResult(draggedInput, input, walk), error => {
      healDraggedWalkOrigin(draggedInput, input, error); throw error;
    }) : draggedWalkResult(draggedInput, input, result);
  } catch (error) {
    healDraggedWalkOrigin(draggedInput, input, error);
    if (error instanceof MovementInputsNeeded) {
      draggedInput.onPending?.('Preparing movement…');
      return awaitEntityCompileReady([input.encounterId, input.actorId], input.actorId).then(ready => {
        if (!ready) throw new MovementRefused('Movement refused: action inputs unavailable. Nothing was moved.');
        return tryCommitPlainWalkPrepared(draggedInput, true);
      });
    }
    if (error instanceof ZoneRecordsNeeded) {
      draggedInput.onPending?.('Preparing movement…');
      zoneRecordsLoading.add(key);
      return error.loading.then(() => {
        zoneRecordsLoading.delete(key);
        return tryCommitPlainWalkRecorded(draggedInput);
      }, () => { throw new MovementRefused('Movement refused: zone entry records are unavailable. Nothing was moved.'); })
        .finally(() => zoneRecordsLoading.delete(key));
    }
    if (!(error instanceof WalkConsentNeeded) || !walkConsentAsker) throw error;
    return askThenWalk(input, error.watchers, draggedInput).then(walk => draggedWalkResult(draggedInput, input, walk), failure => {
      healDraggedWalkOrigin(draggedInput, input, failure); throw failure;
    });
  }
}
const consentAsking = new Set<string>();
const zoneRecordsLoading = new Set<string>();
// The mover may change from dragged rider to mount while planning; the scene serializes drops per encounter.
const walkTimingKey = (input: PlainWalkCommitInput): string => 'walk:' + input.encounterId.toUpperCase();
function tryCommitPlainWalkTimed(input: PlainWalkCommitInput): PlainWalkResult | null | Promise<PlainWalkResult | null> {
  const timing = startActionTiming('walk', walkTimingKey(input));
  try {
    const result = tryCommitPlainWalkRecorded(input);
    if (deferredWalkPlan(result)) return result.then(walk => { endActionTiming(timing, walk === null); return walk; },
      error => { endActionTiming(timing); throw error; });
    endActionTiming(timing, result === null);
    return result;
  } catch (error) { endActionTiming(timing); throw error; }
}
async function askThenWalk(input: PlainWalkCommitInput, watchers: string[], draggedInput: PlainWalkCommitInput): Promise<PlainWalkResult | null> {
  const asker = walkConsentAsker!;
  const key = keyOf(input.encounterId, input.actorId);
  const consent = (input as WalkConsentInput).triggerConsent ?? asker.create(input.encounterId);
  consentAsking.add(key);
  try {
    draggedInput.onPending?.(WALK_CONSENT_PENDING_MESSAGE);
    window.dispatchEvent(new CustomEvent('ft:walk-consent', { detail: { phase: 'asking', encounterId: input.encounterId, actorId: input.actorId, watchers } }));
    // a new question can only come from rows that changed while the first was open (a watcher stepped into reach): bounded rounds
    for (let round = 0; round < 3; round++) {
      const mover = rowFor(input.encounterId, input.actorId);
      if (!mover) throw new MovementRefused('Movement refused: the encounter is still loading on this page. Nothing was moved.');
      await asker.ask(input.encounterId, mover, watchers, consent);
      consentAsking.delete(key);
      if (plainWalkMoverInput(draggedInput).actorId.toUpperCase() !== input.actorId.toUpperCase())
        throw new MovementRefused('Movement refused: compound:mover-changed. Try dragging again. Nothing was moved.');
      try {
        const walk = await tryCommitPlainWalkRecorded({ ...input, triggerConsent: consent } as WalkConsentInput);
        window.dispatchEvent(new CustomEvent('ft:walk-consent', { detail: { phase: 'resolved', encounterId: input.encounterId, actorId: input.actorId,
          answers: Object.fromEntries(watchers.map(id => [id, consent.answer(id, OPPORTUNITY_ATTACK_ID) ?? null])), handled: !!walk } }));
        return walk;
      } catch (error) {
        if (!(error instanceof WalkConsentNeeded)) throw error;
        watchers = error.watchers;
        consentAsking.add(key);
        draggedInput.onPending?.(WALK_CONSENT_PENDING_MESSAGE);
      }
    }
    throw new MovementRefused('The opportunity attack questions kept changing while you moved. Try moving again.');
  } finally { consentAsking.delete(key); }
}
function lastMovementHandback(encounterId: string, actorId: string): string {
  const found = lastHandback;
  if (!found || found.key !== keyOf(encounterId, actorId) || Date.now() - found.at > 10_000) return '';
  lastHandback = null;
  return found.reason;
}
/** MV4: the watchers whose controllers DECLINED the Opportunity Attack the LAST walk scoped for this actor would provoke — a walk still handed
 *  to the legacy endpoint carries them (`ft3d_oa_declined`) so PHP's per-foot loop injects no universal OA for them. One-shot, 10 s, like
 *  lastMovementHandback. */
// Codex MV4 r2 P1: keyed PER ACTOR (never one shared slot): another actor's walk scoped before this actor's legacy POST read its declines
// can neither overwrite nor consume them. One actor's own walks are serialized (consentAsking + the legacy lane), and the scene reads the
// record synchronously as it builds that walk's POST.
const lastOaDeclined = new Map<string, { ids: string[]; at: number }>();
function lastMovementOaDeclined(encounterId: string, actorId: string): string[] {
  const key = keyOf(encounterId, actorId);
  const found = lastOaDeclined.get(key);
  lastOaDeclined.delete(key);
  if (!found || Date.now() - found.at > 10_000) return [];
  return [...found.ids];
}

// Parent owns the canonical encounter cache; both GM and player iframe drops use
// this entry point (synchronous unless a player's opportunity-attack answer is needed — MV4). The commit endpoint persists, then publishes.
Object.assign(window, { ftTryCommitPlainWalk: tryCommitPlainWalkTimed, ftLastMovementHandback: lastMovementHandback, ftLastMovementOaDeclined: lastMovementOaDeclined,
  ftInventoryDrivePlan: inventoryDrivePlan,
  ftMovementSpeedVariable: movementSpeedVariable,
  ftMovementDispatchContext: movementDispatchContext,
  ftMovementTruthConfirmed: confirmedSnapshotFor, ftMovementPermission: movementPermission, ftMovementStartPermission: movementStartPermission,
  // The legacy move lane: the GM parent handler (my_campaign.php) and the participant scene lane both send through it.
  ftRunActorLegacyMove: runActorLegacyMove, ftActorLegacyMoveBusy: legacyMoveBusy, ftWaitForActorMovement: waitForActorMovement });
// R1: a `caster_update` that installed a LEGACY row (never this browser's own render) is saved truth.
casterRowInstalled.push((casterId, row, local) => {
  if (local) return;
  const encounterId = String(own(row.encounter_creatures, 'encounter_id') ?? '');
  if (encounterId && casterId) bankLegacyRowTruth(encounterId, casterId, row as unknown as MovementTruthRow, 'caster-update');
});
window.addEventListener('beforeunload', event => {
  // Terminal entries (stale / refused) are already reconciled; only pending or retryable work warns.
  const pending = [...deliveries.values()].some(value => value.commit !== 'delivered'
    && own(value.errors.commit, 'error') !== 'stale' && own(value.errors.commit, 'terminal') !== true);
  if (pending) { event.preventDefault(); event.returnValue = ''; }
});

// Definitions belong to adoption, never to an ordinary loaded walk.
for (const event of ['ft:encounter-installed', 'ft:encounter-state-received']) window.addEventListener(event, e => {
  const detail = (e as CustomEvent).detail as { encounterId?: string; encounterArr?: { encounters?: unknown }; subType?: string } | undefined;
  if (detail?.subType === 'creature_rows_patched') return;
  const id = detail?.encounterId ?? String(own(detail?.encounterArr?.encounters, 'encounter_id') ?? '');
  if (id) void hydrateMovementStatuses(id)?.catch(error => console.warn('Movement preload failed', error));
});
